import {
    AdvanceDeduction,
    AdvanceDeductionGroup,
    Customer,
    CustomerOverride,
    DOCUMENT_TYPE_LABEL,
    DocumentType,
    Invoice,
    InvoiceDetails,
    InvoiceConfig,
    InvoiceLine,
    InvoiceProjectRef,
    InvoiceSection,
    InvoiceSeries,
    InvoiceStatus,
    InvoiceTotals,
    PaymentDirection,
    PaymentMethod,
    PriorRetention,
    SERIES_FOR_DOCUMENT_TYPE,
    ServiceItem,
    ServiceTransaction,
    TaxCategoryCode,
    TaxGroup,
} from '../types';
import { Cents, formatCents, roundCents, sumCents, taxFromNet } from './money';
import { hasCustomerOverride } from './customerOverride';
import { isExternalAdvanceId } from './externalAdvances';
// flatRateLogic ist ein Blatt-Modul (importiert nur Typen) — kein Zyklus.
import {
    FlatCoverageServiceLike,
    bookedFlatMonths,
    flatKeyLabel,
    flatMonthKey,
    flatMonthKeysInRange,
    isFlatBookingReference,
    isFlatRateService,
    resolveFlatCoverage,
} from './flatRateLogic';
import { bestandteilSperrText, openQuantity, waivedQuantity } from './projectLv';
// bestandteilLogic ist ein Blatt-Modul (Typen + flatRateLogic) — kein Zyklus.
import { hatBestandteile, istAbrechnungGesperrt, paketDeckel } from './bestandteile/bestandteilLogic';
// Kein Importzyklus: projectSplit haengt nur an ../types (reine Typen) und
// serviceLogUtils (ganz ohne Importe) und kennt invoiceLogic nicht. Deshalb
// wird formatShare geteilt statt nachgebaut — die Schreibweise des Anteils muss
// auf dem Beleg und in den Pruefmeldungen dieselbe sein.
import { SHARE_FULL_BP, formatShare } from './projectSplit';
import { asDay, berlinerSortierzeit, buchungstag, findMissingUsedQuantity, isAccountingEntry, isBackfilledAltEntry, kennungFuerVergleich } from './serviceLogUtils';
import {
    allocatableEntries,
    allocatedByEntry,
    allocatedByService,
    billingPeriodOf,
    ohneVerzichteteEintraege,
    openEntries,
    unallocatedBilled,
    verzichteteEintraege,
} from './allocationLogic';

/**
 * Rechen- und Regelschicht des Rechnungsmoduls.
 *
 * Pure Funktionen, kein Supabase, kein Date.now() — "heute" wird immer
 * hereingereicht. Das folgt quoteStats.ts und ist im Projekt die Bedingung
 * dafuer, dass ueberhaupt getestet werden kann (es gibt kein jsdom).
 */

// ═══════════════════════════════════════════════════════════════════════════
// Summen und Steuer
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Zeilenarten, die zum Rechnungsnetto beitragen.
 *
 * Exportiert, weil Andruck und Maske denselben Schnitt brauchen: eine
 * Zwischensumme, die eine dieser Arten auslaesst, weicht vom Nettobetrag ab,
 * und eine Tabelle, die ihr den Betrag verschweigt, verbirgt genau die Zeile,
 * die der Nachlass erzeugt hat.
 */
export const CONTRIBUTES_TO_NET: ReadonlySet<string> = new Set([
    'SERVICE',
    'DISCOUNT',
    'SURCHARGE',
]);

/**
 * Zeilenbetrag VOR Nachlass: Menge mal Einzelpreis, auf ganze Cent gerundet.
 *
 * Eigene Funktion, weil derselbe Wert an drei Stellen gebraucht wird — als
 * Bemessungsgrundlage des Nachlasses, als angedruckte Grundlage im PDF und als
 * BT-137/BasisAmount in der E-Rechnung. Drei Ad-hoc-Multiplikationen driften
 * genau dann auseinander, wenn es teuer wird.
 */
export const lineGrossBeforeDiscount = (line: InvoiceLine): Cents =>
    roundCents((line.quantity ?? 0) * (line.unitPriceCents ?? 0));

/** Traegt die Zeile ueberhaupt einen Positionsnachlass? */
const hasFixedLineDiscount = (line: InvoiceLine): boolean =>
    line.discountAmountCents != null &&
    Number.isFinite(line.discountAmountCents) &&
    line.discountAmountCents !== 0;

/**
 * Zeilennetto aus Menge, Einzelpreis und Nachlass.
 *
 * Erst multiplizieren, dann EINMAL runden — wie lineTotal in quoteLogic. Wer
 * ueber den rabattierten Einzelpreis zwischenrundet, bekommt je nach Menge
 * einen anderen Zeilenbetrag als die Summe erwarten laesst.
 *
 * Der feste Betrag schlaegt den Prozentsatz und wird NICHT mit ihm kombiniert:
 * in der E-Rechnung erklaert entweder ein Prozentsatz den Nachlassbetrag oder
 * gar keiner (siehe InvoiceLine.discountAmountCents). Er wird vom gerundeten
 * Zeilenbetrag abgezogen, damit Menge x Einzelpreis minus Nachlass auf dem
 * Papier exakt aufgeht.
 */
export const computeLineNet = (line: InvoiceLine): Cents => {
    if (!CONTRIBUTES_TO_NET.has(line.kind)) return 0;
    // Korrektur- und Gutschriftzeilen tragen eine DIFFERENZ, die nicht aus
    // Menge x Einzelpreis folgt: Menge und Preis benennen dort die
    // urspruengliche Position, der Betrag ist die Aenderung.
    if (line.netCentsExplicit) return line.netCents;
    if (hasFixedLineDiscount(line)) {
        return lineGrossBeforeDiscount(line) - roundCents(line.discountAmountCents ?? 0);
    }
    const qty = line.quantity ?? 0;
    const unit = line.unitPriceCents ?? 0;
    const discount = line.discountPercent ?? 0;
    return roundCents(qty * unit * (1 - discount / 100));
};

/**
 * Der auf dieser Zeile tatsaechlich abgezogene Nachlass in Cent, positiv als
 * Minderung. 0, wenn die Zeile keinen traegt.
 *
 * Als DIFFERENZ zwischen Menge x Preis und dem Zeilennetto gerechnet und nicht
 * aus dem Prozentsatz neu abgeleitet: nur so stimmt der angedruckte Betrag mit
 * dem eingefrorenen Netto auf den Cent ueberein. Genau dieselbe Ableitung
 * benutzen ublInvoice und ciiInvoice fuer BT-136.
 */
export const lineDiscountCents = (line: InvoiceLine): Cents => {
    if (line.kind !== 'SERVICE' || line.netCentsExplicit) return 0;
    if (!hasFixedLineDiscount(line) && !(line.discountPercent ?? 0)) return 0;
    return lineGrossBeforeDiscount(line) - computeLineNet(line);
};

/**
 * Der Nachlassvermerk unter der Leistungsbezeichnung, oder undefined, wenn die
 * Zeile keinen Nachlass traegt.
 *
 * Der Vermerk ist der Grund, warum der Einzelpreis unrabattiert gedruckt werden
 * DARF: er nennt die Grundlage (Menge x Einzelpreis) und den Abzug, damit der
 * Empfaenger das Zeilennetto nachrechnen kann. Ohne ihn stuenden auf dem Beleg
 * ein Einzelpreis und ein Netto, die nicht zueinander passen — der haeufigste
 * Anlass fuer eine Rechnungsrueckfrage.
 *
 * Der Abzug kommt aus lineDiscountCents und ist damit die Differenz zum
 * eingefrorenen Netto: Grundlage minus Abzug ergibt auf dem Papier exakt den
 * Betrag der Netto-Spalte, auch wenn der Prozentsatz krumm ist.
 */
export const lineDiscountNote = (line: InvoiceLine): string | undefined => {
    const discount = lineDiscountCents(line);
    if (discount === 0) return undefined;
    const base = formatCents(lineGrossBeforeDiscount(line));
    const abzug = formatCents(-discount);
    return hasFixedLineDiscount(line)
        ? `abzüglich Nachlass auf ${base} = ${abzug}`
        : `abzüglich ${formatPercent(line.discountPercent ?? 0)} % Nachlass auf ${base} = ${abzug}`;
};

/**
 * Mengen im Vermerk so schreiben wie ueberall sonst auf dem Beleg: deutsches
 * Komma, bis zu drei Nachkommastellen, keine unnoetigen Nullen (8 -> "8",
 * 8,5 -> "8,5"). Dasselbe Format wie fmtQuantity im Rechnungs-PDF und wie die
 * Mengenangabe in den Pruefmeldungen weiter unten — eine zweite Schreibweise
 * fuer dieselbe Groesse laesst den Empfaenger zweimal hinsehen.
 */
const formatSplitQuantity = (quantity: number): string =>
    quantity.toLocaleString('de-DE', { maximumFractionDigits: 3 });

/**
 * Der Anteilsvermerk unter der Leistungsbezeichnung, oder undefined, wenn die
 * Zeile keinem Aufteilungsschluessel entstammt.
 *
 * Er ist das Gegenstueck zum Nachlassvermerk: die Zeile fordert nur einen TEIL
 * der erbrachten Menge, und der Empfaenger muss ohne Rueckfrage sehen koennen,
 * warum. Steht dort nur "4 h", waehrend das Protokoll 8 h ausweist, sieht der
 * Beleg nach einer zu niedrigen Abrechnung aus — oder, schlimmer, der zweite
 * Auftraggeber haelt seinen eigenen Teilbeleg fuer eine Doppelforderung.
 *
 * Der zweite Grund ist ein interner: wir lesen eigene Belege spaeter wieder ein
 * (Alt-Rechnungs-Import, Deckungspruefung). Dabei muss erkennbar bleiben, dass
 * die 4 h auf dem Papier ein Anteil an 8 h sind und nicht der ganze
 * Leistungsstand — sonst faellt die fehlende Haelfte als "erbracht, nie
 * abgerechnet" durch, oder sie wird ein zweites Mal gefordert.
 *
 * 100,00 % ist ausdruecklich KEINE Teilung (siehe hasSplit in projectSplit):
 * ein einzelner Auftraggeber mit vollem Anteil soll exakt denselben Beleg
 * erzeugen wie ein Projekt ganz ohne Schluessel. Ein Vermerk "Anteil 100,00 %"
 * waere Rauschen auf jedem dieser Belege.
 *
 * Ohne splitFullQuantity bleibt der Vermerk beim Prozentsatz stehen, statt eine
 * Menge zu erfinden: die ungeteilte Menge wird beim Teilen eingefroren
 * mitgegeben und laesst sich aus Menge und Anteil nicht zurueckrechnen, ohne
 * Rundungsreste als Tatsache auszugeben.
 */
export const splitShareNote = (line: InvoiceLine): string | undefined => {
    const bp = line.splitShareBp;
    if (bp == null || !Number.isFinite(bp) || bp === SHARE_FULL_BP) return undefined;
    const anteil = `Anteil ${formatShare(bp)}`;
    const full = line.splitFullQuantity;
    if (full == null || !Number.isFinite(full)) return anteil;
    const unit = line.unit?.trim();
    return `${anteil} von ${formatSplitQuantity(full)}${unit ? ` ${unit}` : ''}`;
};

/**
 * Der Positionsnachlass als Prozentsatz — auch dann, wenn er als fester Betrag
 * erfasst wurde.
 *
 * Gebraucht, wo eine TEILmenge der Zeile zu ihren Konditionen bewertet werden
 * muss (uncoveredPriorBilling): ein fester Betrag gilt fuer die ganze Zeile und
 * laesst sich auf eine Teilmenge nur ueber seinen Anteil umlegen. Nicht zum
 * Rechnen des Zeilennettos verwenden — dort ist der Betrag massgeblich, und
 * eine Umrechnung ueber den Prozentsatz koennte um einen Cent abweichen.
 */
export const effectiveLineDiscountPercent = (line: InvoiceLine): number => {
    if (!hasFixedLineDiscount(line)) return line.discountPercent ?? 0;
    const base = lineGrossBeforeDiscount(line);
    if (base === 0) return 0;
    return ((line.discountAmountCents ?? 0) / base) * 100;
};

/**
 * Berechnet netCents aller Zeilen neu.
 *
 * Nur waehrend der Bearbeitung aufzurufen. Ab der Ausstellung ist netCents
 * eingefroren und wird NICHT neu berechnet — sonst kann ein spaeter geoeffneter
 * Beleg von seinem eigenen archivierten PDF abweichen.
 */
export const recalculateLines = (lines: InvoiceLine[]): InvoiceLine[] =>
    lines.map((line) => ({ ...line, netCents: computeLineNet(line) }));

/**
 * Nachlass auf den ganzen Beleg, wie er in details erfasst wird.
 *
 * Der Betrag schlaegt den Prozentsatz — dieselbe Ausschliesslichkeit wie auf der
 * Position, damit "Prozent ODER Euro" ueberall dasselbe heisst.
 */
export interface DocumentDiscount {
    percent?: number;
    /** Positiv als Minderung. */
    amountCents?: Cents;
}

/**
 * Liest den Belegnachlass aus den Belegdetails.
 *
 * Eine Stelle statt an jedem Aufrufer zwei Felder abzuschreiben: die
 * Ausschliesslichkeit von Prozent und Betrag entscheidet applyDocumentDiscount,
 * und sie darf nicht davon abhaengen, welcher Aufrufer die Felder wie einsammelt.
 */
export const documentDiscountOf = (
    details: Invoice['details'] | null | undefined,
): DocumentDiscount => ({
    percent: details?.discountPercent,
    amountCents: details?.discountAmountCents,
});

/**
 * Verteilt einen festen Nachlassbetrag anteilig auf die Bemessungsgrundlagen
 * der Steuergruppen.
 *
 * Anteilig nach dem Betrag der Gruppe, jede Gruppe kaufmaennisch gerundet, und
 * die LETZTE Gruppe bekommt den Rest, damit die Summe der Anteile exakt dem
 * Nachlass entspricht. Die Gruppen kommen aus groupTaxes und stehen dort nach
 * Steuersatz ABSTEIGEND — der Restcent faellt damit immer auf den NIEDRIGSTEN
 * Satz des Belegs. So kostet die willkuerliche Zuteilung des Cents die
 * wenigste Steuer, und bei 0 % (§13b, steuerfrei) gar keine.
 *
 * Das Vorzeichen richtet sich nach der Grundlage, nicht nach dem Nachlass:
 * geteilt wird durch den BETRAG der Gesamtgrundlage. Auf einem negativen Beleg
 * (Storno, Gutschrift) mindert der Nachlass damit den Betrag, statt ihn zu
 * vergroessern — sonst erstattete ein Storno mehr, als die Rechnung gefordert
 * hat.
 *
 * @returns je Gruppe die Minderung, positiv bei positiver Grundlage.
 */
export const distributeDiscountAmount = (
    bases: readonly Cents[],
    amountCents: Cents,
): Cents[] => {
    const total = sumCents([...bases]);
    const scale = Math.abs(total);
    // Ohne Bemessungsgrundlage gibt es nichts zu verteilen. Ein Beleg, dessen
    // Leistungen sich zu 0 aufheben, traegt keinen Nachlass — anteilig waere
    // dort eine Division durch Null.
    if (scale === 0) return bases.map(() => 0);

    const target = total >= 0 ? amountCents : -amountCents;
    const out: Cents[] = [];
    let assigned = 0;
    bases.forEach((base, i) => {
        if (i === bases.length - 1) {
            out.push(target - assigned);
            return;
        }
        const share = roundCents((amountCents * base) / scale);
        out.push(share);
        assigned += share;
    });
    return out;
};

/**
 * Setzt den Nachlass auf den ganzen Beleg neu — je Steuersatz eine eigene Zeile.
 *
 * Wie beim Nebenkostenzuschlag entsteht der Nachlass als POSITION und nicht als
 * Feld am Beleg: das Datenmodell kennt keine Dokumentebene fuer Nachlaesse
 * (en16931Rules, BT-107 wird nie geschrieben). Und wie dort je Steuersatz eine
 * eigene Zeile: die Steuer wird aus der Gruppensumme gerechnet, ein Nachlass in
 * nur einer Gruppe verschoebe die Bemessungsgrundlagen zwischen den Saetzen.
 *
 * Zuvor erzeugte Nachlasszeilen werden verworfen und neu gebildet — sonst
 * bliebe der Nachlass auf der Grundlage stehen, die vor der letzten
 * Positionsaenderung galt.
 *
 * Bemessungsgrundlage ist die Honorarsumme OHNE Zuschlagszeilen. Der Zuschlag
 * wird anschliessend auf die bereits geminderte Summe neu gebildet und traegt
 * den Nachlass dadurch mit; ihn hier einzubeziehen, hiesse den Nachlass auf den
 * Zuschlag zweimal zu beruecksichtigen. AUFRUFREIHENFOLGE ist deshalb tragend:
 * recalculateLines -> applyDocumentDiscount -> applyAncillaryCosts.
 *
 * Pur: der Aufrufer bestimmt, wann neu gerechnet wird. Nach der Festschreibung
 * darf das nicht mehr passieren.
 */
export const applyDocumentDiscount = (
    lines: InvoiceLine[],
    discount: DocumentDiscount | undefined,
    makeId: () => string,
): InvoiceLine[] => {
    const base = lines.filter((l) => l.generated !== 'DISCOUNT');

    const amountCents = discount?.amountCents;
    const usesAmount =
        amountCents != null && Number.isFinite(amountCents) && roundCents(amountCents) !== 0;
    const percent = discount?.percent;
    const usesPercent =
        !usesAmount && percent != null && Number.isFinite(percent) && percent > 0;
    if (!usesAmount && !usesPercent) return base;

    // Gruppen ohne Bemessungsgrundlage bleiben aussen vor: sie bekaemen sonst
    // ueber die Restverteilung einen Nachlass auf nichts.
    const groups = groupTaxes(base.filter((l) => l.kind !== 'SURCHARGE')).filter(
        (g) => g.taxableNetCents !== 0,
    );
    if (groups.length === 0) return base;

    const shares = usesAmount
        ? distributeDiscountAmount(
              groups.map((g) => g.taxableNetCents),
              roundCents(amountCents ?? 0),
          )
        : groups.map((g) => roundCents((g.taxableNetCents * (percent ?? 0)) / 100));

    const title = usesAmount
        ? 'abzüglich Nachlass'
        : `abzüglich ${formatPercent(percent ?? 0)} % Nachlass`;

    const discounts = groups
        .map((group, i) => ({ group, netCents: -shares[i] }))
        // Eine 0,00-EUR-Zeile kostet nur Platz.
        .filter((d) => d.netCents !== 0)
        .map(({ group, netCents }) => ({
            id: makeId(),
            kind: 'DISCOUNT' as const,
            generated: 'DISCOUNT' as const,
            title,
            taxRateBp: group.taxRateBp,
            taxCategoryCode: group.taxCategoryCode,
            taxExemptionReason: group.taxExemptionReason,
            netCents,
            // Massgeblich ist der Betrag: der Nachlass hat weder Menge noch
            // Einzelpreis.
            netCentsExplicit: true,
        }));

    return [...base, ...discounts];
};

/**
 * Setzt den Nebenkostenzuschlag auf die Honorarsumme neu.
 *
 * Der Zuschlag ist eine Nebenleistung und teilt das steuerliche Schicksal der
 * Hauptleistung: bei mehreren Steuersaetzen auf dem Beleg entsteht deshalb je
 * Satz eine eigene Zuschlagszeile, berechnet auf die Bemessungsgrundlage genau
 * dieses Satzes. Eine einzige Zeile mit einem gemittelten Satz waere weder
 * nachrechenbar noch §14-konform.
 *
 * Zuvor erzeugte Zuschlagszeilen werden verworfen. Ohne das bliebe der Zuschlag
 * auf dem Betrag stehen, der vor der letzten Positionsaenderung galt — und der
 * Beleg saehe korrekt aus, waehrend er es nicht ist.
 *
 * Pur: der Aufrufer bestimmt, wann neu gerechnet wird. Nach der Festschreibung
 * darf das nicht mehr passieren.
 */
export const applyAncillaryCosts = (
    lines: InvoiceLine[],
    percent: number | undefined,
    makeId: () => string,
): InvoiceLine[] => {
    const base = lines.filter((l) => l.generated !== 'ANCILLARY');
    if (!percent || !Number.isFinite(percent) || percent <= 0) return base;

    // Grundlage ist die Honorarsumme — das Netto der Leistungen abzueglich
    // Nachlaesse. Zuschlagszeilen bleiben aussen vor: der eigene Zuschlag waere
    // Zinseszins, und ein von Hand ergaenzter Zuschlag ist selbst eine
    // Nebenleistung und keine Leistung, auf die Nebenkosten entfallen.
    const groups = groupTaxes(base.filter((l) => l.kind !== 'SURCHARGE'));

    const surcharges = groups
        .map((g) => ({
            group: g,
            netCents: roundCents((g.taxableNetCents * percent) / 100),
        }))
        // Bei einer Bemessungsgrundlage von 0 entstuende eine 0,00-EUR-Zeile,
        // die nur Platz kostet. Negative Gruppen (Gutschrift) ergeben einen
        // negativen Zuschlag — das ist richtig und bleibt.
        .filter((s) => s.netCents !== 0)
        .map(({ group, netCents }) => ({
            id: makeId(),
            kind: 'SURCHARGE' as const,
            generated: 'ANCILLARY' as const,
            title: `zzgl. ${formatPercent(percent)} % Nebenkosten`,
            taxRateBp: group.taxRateBp,
            taxCategoryCode: group.taxCategoryCode,
            taxExemptionReason: group.taxExemptionReason,
            netCents,
            // Massgeblich ist der Betrag, nicht Menge mal Preis: der Zuschlag
            // ist ein Prozentsatz und hat weder Menge noch Einzelpreis.
            netCentsExplicit: true,
        }));

    return [...base, ...surcharges];
};

/** Prozentsatz ohne unnoetige Nullen: 2 -> "2", 2.5 -> "2,5". */
const formatPercent = (percent: number): string =>
    percent.toLocaleString('de-DE', { maximumFractionDigits: 2 });

/** Standard-Steuerkategorie zu einem Satz, wenn die Zeile keine eigene nennt. */
const defaultTaxCategory = (taxRateBp: number): TaxCategoryCode => (taxRateBp > 0 ? 'S' : 'Z');

/**
 * Gruppiert die Bemessungsgrundlagen je Steuersatz und berechnet die Steuer
 * aus der GRUPPENSUMME.
 *
 * Bewusst nicht je Position: die Spezifikation (§10.3) verlangt Netto je
 * Position, Steuer aber je Steuersatz. Positionsweise Steuerrundung ergibt bei
 * vielen Kleinbetraegen eine andere Summe, als das Finanzamt erwartet.
 *
 * Gruppenschluessel ist der Basispunkt-Wert. Waere der Satz ein Float, wuerden
 * 19.00 und 19 zwei Gruppen auf demselben Beleg erzeugen.
 */
export const groupTaxes = (lines: InvoiceLine[]): TaxGroup[] => {
    const byRate = new Map<number, TaxGroup>();

    for (const line of lines) {
        if (!CONTRIBUTES_TO_NET.has(line.kind)) continue;
        const rate = line.taxRateBp ?? 0;
        const existing = byRate.get(rate);
        if (existing) {
            existing.taxableNetCents += line.netCents;
            // Ein Befreiungsgrund an irgendeiner Zeile gilt fuer die Gruppe.
            if (!existing.taxExemptionReason && line.taxExemptionReason) {
                existing.taxExemptionReason = line.taxExemptionReason;
            }
        } else {
            byRate.set(rate, {
                taxRateBp: rate,
                taxCategoryCode: line.taxCategoryCode ?? defaultTaxCategory(rate),
                taxableNetCents: line.netCents,
                taxCents: 0,
                taxExemptionReason: line.taxExemptionReason,
            });
        }
    }

    return [...byRate.values()]
        .map((g) => ({ ...g, taxCents: taxFromNet(g.taxableNetCents, g.taxRateBp) }))
        .sort((a, b) => b.taxRateBp - a.taxRateBp);
};

/**
 * Fasst Abschlagsabzuege je Steuersatz zusammen.
 *
 * Auch die "zusammengefasste" Darstellung gruppiert nach Satz und nicht in eine
 * einzige Zeile: bei mehreren Saetzen waere eine gesammelte
 * "abzueglich Abschlaege"-Zeile nicht §14-UStG-konform, weil die enthaltene
 * Umsatzsteuer nicht mehr nachvollziehbar ist.
 */
export const groupAdvanceDeductions = (deductions: AdvanceDeduction[]): AdvanceDeductionGroup[] => {
    const byRate = new Map<number, AdvanceDeductionGroup>();

    for (const d of deductions) {
        const g = byRate.get(d.taxRateBp);
        if (g) {
            g.netCents += d.netCents;
            g.taxCents += d.taxCents;
            g.grossCents += d.grossCents;
            g.sourceInvoiceIds.push(d.advanceInvoiceId);
            g.sourceInvoiceNumbers.push(d.invoiceNumber);
        } else {
            byRate.set(d.taxRateBp, {
                taxRateBp: d.taxRateBp,
                netCents: d.netCents,
                taxCents: d.taxCents,
                grossCents: d.grossCents,
                sourceInvoiceIds: [d.advanceInvoiceId],
                sourceInvoiceNumbers: [d.invoiceNumber],
            });
        }
    }

    return [...byRate.values()].sort((a, b) => b.taxRateBp - a.taxRateBp);
};

/**
 * Sicherheitseinbehalt, wie er in die Summen eingeht.
 *
 * Der Prozentsatz kommt vom Beleg (effectiveRetentionPercent), die Liste der
 * auf den Vorbelegen einbehaltenen Betraege vom Aufrufer
 * (priorRetentionsFromDeductions in advanceInvoiceLogic) — dieses Modul kennt
 * die Vorbelege nicht und soll sie nicht kennen, genau wie beim Abschlagsabzug.
 */
export interface RetentionInput {
    /** Effektiver Satz, 5 = 5 %. Fehlend, 0 oder ungueltig heisst: Satz 0. */
    percent?: number;
    /** Einbehalte der Vorbelege, die dieser Beleg abzieht (Invoice.priorRetentions). */
    priorRetentions?: PriorRetention[];
}

/**
 * Der Satz, mit dem der Beleg tatsaechlich rechnet.
 *
 * Die Schlussrechnung gibt frei (releaseRetention fehlt oder true): ihr Satz
 * ist 0, und die kumulative Formel gibt damit alles frei, was die Vorbelege
 * einbehalten haben. Mit releaseRetention false bleibt der Satz des Belegs
 * stehen — der Gewaehrleistungseinbehalt wird nicht ausgezahlt. Ungueltige
 * oder negative Saetze sind 0: ein negativer Einbehalt waere eine Auszahlung
 * ohne Grundlage.
 */
export const effectiveRetentionPercent = (
    documentType: DocumentType,
    details: Pick<InvoiceDetails, 'retentionPercent' | 'releaseRetention'> | null | undefined,
): number => {
    if (documentType === 'FINAL_INVOICE' && details?.releaseRetention !== false) return 0;
    const pct = details?.retentionPercent;
    if (pct == null || !Number.isFinite(pct) || pct <= 0) return 0;
    return pct;
};

/** Die fuenf Einbehalt-Felder der Summen, immer gefuellt. */
export type RetentionTotals = Required<
    Pick<
        InvoiceTotals,
        | 'cumulativeRetentionCents'
        | 'priorRetentionCents'
        | 'retentionCents'
        | 'retentionReleaseCents'
        | 'paidPriorCents'
    >
>;

/**
 * Der kumulative Einbehalt (VOB-Muster) — die eine Formel fuer alles.
 *
 *   cumulative = gross > 0 ? round(gross x pct / 100) : 0
 *   delta      = cumulative - prior
 *   retention  = max(0, delta)      mit diesem Beleg einbehalten
 *   release    = max(0, -delta)     mit diesem Beleg freigegeben
 *   paidPrior  = prepaid - prior    tatsaechlich erhaltene Abschlagszahlungen
 *
 * Auf den LEISTUNGSSTAND (gross), nicht auf gross - prepaid: der Einbehalt
 * eines Projekts ist nach jedem Beleg exakt round(pct x Leistungsstand). Damit
 * holt der naechste Beleg nach, was ein Vorbeleg nicht einbehalten hat
 * (Altbestand, Satzwechsel), die Schlussrechnung (Satz 0) gibt mit derselben
 * Rechnung alles frei, und die Rundung entsteht nur einmal auf der Summe statt
 * je Beleg. Das inkrementelle Modell (x % auf gross - prepaid) konnte keines
 * davon: es zeigte auf der Abzugszeile die Forderungen der Vorbelege statt der
 * erhaltenen Zahlungen und vergass einen fehlenden Einbehalt fuer immer
 * (Befund der Buchhaltung, 15.09.2026).
 *
 * Fortschreibende Belege ohne Abzuege haben prior 0 und retention =
 * pct x gross — richtig, denn dort ist gross nur die eigene neue Leistung.
 *
 * Bei gross <= 0 (Storno, Gutschrift, Kundenguthaben) ist cumulative 0: ein
 * negativer Leistungsstand behaelt nichts ein. Storno und Korrektur SPIEGELN
 * die Werte des Originals stattdessen (creditNoteLogic) und rechnen nicht neu.
 */
export const computeRetention = (
    grossTotalCents: Cents,
    prepaidCents: Cents,
    percent: number,
    priorRetentionCents: Cents,
): RetentionTotals => {
    const pct = Number.isFinite(percent) && percent > 0 ? percent : 0;
    const cumulativeRetentionCents =
        grossTotalCents > 0 && pct > 0 ? roundCents((grossTotalCents * pct) / 100) : 0;
    const delta = cumulativeRetentionCents - priorRetentionCents;
    return {
        cumulativeRetentionCents,
        priorRetentionCents,
        retentionCents: Math.max(0, delta),
        retentionReleaseCents: Math.max(0, -delta),
        paidPriorCents: prepaidCents - priorRetentionCents,
    };
};

/**
 * Bruttoforderung dieses Belegs: gross - prepaid, der "Rechnungsbetrag
 * (brutto)" VOR Einbehalt und Freigabe. Bezugsgroesse fuer payableBreakdown
 * und die Summenzeile ueber der Einbehaltzeile — nicht mehr die Grundlage des
 * Einbehalts (die ist gross, siehe computeRetention).
 */
export const invoiceAmountCents = (grossTotalCents: Cents, prepaidCents: Cents): Cents =>
    grossTotalCents - prepaidCents;

/**
 * Gesamtsummen einer Rechnung.
 *
 * Der Abschlagsabzug mindert NICHT das ausgewiesene Netto und nicht die
 * ausgewiesene Steuer — die Gesamtleistung bleibt vollstaendig sichtbar
 * (Spezifikation §12.1, §13, §14). Abgezogen wird erst am Ende, vom
 * Bruttobetrag, was den Zahlbetrag ergibt.
 *
 * Der Sicherheitseinbehalt folgt derselben Regel und ist deshalb hier und
 * nicht bei den Zeilen: er ist keine Entgeltminderung (kein Nachlass), sondern
 * ein zurueckgehaltener Teil des Zahlbetrags. Die Steuer entsteht auf das
 * volle Entgelt (§ 14 UStG). Gerechnet wird kumulativ (computeRetention):
 *
 *   payable = gross - prepaid - retention + retentionRelease
 *
 * Ohne dritten Parameter sind alle Einbehalt-Felder 0 und der Zahlbetrag wie
 * bisher — die bestehenden Aufrufer bleiben zeichengleich gueltig.
 *
 * Der Darstellungsmodus beeinflusst ausschliesslich das Layout: beide Modi
 * rechnen mit derselben Abzugsliste, deshalb ist payableCents in INDIVIDUAL und
 * GROUPED konstruktionsbedingt identisch.
 */
export const calculateInvoiceTotals = (
    lines: InvoiceLine[],
    deductions: AdvanceDeduction[] = [],
    retention?: RetentionInput,
): InvoiceTotals => {
    const taxGroups = groupTaxes(lines);
    const netTotalCents = sumCents(taxGroups.map((g) => g.taxableNetCents));
    const taxTotalCents = sumCents(taxGroups.map((g) => g.taxCents));
    const grossTotalCents = netTotalCents + taxTotalCents;
    const prepaidCents = sumCents(deductions.map((d) => d.grossCents));
    const priorRetentionCents = sumCents(
        (retention?.priorRetentions ?? []).map((r) => r.retentionCents),
    );
    const r = computeRetention(
        grossTotalCents,
        prepaidCents,
        retention?.percent ?? 0,
        priorRetentionCents,
    );

    return {
        netTotalCents,
        taxTotalCents,
        grossTotalCents,
        prepaidCents,
        ...r,
        payableCents: grossTotalCents - prepaidCents - r.retentionCents + r.retentionReleaseCents,
        taxGroups,
    };
};

/**
 * Beschriftung der Einbehaltzeile im Summenblock: "abzüglich 5 % Sicherheitseinbehalt".
 *
 * Eine Quelle fuer Andruck und Maske, wie bei den Abzugszeilen. Der Betrag
 * steht nicht im Text, sondern in der Betragsspalte daneben.
 */
export const retentionNote = (percent: number): string =>
    `abzüglich ${formatPercent(percent)} % Sicherheitseinbehalt`;

/**
 * Die kleine Zeile unter dem Einbehalt: "auf die Gesamtleistung von 51.726,33 €".
 *
 * Nennt die Grundlage, damit der Empfaenger den Einbehalt nachrechnen kann —
 * derselbe Grund, aus dem der Positionsnachlass seine Grundlage nennt
 * (lineDiscountNote). Kumulativ ist die Grundlage der LEISTUNGSSTAND (gross),
 * nicht die Bruttoforderung dieses Belegs: der gedruckte Satz mal der
 * gedruckten Grundlage ergibt den kumulativen Einbehalt, und die Differenz zum
 * bereits Einbehaltenen ist die Zeile darueber.
 */
export const retentionBasisNote = (grossCents: Cents): string =>
    `auf die Gesamtleistung von ${formatCents(grossCents)}`;

// ═══════════════════════════════════════════════════════════════════════════
// Nummernkreis
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Welcher Nummernkreis fuer diese Belegart gilt.
 *
 * Im Modus 'shared' laeuft ein einziger Zaehler ueber alle Belegarten — so vom
 * Betreiber gewuenscht. 'per_type' gibt jeder Art einen eigenen Kreis. Der
 * Wechsel ist reine Konfiguration und braucht keine Datenmigration.
 */
export const resolveSeries = (type: DocumentType, cfg: InvoiceConfig): InvoiceSeries =>
    cfg.numbering.mode === 'shared' ? cfg.numbering.sharedSeries : SERIES_FOR_DOCUMENT_TYPE[type];

// ═══════════════════════════════════════════════════════════════════════════
// Status
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Erlaubte Statusuebergaenge.
 *
 * OVERDUE fehlt hier bewusst als Ziel: Ueberfaelligkeit ist kein Zustand, in
 * den jemand versetzt, sondern eine Folge des Datums (siehe
 * effectiveInvoiceStatus).
 */
export const ALLOWED_INVOICE_TRANSITIONS: Record<InvoiceStatus, InvoiceStatus[]> = {
    DRAFT: ['READY_FOR_REVIEW', 'APPROVED', 'CANCELLED'],
    READY_FOR_REVIEW: ['DRAFT', 'APPROVED', 'ISSUING'],
    APPROVED: ['DRAFT', 'READY_FOR_REVIEW', 'ISSUING'],
    // Nur die Ausstellung selbst verlaesst diesen Zustand: nach vorn zu ISSUED,
    // bei einem Fehler zurueck zu APPROVED.
    ISSUING: ['ISSUED', 'APPROVED'],
    ISSUED: [
        'SENT',
        'PARTIALLY_PAID',
        'PAID',
        'PARTIALLY_CREDITED',
        'CREDITED',
        'CANCELLED',
        'DISPUTED',
        'SUPERSEDED_BY_FINAL_INVOICE',
    ],
    SENT: [
        'PARTIALLY_PAID',
        'PAID',
        'PARTIALLY_CREDITED',
        'CREDITED',
        'CANCELLED',
        'DISPUTED',
        'SUPERSEDED_BY_FINAL_INVOICE',
    ],
    PARTIALLY_PAID: ['PAID', 'PARTIALLY_CREDITED', 'CREDITED', 'CANCELLED', 'DISPUTED'],
    PAID: ['PARTIALLY_CREDITED', 'CREDITED', 'REFUND_PENDING', 'DISPUTED'],
    PARTIALLY_CREDITED: ['CREDITED', 'PAID', 'REFUND_PENDING', 'CANCELLED'],
    CREDITED: ['REFUND_PENDING', 'REFUNDED'],
    REFUND_PENDING: ['REFUNDED', 'CREDITED'],
    REFUNDED: [],
    DISPUTED: ['ISSUED', 'SENT', 'CANCELLED', 'PAID'],
    OVERDUE: [],
    // Zurueck nach ISSUED bzw. PAID, wenn die abloesende Schlussrechnung
    // storniert wird: der Abschlag ist dann durch nichts mehr ersetzt. Ohne
    // diesen Weg waeren die Abschlaege nach einem Storno endgueltig blockiert —
    // weder offen noch erneut abrechenbar, obwohl der Kunde sie bezahlt hat.
    SUPERSEDED_BY_FINAL_INVOICE: ['CANCELLED', 'ISSUED', 'PAID'],
    CANCELLED: [],
};

export const canTransitionInvoice = (from: InvoiceStatus, to: InvoiceStatus): boolean =>
    (ALLOWED_INVOICE_TRANSITIONS[from] || []).includes(to);

/**
 * Laesst sich aus diesem Zustand heraus ausstellen?
 *
 * Zielzustand ist ISSUING, nicht ISSUED: die Ausstellung ist zweistufig. Erst
 * beansprucht sie den Beleg (ISSUING), dann zieht sie Nummer, rendert, hasht
 * und archiviert, und erst danach steht ISSUED.
 *
 * Als eigene Funktion, weil genau diese Verwechslung schon einmal passiert ist:
 * geprueft wurde auf ISSUED, und da nur ISSUING dorthin fuehrt, lehnte der
 * Wachposten JEDEN Ausstellversuch ab — mit der Meldung, der Status APPROVED
 * sei dafuer nicht geeignet.
 */
export const canIssueFrom = (status: InvoiceStatus): boolean =>
    canTransitionInvoice(status, 'ISSUING');

/**
 * Bis einschliesslich dieser Status darf der BENUTZER den Beleg aendern.
 *
 * ISSUING fehlt bewusst: waehrend der Ausstellung schreibt ausschliesslich
 * issueInvoice. Der Datenbank-Trigger laesst ISSUING zwar zu — sonst koennte
 * sich die Rechnung nicht selbst festschreiben —, die Oberflaeche darf dort
 * aber nichts anbieten.
 */
export const EDITABLE_STATUSES: InvoiceStatus[] = ['DRAFT', 'READY_FOR_REVIEW', 'APPROVED'];

export const isEditable = (status: InvoiceStatus): boolean => EDITABLE_STATUSES.includes(status);

/**
 * Status, in denen ein Beleg Mengen reserviert. Muss der WHERE-Bedingung von
 * v_invoice_line_reservations entsprechen — dieselbe Menge wie in
 * invoiceService.RESERVING_STATUSES.
 */
export const RESERVING_INVOICE_STATUSES: ReadonlySet<InvoiceStatus> = new Set([
    'DRAFT',
    'READY_FOR_REVIEW',
    'APPROVED',
]);

/**
 * Menge, die EINE Zeile in v_invoice_line_reservations beitraegt.
 *
 * Das ist der Gleichlauf mit der Sicht in Rechenform: die installierte Fassung
 * (supabase/invoices_reservations_cumulative.sql, ergaenzt um
 * supabase/fix_reservations_quantity_informational.sql) summiert je Zeile
 * greatest(quantity - previouslyBilledQty, 0) und laesst Zeilen mit
 * quantityInformational ganz aus. Jede Stelle, die den EIGENEN Anteil eines
 * Belegs aus der Sicht herausrechnet, muss genau diesen Ausdruck verwenden.
 *
 * Die Klemmung auf >= 0 ist der Kern: eine Storno-/Gutschriftzeile traegt eine
 * NEGATIVE Menge (mirrorLines). Wurde die rohe quantity abgezogen, entstand aus
 * 0 - (-1) eine Reservierung von +1, die es nirgends gibt — der Projektcheck
 * meldete "Menge doppelt beansprucht" ausgerechnet fuer den Storno, der die
 * Menge zurueckgibt.
 *
 * Aendert sich die Sicht, MUSS diese Funktion mitgeaendert werden.
 */
export const reservedQtyOfLine = (
    line: Pick<InvoiceLine, 'quantity' | 'previouslyBilledQty' | 'quantityInformational'>,
): number => {
    if (line.quantityInformational) return 0;
    return Math.max(0, (line.quantity ?? 0) - (line.previouslyBilledQty ?? 0));
};

/** projectId:serviceId je SERVICE-Zeile mit LV-Herkunft. */
const reservedLineKeys = (inv: Invoice): Set<string> => {
    const keys = new Set<string>();
    for (const l of inv.lines) {
        if (l.kind !== 'SERVICE' || !l.projectId || !l.serviceId) continue;
        keys.add(`${l.projectId}:${l.serviceId}`);
    }
    return keys;
};

/** Mengenlage je projectId:serviceId, Stand NACH der Ausstellung. */
export interface CompetingDraftContext {
    /** Erbracht/abgerechnet aus dem Projekt-LV — inkl. der Rueckschreibung des eben ausgestellten Belegs. */
    quantities: Map<string, { used: number; billed: number }>;
    /** Reservierte Menge ueber ALLE noch offenen Belege (v_invoice_line_reservations). */
    reservations: Map<string, number>;
}

/** Mengen sind auf 3 Nachkommastellen genau — Gleichstand darf nicht an Gleitkommarauschen scheitern. */
const QTY_EPSILON = 1e-9;

/**
 * Offene Belege, die LV-Positionen des ausgestellten Belegs beanspruchen und
 * deren Menge dort nicht mehr offen ist.
 *
 * Die blosse Ueberlappung genuegt NICHT: bei Teilabrechnungen tragen mehrere
 * Belege dieselbe Position mit insgesamt zulaessigen Mengen — so ein Entwurf
 * ist weiterhin ausstellbar und darf nicht zur Loeschung angeboten werden.
 * Konkurrent ist nur, wessen Menge je Position — ueber alle Zeilen des Belegs
 * summiert — die offene Menge (erbracht minus abgerechnet minus fremde
 * Reservierungen) uebersteigt: dieselbe Rechnung, mit der der Projektcheck
 * "Menge doppelt beansprucht" meldet. Fehlt der
 * Mengenstand zu einer Position, gilt der Entwurf konservativ als NICHT
 * konkurrierend: ohne Daten wird keine Loeschung angeboten. Die Funktion
 * loescht selbst nichts.
 */
export const findCompetingDrafts = (
    issued: Invoice,
    candidates: Invoice[],
    ctx: CompetingDraftContext,
): Invoice[] => {
    const issuedKeys = reservedLineKeys(issued);
    if (!issuedKeys.size) return [];

    const isBlocked = (c: Invoice): boolean => {
        // Eigene Reservierung je Position — die Sicht aggregiert ueber alle
        // Belege, der eigene Anteil muss raus (wie in fetchReservations).
        // Gezaehlt wird ueber reservedQtyOfLine, also mit derselben Rechnung wie
        // in der Sicht: die rohe Menge liess eine negative Storno-Zeile den
        // fremden Anteil aufblaehen (reservations - (-1)) und daraus einen
        // Konkurrenten entstehen, den es nicht gibt.
        const ownReserved = new Map<string, number>();
        for (const l of c.lines) {
            if (l.kind !== 'SERVICE' || !l.projectId || !l.serviceId) continue;
            const key = `${l.projectId}:${l.serviceId}`;
            ownReserved.set(key, (ownReserved.get(key) ?? 0) + reservedQtyOfLine(l));
        }
        // Geprueft wird je Position, nicht je Zeile: ein Beleg darf dieselbe
        // Position mehrfach fuehren, reserviert wird die Summe. Zeilenweise
        // blieben zwei Zeilen zu je 6 bei 7 offenen Einheiten einzeln
        // zulaessig, obwohl der Beleg 12 beansprucht.
        for (const [key, reserved] of ownReserved) {
            if (!issuedKeys.has(key)) continue;
            // Wer nichts beansprucht, kann nichts streitig machen. Storno- und
            // reine Betragskorrektur-Belege landen nach der Klemmung bei 0 —
            // ohne diese Zeile gaebe eine anderweitig ueberzeichnete Position
            // (open < 0) sie zum Loeschen frei, obwohl sie Mengen nur zurueck-
            // geben.
            if (reserved <= 0) continue;
            const q = ctx.quantities.get(key);
            if (!q) continue;
            const othersReserved = Math.max(0, (ctx.reservations.get(key) ?? 0) - reserved);
            const open = q.used - q.billed - othersReserved;
            if (reserved > open + QTY_EPSILON) return true;
        }
        return false;
    };

    return candidates.filter(
        (c) =>
            c.id !== issued.id && RESERVING_INVOICE_STATUSES.has(c.status) && isBlocked(c),
    );
};

/**
 * Status, wie er dem Nutzer angezeigt wird.
 *
 * OVERDUE wird abgeleitet statt gespeichert — genau das Muster von
 * effectiveQuoteStatus. Dadurch gibt es eine einzige Wahrheit fuer Liste,
 * Kennzahlen und Mahnwesen, und es braucht keinen naechtlichen Job, der Status
 * umschreibt (und dabei die Festschreibung verletzen wuerde).
 */
export const effectiveInvoiceStatus = (
    invoice: Pick<Invoice, 'status' | 'dueDate'>,
    todayIso: string,
): InvoiceStatus => {
    // PARTIALLY_CREDITED gehoert dazu: nach einer Teilgutschrift steht der Rest
    // weiterhin offen. Ohne den Status wurde ein Beleg mit 1.000 EUR
    // Restforderung nie ueberfaellig — nicht gemahnt, in der Altersstruktur im
    // Balken "0-30 Tage", und in "davon ueberfaellig" gar nicht enthalten.
    // OWING_STATUSES in invoiceStats und bookable fuehren ihn laengst so.
    //
    // DISPUTED bleibt bewusst draussen: ein bestrittener Beleg soll nicht
    // automatisch gemahnt werden. Die Altersstruktur rechnet ihn deshalb ueber
    // daysPastDue statt ueber diesen Status ein.
    const owing: InvoiceStatus[] = ['ISSUED', 'SENT', 'PARTIALLY_PAID', 'PARTIALLY_CREDITED'];
    if (owing.includes(invoice.status) && invoice.dueDate && invoice.dueDate < todayIso) {
        return 'OVERDUE';
    }
    return invoice.status;
};

/** Tage ueber der Faelligkeit. 0, solange nichts ueberfaellig ist. */
export const daysOverdue = (
    invoice: Pick<Invoice, 'status' | 'dueDate'>,
    todayIso: string,
): number => {
    if (effectiveInvoiceStatus(invoice, todayIso) !== 'OVERDUE' || !invoice.dueDate) return 0;
    return daysPastDue(invoice, todayIso);
};

/**
 * Tage seit der Faelligkeit, OHNE Ruecksicht auf den Status.
 *
 * Fuer die Altersstruktur: ein bestrittener Beleg (DISPUTED) wird bewusst nicht
 * gemahnt, ist aber trotzdem seit Monaten faellig. Ueber daysOverdue gerechnet
 * landete er im Balken "0-30 Tage" und liess die Forderungen juenger aussehen,
 * als sie sind.
 */
export const daysPastDue = (invoice: Pick<Invoice, 'dueDate'>, todayIso: string): number => {
    if (!invoice.dueDate) return 0;
    const ms = Date.parse(`${todayIso}T00:00:00Z`) - Date.parse(`${invoice.dueDate}T00:00:00Z`);
    return Math.max(0, Math.round(ms / 86_400_000));
};

/** Faelligkeitsdatum aus Rechnungsdatum und Zahlungsziel. */
export const computeDueDate = (invoiceDate: string, paymentTermsDays: number): string => {
    const d = new Date(`${invoiceDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + (paymentTermsDays || 0));
    return d.toISOString().slice(0, 10);
};

/**
 * Zahlungsbedingungen-Satz mit Skonto, wie er auf dem Beleg steht.
 *
 * Das Skonto-Datum wird aus Rechnungsdatum + Skontofrist AUSGERECHNET und in
 * den Satz geschrieben — der Kunde soll den Stichtag lesen, nicht rechnen.
 * Der Satz ist eine Vorbelegung von details.paymentTerms und bleibt dort frei
 * editierbar; nach einer Aenderung von Datum oder Fristen wird er deshalb
 * nicht automatisch nachgezogen (das ueberschriebe eine Handkorrektur),
 * sondern im Editor bewusst neu eingesetzt.
 */
export const skontoPaymentTermsText = (
    invoiceDate: string,
    paymentTermsDays: number,
    skonto: { percent: number; days: number },
): string => {
    const [y, m, d] = computeDueDate(invoiceDate, skonto.days).split('-');
    const percent = String(skonto.percent).replace('.', ',');
    return (
        `Zahlbar innerhalb von ${skonto.days} Tagen (${d}.${m}.${y}) abzüglich ` +
        `${percent} % Skonto, ansonsten innerhalb von ${paymentTermsDays} Tagen rein netto.`
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// Zahlungen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Teilt einen Zahlbetrag in Netto und Steuer auf.
 *
 * Bei genau einem Steuersatz ist das eindeutig. Bei mehreren waere eine
 * automatische proportionale Verteilung riskant (Spezifikation §16.2), deshalb
 * liefert die Funktion dann null — der Aufrufer muss eine fachlich bestaetigte
 * Zuordnung verlangen, statt stillschweigend zu schaetzen.
 */
export const allocatePayment = (
    amountCents: Cents,
    taxGroups: TaxGroup[],
): { allocatedNetCents: Cents; allocatedTaxCents: Cents } | null => {
    const relevant = taxGroups.filter((g) => g.taxableNetCents !== 0);
    if (relevant.length === 0) {
        return { allocatedNetCents: amountCents, allocatedTaxCents: 0 };
    }
    if (relevant.length > 1) return null;

    const rate = relevant[0].taxRateBp;
    const net = roundCents((amountCents * 10_000) / (10_000 + rate));
    // Steuer als Rest, damit netto + steuer === Zahlbetrag immer gilt.
    return { allocatedNetCents: net, allocatedTaxCents: amountCents - net };
};

/** Zahlungsstatus aus den Aggregaten. */
export const derivePaymentStatus = (
    payableCents: Cents,
    paidCents: Cents,
    creditedCents: Cents,
): 'UNPAID' | 'PARTIALLY_PAID' | 'PAID' | 'OVERPAID' => {
    const settled = paidCents + creditedCents;
    if (settled <= 0) return 'UNPAID';
    if (settled < payableCents) return 'PARTIALLY_PAID';
    if (settled > payableCents) return 'OVERPAID';
    return 'PAID';
};

/**
 * Ueberzahlung. Positiver Betrag heisst: es ist ein Kundenguthaben entstanden.
 *
 * Das System darf nie einen negativen "zu zahlenden Betrag" ausweisen
 * (Spezifikation §20) — der Ueberhang wird als Guthaben gefuehrt.
 */
export const overpaymentCents = (
    payableCents: Cents,
    paidCents: Cents,
    creditedCents: Cents,
): Cents => {
    // Ein negativer Zahlbetrag ist KEINE Ueberzahlung. Ohne diese Schranke
    // meldete jedes Storno ein Kundenguthaben in Hoehe seines Bruttobetrags —
    // max(0, 0 - (-119000)) = 119000 — und das Cockpit wies Guthaben aus,
    // obwohl nie ein Cent geflossen ist. Die Erstattung eines negativen Belegs
    // laeuft ueber die Gutschrift, nicht ueber diese Groesse.
    if (payableCents < 0) return 0;
    return Math.max(0, paidCents + creditedCents - payableCents);
};

/** Noch offener Betrag. Nie negativ — ein Ueberhang ist Guthaben, keine Schuld. */
export const openAmountCents = (
    invoice: Pick<Invoice, 'totals' | 'paidCents' | 'creditedCents'>,
): Cents => Math.max(0, invoice.totals.payableCents - invoice.paidCents - invoice.creditedCents);

// ═══════════════════════════════════════════════════════════════════════════
// Mahnwesen
// ═══════════════════════════════════════════════════════════════════════════

export interface DunningLevelConfig {
    level: 1 | 2 | 3;
    daysAfterDue: number;
    feeCents: Cents;
}

/**
 * Voreinstellung der Mahnstufen.
 *
 * Stufe 1 ohne Gebuehr: die erste Erinnerung ist keine Mahnung im Rechtssinn
 * und traegt daher keine Kosten. Ab Stufe 2 befindet sich der Kunde im Verzug
 * (§ 286 BGB), erst dann ist eine Mahngebuehr durchsetzbar.
 */
export const DEFAULT_DUNNING_LEVELS: DunningLevelConfig[] = [
    { level: 1, daysAfterDue: 7, feeCents: 0 },
    { level: 2, daysAfterDue: 21, feeCents: 500 },
    { level: 3, daysAfterDue: 42, feeCents: 1000 },
];

export const resolveDunningLevels = (cfg?: Pick<InvoiceConfig, 'dunningLevels'>) => {
    const raw = cfg?.dunningLevels;
    if (!raw?.length) return DEFAULT_DUNNING_LEVELS;
    return [...raw].sort((a, b) => a.daysAfterDue - b.daysAfterDue) as DunningLevelConfig[];
};

/**
 * Faellige Mahnstufe — oder null, wenn nichts zu mahnen ist.
 *
 * Gibt bewusst nur die NAECHSTE Stufe zurueck, nicht die hoechste erreichte:
 * wer 60 Tage ueberfaellig ist und noch nie gemahnt wurde, bekommt Stufe 1,
 * nicht Stufe 3. Eine dritte Mahnung ohne erste ist keine wirksame Mahnung.
 */
export const dueDunningLevel = (
    invoice: Pick<Invoice, 'status' | 'dueDate' | 'dunningLevel' | 'totals' | 'paidCents' | 'creditedCents'>,
    todayIso: string,
    levels: DunningLevelConfig[] = DEFAULT_DUNNING_LEVELS,
): DunningLevelConfig | null => {
    if (effectiveInvoiceStatus(invoice, todayIso) !== 'OVERDUE') return null;
    if (openAmountCents(invoice) <= 0) return null;

    const current = invoice.dunningLevel ?? 0;
    const next = levels.find((l) => l.level > current);
    if (!next) return null;
    return daysOverdue(invoice, todayIso) >= next.daysAfterDue ? next : null;
};

// ═══════════════════════════════════════════════════════════════════════════
// Positionen aus dem Projekt-LV
// ═══════════════════════════════════════════════════════════════════════════

export interface OpenPosition {
    service: ServiceItem;
    /** Noch abrechenbare Menge: erbracht minus abgerechnet minus reserviert. */
    openQty: number;
    unitPriceCents: Cents;
    netCents: Cents;
    /**
     * Gesetzt, wenn ein Leistungszeitraum die Menge unter den insgesamt offenen
     * Wert gedrueckt hat. Traegt die Gesamtmenge, damit die Oberflaeche sagen
     * kann, was ausserhalb des Zeitraums liegen bleibt.
     */
    periodLimitedFromQty?: number;
}

/** Leistungszeitraum als Filter fuer die offenen Positionen (BT-73/BT-74). */
export interface OpenPositionPeriod {
    /** yyyy-mm-dd, einschliesslich. Leer heisst "nach hinten offen". */
    from?: string;
    /** yyyy-mm-dd, einschliesslich. */
    to?: string;
    /** Arbeitsprotokoll des Projekts — die einzige Quelle mit Datumsbezug. */
    serviceLog?: ServiceTransaction[];
    /**
     * Nimmt zusaetzlich die Mengen mit, zu denen es ueberhaupt keinen
     * Protokolleintrag gibt.
     *
     * Sie gehoeren zu keinem Monat und faellen sonst durch jedes Zeitfenster —
     * bei Altbestaenden dauerhaft. Das ist eine bewusste Entscheidung des
     * Nutzers, keine Voreinstellung: er schreibt sie damit dem gewaehlten
     * Zeitraum zu, und niemand sonst kann wissen, ob das stimmt.
     */
    includeUndated?: boolean;
    /**
     * Belege des Projekts.
     *
     * Aus ihnen kommt die festgeschriebene Zuordnung zu einzelnen
     * Protokolleintraegen. Damit zaehlt im Zeitraum nur noch, was von diesen
     * Eintraegen offen ist. Ohne sie bleibt es beim bisherigen Verhalten —
     * die volle protokollierte Menge, gedeckelt auf den offenen Gesamtstand.
     */
    invoices?: Pick<Invoice, 'lines'>[];
}

/**
 * Menge je Leistung, die im Leistungsverzeichnis steht, aber in keinem
 * Protokolleintrag — also zu keinem Datum gehoert.
 *
 * Entsteht, wo quantityUsed direkt gesetzt wurde. Negative Eintraege
 * (Korrekturen nach unten, Storno) zaehlen mit, damit die Summe der Bewegungen
 * dem IST-Stand entspricht.
 */
const undatedQuantities = (
    services: ServiceItem[],
    log: ServiceTransaction[],
): Map<string, number> => {
    const logged = new Map<string, number>();
    for (const entry of log) {
        if (!entry?.serviceId || isAccountingEntry(entry)) continue;
        logged.set(entry.serviceId, (logged.get(entry.serviceId) ?? 0) + (entry.quantity || 0));
    }
    const out = new Map<string, number>();
    for (const service of services) {
        out.set(service.id, Math.max(0, (service.quantityUsed || 0) - (logged.get(service.id) ?? 0)));
    }
    return out;
};

/** Eine Position, deren Menge zu keinem Datum gehoert. */
export interface UndatedBacklogItem {
    serviceId: string;
    title: string;
    unit?: string;
    lvPosition?: string;
    /** Die Luecke: quantityUsed minus Summe der Protokolleintraege. */
    quantity: number;
    netCents: Cents;
    /**
     * Der Teil der Luecke, der noch NICHT abgerechnet ist — echtes offenes
     * Geld, das ohne Datum durch jede Zeitraumrechnung faellt.
     *
     * Die Trennung ist der Unterschied zwischen "muss ich handeln" und "ist
     * nur Buchhaltungshygiene". Ohne sie sieht eine laengst bezahlte
     * Pauschale genauso dringend aus wie eine vergessene Begehung (6265-1 L:
     * 500,00 ohne Datum, beide Positionen vollstaendig abgerechnet).
     */
    openNetCents: Cents;
    /** Der bereits abgerechnete Teil der Luecke. netCents - openNetCents. */
    billedNetCents: Cents;
}

/**
 * Die Protokolluecke je Position — was das Leistungsverzeichnis als erbracht
 * fuehrt, wovon aber kein Eintrag im Leistungsprotokoll erzaehlt.
 *
 * Der Unterschied zu undatedOpenSummary ist Absicht: die Zusammenfassung zeigt
 * nur den noch OFFENEN Teil (das ist die Zahl, die der Anwender abrechnen
 * kann), hier steht die ganze Luecke. Geschlossen werden muss sie ganz —
 * computeCumulativePositions misst gegen quantityUsed, und solange die
 * Eintragssumme darunter liegt, faellt der Rest durch jeden Zeitraum
 * (openByMonth: „ohne Datum").
 *
 * Negative Luecken kommen nicht vor: quantityUsed unter der Eintragssumme
 * heisst ueberprotokolliert, und das ist ein anderer Fall — er wird hier
 * uebergangen, nicht stillschweigend ausgeglichen.
 */
export const undatedBacklog = (
    services: ServiceItem[],
    log: ServiceTransaction[],
): UndatedBacklogItem[] => {
    const gaps = undatedQuantities(services, log);
    const out: UndatedBacklogItem[] = [];
    for (const service of services) {
        const quantity = gaps.get(service.id) ?? 0;
        if (quantity <= UNCOVERED_QTY_EPSILON) continue;
        const unitPriceCents = roundCents((service.unitPrice || 0) * 100);
        const netCents = roundCents(quantity * unitPriceCents);
        // Wieviel der Luecke ist noch offen? Die offene Menge der Position ist
        // erbracht minus abgerechnet; mehr als die Luecke kann davon nicht in
        // ihr stecken. Der Rest der Luecke ist bereits berechnet worden — das
        // Datum fehlt dort trotzdem, es kostet nur kein Geld.
        const openQty = Math.min(
            quantity,
            Math.max(0, (service.quantityUsed || 0) - (service.quantityBilled || 0)),
        );
        const openNetCents = roundCents(openQty * unitPriceCents);
        out.push({
            serviceId: service.id,
            title: service.title,
            unit: service.unit,
            lvPosition: service.lvPosition,
            quantity,
            netCents,
            openNetCents,
            billedNetCents: netCents - openNetCents,
        });
    }
    return out;
};

/**
 * Die Protokolleintraege, die die Luecke schliessen — ein Sammelnachtrag je
 * Position.
 *
 * KEINE Buchungsfelder — weder Referenz noch bookedAt: der Eintrag ist
 * Leistung, kein Abrechnungsvorgang. Dieselbe Form wie der Korrektureintrag
 * des Nachtragsdialogs (ProjectSupplementModal).
 * Mit einer Referenz zaehlte ihn isAccountingEntry heraus, die Luecke bliebe
 * bestehen, und der naechste Klick legte denselben Eintrag noch einmal an.
 *
 * Das Datum ist eine AUSSAGE des Anwenders — „diese Menge wurde bis dahin
 * erbracht". Erfunden wird es nicht: der Aufrufer gibt es herein, und die
 * Beschreibung nennt den Vorgang, damit im Protokoll erkennbar bleibt, dass
 * die Menge nachgetragen und nicht an diesem Tag gemeldet wurde.
 */
/**
 * Wer im Protokoll steht, wenn der Nachtlauf nachtraegt — und nicht ein Mensch.
 *
 * `performerName` beantwortet die Frage, WER die Leistung erbracht hat. Solange
 * der Sammelnachtrag beim Oeffnen der Rechnungsansicht von selbst lief, stand
 * dort der Name dessen, der das Fenster offen hatte: am 31.08.2026 acht
 * Eintraege in drei Projekten auf eine Kollegin, die nichts angeklickt hatte.
 * Ein Vorgang ohne Menschen dahinter sagt das jetzt selbst.
 */
export const SYSTEM_BACKLOG_ACTOR = 'System (Sammelnachtrag)';

export const planUndatedBacklogEntries = (
    items: UndatedBacklogItem[],
    opts: {
        /** JJJJ-MM-TT. */
        date: string;
        actorId: string;
        actorName: string;
        newId: () => string;
    },
): ServiceTransaction[] =>
    items.map((item) => ({
        id: opts.newId(),
        date: opts.date,
        serviceId: item.serviceId,
        quantity: item.quantity,
        performerId: opts.actorId,
        performerName: opts.actorName,
        isSubstitution: false,
        description: `Menge ohne Protokolleintrag nachgetragen (Sammelnachtrag, ${item.quantity} ${item.unit ?? ''})`.trim(),
    }));

/** Ein Monat und der Nettowert, der dort noch offen ist. */
export interface OpenMonthBucket {
    /** 'JJJJ-MM', oder null fuer Mengen ohne Datum. */
    month: string | null;
    netCents: Cents;
}

/**
 * Der offene Bestand, aufgeteilt nach dem Monat der Leistung.
 *
 * Beantwortet die Frage, die ein Zeitraumfilter aufwirft: waehle ich Mai, was
 * bleibt dann in Juni und Juli liegen? Ohne diese Uebersicht sieht der
 * Anwender nur, was er gerade nimmt, und nie, was er stehen laesst.
 *
 * JUENGSTE ZUERST: das Leistungsverzeichnis merkt sich nur eine Gesamtsumme
 * (quantityBilled), nicht WELCHE Protokolleintraege abgerechnet wurden.
 * Abgerechnet wird von alt nach neu, also gehoert das noch Offene ans Ende der
 * Zeitachse. Umgekehrt zugeteilt behauptete die Uebersicht offene Mengen in
 * einem Monat, der laengst abgerechnet ist.
 *
 * Die Summe ueber alle Monate ergibt deshalb genau den offenen Gesamtbestand —
 * jeder Monat wird nur so weit gefuellt, wie ueberhaupt noch etwas offen ist.
 */
export const openByMonth = (
    services: ServiceItem[],
    serviceLog: ServiceTransaction[] = [],
    reservations: Map<string, number> = new Map(),
    /**
     * Die Belege des Projekts. Aus ihnen kommt die festgeschriebene Zuordnung
     * zu den einzelnen Protokolleintraegen — dann wird gerechnet statt geraten.
     */
    invoices: Pick<Invoice, 'lines'>[] = [],
    /**
     * Mengen, die eine ALT-Rechnung bereits abgegolten hat
     * (externalAdvances.altCoveredQuantities).
     *
     * NUR fuer diese Uebersicht: nachgetragene Alt-Positionen tragen
     * quantityBilled 0 — sie stuenden sonst als "offen" in einem Monat, obwohl
     * sie mit dem Altbeleg gefordert wurden. Sie werden wie unzugeordneter
     * Altbestand behandelt: vom Gesamtoffenen abgezogen und von den AELTESTEN
     * Eintraegen geschluckt — dort, wo die Alt-Buchungen liegen.
     *
     * Leer heisst: unveraendertes Verhalten. Die Zeilenbildung der Rechnung und
     * die Bemessungsgrundlage der Abzuege sehen diesen Wert nie.
     */
    altCovered: Map<string, number> = new Map(),
    /**
     * ids der ALT-Protokollzeilen (externalAdvances.backfillEntryIds).
     *
     * Sie belegen keinen offenen Monat: was auf einer Alt-Rechnung stand, ist
     * bezahlte Vergangenheit. Ohne diese Liste bleibt nur die Annahme "das
     * Aelteste zuerst" — und die trifft daneben, sobald der Altbeleg spaeter
     * datiert als regulaer erfasste Leistung: dann schluckt der Altbestand die
     * echten Vormonate, und der Monat der Alt-Buchung erscheint als offen.
     *
     * Leer heisst: unveraendertes Verhalten.
     */
    altEntryIds: ReadonlySet<string> = new Set(),
): OpenMonthBucket[] => {
    const allocatedEntries = allocatedByEntry(invoices);
    const allocatedServices = allocatedByService(invoices);

    const buckets = new Map<string | null, Cents>();
    const add = (month: string | null, cents: Cents) =>
        buckets.set(month, (buckets.get(month) ?? 0) + cents);

    // Offene Eintraege je Leistung, aelteste zuerst — in dieser Reihenfolge
    // wird gleich der unzugeordnete Altbestand abgezogen.
    //
    // ALT-Zeilen kommen hier gar nicht erst hinein; was sie an offener Menge
    // getragen haetten, wird je Leistung festgehalten, damit der Altbestand
    // unten nicht ein zweites Mal abgezogen wird.
    //
    // VERZICHTETE Eintraege belegen ebenfalls keinen Monat: `overallOpen`
    // zieht ihre Menge schon ab, verteilt wird der Rest. Blieben sie in der
    // Liste, schluckten sie diesen Rest in IHREM Monat, und die spaeteren
    // offenen Termine fehlten (6172: „Oktober 3, November 0" statt 2 und 1).
    // Fuer die Zeilenmenge der ALT-Regel unten zaehlen sie weiter — sie sind
    // erbracht und stecken in `quantityUsed`.
    const verzichtet = verzichteteEintraege(services);
    const byService = new Map<string, ReturnType<typeof openEntries>>();
    const removedAltQty = new Map<string, number>();
    for (const entry of openEntries(serviceLog, allocatedEntries)) {
        if (altEntryIds.has(entry.id)) {
            removedAltQty.set(
                entry.serviceId,
                (removedAltQty.get(entry.serviceId) ?? 0) + entry.openQty,
            );
            continue;
        }
        if (verzichtet.get(entry.serviceId)?.has(kennungFuerVergleich(entry.id))) continue;
        const list = byService.get(entry.serviceId);
        if (list) list.push(entry);
        else byService.set(entry.serviceId, [entry]);
    }
    for (const list of byService.values()) {
        // Auf der BERLINER Wanduhr, Gleichstand nach id — dieselbe Ordnung wie
        // `orderForPeriod` in allocationLogic (Codex-Review S5a, 03.10.2026):
        // sonst zehrte quantityBilled hier andere Eintraege auf als die
        // Zuordnung, und die Monatsuebersicht stuende neben der Rechnung.
        list.sort((a, b) => {
            const za = berlinerSortierzeit(a.date) ?? '9999-99-99';
            const zb = berlinerSortierzeit(b.date) ?? '9999-99-99';
            if (za !== zb) return za.localeCompare(zb);
            return kennungFuerVergleich(a.id).localeCompare(kennungFuerVergleich(b.id));
        });
    }

    // Die im Projekt selbst protokollierte Leistung je Position — ohne die
    // ALT-Zeilen. Sie entscheidet gleich darueber, WIEVIEL eine ALT-Zeile
    // ueberhaupt an Leistung traegt.
    //
    // Denn es gibt zwei Sorten ALT-Zeile, und sie sehen gleich aus:
    //   * Die nachgetragene Altposition: es gibt keine einzelnen Berichte, die
    //     Zeile IST die erbrachte Leistung und steckt in `quantityUsed`.
    //   * Der blosse Abrechnungsvermerk: die Begehungen stehen bereits einzeln
    //     im Protokoll, die ALT-Zeile haelt nur fest, welcher Altbeleg sie
    //     gefordert hat — `quantityUsed` zaehlt sie NICHT mit.
    // Bei 6290-1 (2.7.10 Horster Dreieck) ist es die zweite Sorte: 21
    // Berichtszeilen sind `quantityUsed` 21, die ALT-Zeile mit 13 kommt
    // obendrauf.
    const eigeneProtokollMenge = new Map<string, number>();
    for (const entry of allocatableEntries(serviceLog)) {
        if (altEntryIds.has(entry.id)) continue;
        eigeneProtokollMenge.set(
            entry.serviceId,
            (eigeneProtokollMenge.get(entry.serviceId) ?? 0) + entry.quantity,
        );
    }

    for (const service of services) {
        // quantityBilled zehrt die AELTESTEN Mengen auf — und das sind gerade
        // die Alt-Mengen. Was regulaer abgerechnet wurde, ist keine offene
        // Alt-Deckung mehr; ohne diesen Abzug gaelte nach der ersten
        // kumulativen Rechnung (billed = used) jede NEUE Einheit dieser
        // Position dauerhaft als gedeckt und fehlte in jeder Monatsuebersicht.
        const altRaw = altCovered.get(service.id) ?? 0;
        const billed = service.quantityBilled || 0;
        const altQty = Math.max(0, altRaw - billed);
        const overallOpen = Math.max(
            0,
            openQuantity(service, reservations.get(service.id) ?? 0) - altQty,
        );
        if (overallOpen <= 0.0005) continue;
        const unitCents = roundCents((service.unitPrice || 0) * 100);

        // Belege aus der Zeit vor der Zuordnung sagen nicht, welche Leistung
        // sie abgerechnet haben. Diese Menge wird von den AELTESTEN Eintraegen
        // abgezogen — die einzige Annahme, die hier bleibt, und sie betrifft
        // nur Altbelege. Alles Neue ist zugeordnet und wird nicht geraten.
        //
        // Die von einer Alt-Rechnung abgegoltene Menge kommt dazu: sie ist
        // genau derselbe Fall — gefordert, aber ohne Zuordnung zu einer Zeile
        // dieses Systems. Ihre Protokollzeilen sind die aeltesten der Position
        // (sie datieren auf den Altbeleg), werden hier also zuerst geschluckt.
        //
        // Was die herausgenommenen ALT-Zeilen getragen haben, ist bereits
        // erledigt: sie stehen nicht mehr in der Liste, aus der hier verteilt
        // wird. Nur der Rest muss noch von den aeltesten Eintraegen geschluckt
        // werden — ihn ein zweites Mal abzuziehen fraesse echte offene Monate.
        //
        // ABER nur so weit, wie die ALT-Zeile ueberhaupt Leistung TRAEGT.
        // Deckt das eigene Protokoll den erbrachten Stand bereits ab, ist die
        // ALT-Zeile ein blosser Abrechnungsvermerk: sie aus der Verteilung zu
        // nehmen hat dann NICHTS geschluckt, und `legacy` um ihre Menge zu
        // kuerzen zoege den Abzug ein zweites Mal ab. Der Abzug faellt damit
        // auf null, kein einziger alter Eintrag wird gedeckt — und die offene
        // Menge steht auf den AELTESTEN Monaten statt auf den juengsten.
        //
        // Genau so las sich 6290-1 am 02.09.2026: 3.580 EUR offen, ausgewiesen
        // fuer Januar bis Mai, waehrend die ungedeckten Begehungen im Juli,
        // August und September lagen. Der Betrag stimmte, die Zeitachse nicht.
        const removedAlt = removedAltQty.get(service.id) ?? 0;
        const traegtAltLeistung = Math.max(
            0,
            (service.quantityUsed || 0) - (eigeneProtokollMenge.get(service.id) ?? 0),
        );
        let legacy = Math.max(
            0,
            unallocatedBilled(billed, allocatedServices.get(service.id) ?? 0) +
                altQty -
                Math.min(removedAlt, traegtAltLeistung),
        );

        // Die Menge je Monat dieser Leistung wird erst SUMMIERT und dann einmal
        // gerundet — nicht je Eintrag. Sonst haengt der Centbetrag davon ab,
        // in welcher Reihenfolge Bruchmengen desselben Monats gezogen werden:
        // 0,005 + 0,005 Std. zu 3 EUR sind je Eintrag gerundet 2 + 2 Cent,
        // zusammen gerundet 3 Cent (Codex-Review S5a, 03.10.2026, P2 — der
        // Gleichstandsbruch nach id hatte die Reihenfolge verschoben).
        const mengeJeMonat = new Map<string | null, number>();
        const merke = (month: string | null, menge: number) =>
            mengeJeMonat.set(month, (mengeJeMonat.get(month) ?? 0) + menge);

        let assigned = 0;
        for (const entry of byService.get(service.id) ?? []) {
            if (assigned >= overallOpen - 0.0005) break;
            let qty = entry.openQty;
            if (legacy > 0.0005) {
                const swallowed = Math.min(legacy, qty);
                legacy -= swallowed;
                qty -= swallowed;
            }
            if (qty <= 0.0005) continue;
            // Nie mehr ausweisen, als insgesamt offen ist: eine direkte
            // Korrektur des Leistungsstands nach unten kuerzt den Gesamtstand,
            // ohne einen Protokolleintrag zu entfernen.
            const take = Math.min(qty, overallOpen - assigned);
            // Der Monat des BERLINER Tags (Betreiber-Entscheid 1 vom
            // 02.10.2026) — derselbe, in dem der Zeitraumfilter und die
            // Zuordnung die Begehung sehen. Mit dem UTC-Textschnitt stuende
            // eine Begehung von 00:30 Uhr am Monatsersten im Vormonat offen,
            // der Filter auf diesen Vormonat faende sie aber nicht.
            merke(buchungstag(entry.date)?.slice(0, 7) || null, take);
            assigned += take;
        }

        // Was kein Protokolleintrag deckt, gehoert zu keinem Monat — etwa eine
        // von Hand korrigierte Menge.
        const undated = overallOpen - assigned;
        if (undated > 0.0005) merke(null, undated);

        // Kumulativ gerundet: jeder Monat erhaelt rund(Summe bis hier) minus
        // rund(Summe bis zum Vormonat). So ergeben die Monate zusammen genau
        // das gerundete Ganze dieser Leistung, und kein Monat weicht um einen
        // Cent oder mehr von seinem Anteil ab — je Monat gerundet waeren
        // 0,005 Std. im Januar und 0,005 im Februar zu 3 EUR 2 + 2 Cent statt
        // 3 (Codex-Review S5a, Runde 3). Die Map traegt die Monate in der
        // Reihenfolge der Eintraege, also aelteste zuerst, ohne Monat zuletzt;
        // die Verteilung ist damit fest.
        let mengeBisHier = 0;
        let centBisHier = 0;
        for (const [month, menge] of mengeJeMonat) {
            mengeBisHier += menge;
            const bisHier = roundCents(mengeBisHier * unitCents);
            add(month, bisHier - centBisHier);
            centBisHier = bisHier;
        }
    }

    return [...buckets.entries()]
        .map(([month, netCents]) => ({ month, netCents }))
        .filter((b) => b.netCents !== 0)
        // Undatiertes ans Ende: es hat keine Stelle auf der Zeitachse.
        .sort((a, b) => (a.month ?? '9999-99').localeCompare(b.month ?? '9999-99'));
};

/** Ein offener Monat VOR dem Abrechnungszeitraum — datiert, sonst waere er keiner. */
export interface PriorOpenMonth {
    /** 'JJJJ-MM'. */
    month: string;
    netCents: Cents;
}

/**
 * Offene Monate, die VOR dem gewaehlten Abrechnungszeitraum liegen.
 *
 * Der Zeitraumfilter laesst sie stillschweigend liegen: die Rechnung entsteht,
 * sieht vollstaendig aus, und der Altbestand faellt erst Monate spaeter auf.
 * Diese Liste ist die Grundlage der Rueckfrage vor dem Anlegen.
 *
 * Undatiertes bleibt aussen vor: es liegt auf keiner Stelle der Zeitachse und
 * ist deshalb weder vor noch nach dem Zeitraum — dafuer gibt es den eigenen
 * Schalter im Anlagedialog.
 *
 * Aufsteigend sortiert, denn die Auswahl im Dialog ist ein zusammenhaengender
 * Bereich "ab Monat X" — die Reihenfolge ist dort keine Kosmetik.
 */
export const openMonthsBeforePeriod = (
    months: OpenMonthBucket[],
    /** Beginn des Zeitraums als 'JJJJ-MM-TT' oder 'JJJJ-MM'; leer heisst: kein Filter. */
    periodFrom: string | null | undefined,
): PriorOpenMonth[] => {
    // Ohne Beginn filtert der Zeitraum nach vorn nichts weg — dann gibt es auch
    // keine uebergangenen Vormonate.
    if (!periodFrom) return [];
    const limit = periodFrom.slice(0, 7);
    return months
        .filter(
            (b): b is PriorOpenMonth => b.month != null && b.month < limit && b.netCents > 0,
        )
        .map((b) => ({ month: b.month, netCents: b.netCents }))
        .sort((a, b) => a.month.localeCompare(b.month));
};

/** Was ein Zeitraumfilter nicht sehen kann — fuer die Anzeige, nicht zum Rechnen. */
export interface UndatedSummary {
    /** Positionen mit offener, aber undatierter Menge. */
    count: number;
    /** Deren Nettowert, begrenzt auf die tatsaechlich noch offene Menge. */
    netCents: Cents;
}

/**
 * Offene Mengen ohne Datumsbezug, zusammengefasst.
 *
 * Damit die Oberflaeche sie benennen kann, statt sie stillschweigend
 * wegzulassen. Eine fehlende Rechnungszeile sieht aus wie eine, die es nicht
 * gibt — genau das macht diesen Fall gefaehrlich.
 */
export const undatedOpenSummary = (
    services: ServiceItem[],
    reservations: Map<string, number> = new Map(),
    serviceLog: ServiceTransaction[] = [],
): UndatedSummary => {
    const undated = undatedQuantities(services, serviceLog);
    let count = 0;
    let netCents = 0;
    for (const service of services) {
        const open = openQuantity(service, reservations.get(service.id) ?? 0);
        const qty = Math.min(open, undated.get(service.id) ?? 0);
        if (qty <= 0.0005) continue;
        count += 1;
        netCents += roundCents(qty * roundCents((service.unitPrice || 0) * 100));
    }
    return { count, netCents };
};

/**
 * Im Zeitraum protokollierte Menge je Leistung.
 *
 * Buchungseintraege (bookingReference "BU-…") zaehlen NICHT: sie sind
 * Abrechnungsvorgaenge, keine Leistung. Ohne diese Schranke laese eine Rechnung
 * die eigene Buchung als erbrachte Menge des Zeitraums und boete die Position
 * gleich ein zweites Mal an.
 *
 * Exportiert fuer den Schattenvergleich (allocationShadow, S5a Stufe 2): der
 * Zeitraumdeckel wird dort auf JSON- und Journal-Protokoll mit GENAU dieser
 * Funktion gerechnet. Eine nachgebaute Fassung maesse sich selbst.
 */
export const loggedQuantityInPeriod = (
    log: ServiceTransaction[],
    period: { from?: string; to?: string },
    /**
     * Belege des Projekts. Mit ihnen zaehlt nur der NOCH OFFENE Anteil eines
     * Eintrags — ohne sie die volle protokollierte Menge, wie bisher.
     */
    invoices?: Pick<Invoice, 'lines'>[],
): Map<string, number> => {
    const allocated = invoices?.length ? allocatedByEntry(invoices) : null;
    const out = new Map<string, number>();
    for (const entry of log) {
        if (!entry?.serviceId || isAccountingEntry(entry)) continue;
        // Auf den Kalendertag kuerzen: das Protokoll fuehrt Zeitstempel, der
        // Zeitraum reine Tage. Ohne das faellt der letzte Tag heraus. Der
        // BERLINER Tag (Betreiber-Entscheid 1 vom 02.10.2026): dieselbe Regel
        // wie die Zuordnung (allocationLogic.orderForPeriod) und wie der
        // Journal-Weg, der Berliner Wanduhrzeit liefert. Ein reiner Tag bleibt.
        const date = buchungstag(entry.date);
        if (period.from && (!date || date < period.from)) continue;
        if (period.to && (!date || date > period.to)) continue;
        // Als ZAHL gelesen (Codex-Review S5a Stufe 2, 02.10.2026): eine Menge,
        // die als Text im Protokoll steht („2"), wuerde unten nicht addiert,
        // sondern angehaengt — zweimal „2" ergaebe „022" statt 4.
        const quantity = Number(entry.quantity) || 0;
        // Nur Zugaenge werden zugeordnet; eine negative Korrektur bleibt in
        // voller Hoehe stehen und mindert die Menge des Zeitraums. Nachgeschlagen
        // wie allocatedByEntry die Map fuellt (`kennungFuerVergleich`): eine
        // grossgeschriebene uuid der Vorbelege trifft sonst die kleingeschriebene
        // des Journals nicht, und der Zeitraum boete Abgerechnetes erneut an.
        const open =
            allocated && quantity > 0
                ? Math.max(0, quantity - (allocated.get(kennungFuerVergleich(entry.id)) ?? 0))
                : quantity;
        out.set(entry.serviceId, (out.get(entry.serviceId) ?? 0) + open);
    }
    return out;
};

/**
 * Was BIS zum Stichtag erbracht ist, je Leistung — der Deckel der Stichtagsrechnung.
 *
 * `loggedQuantityInPeriod({ to })` allein taugt dafuer nicht, und zwar wegen
 * der ALT-Zeilen. Es gibt zwei Sorten, und sie sehen gleich aus (die
 * Unterscheidung steht ausfuehrlich in openByMonth, `traegtAltLeistung`):
 *   * Die nachgetragene Altposition: keine einzelnen Berichte, die ALT-Zeile
 *     IST die erbrachte Leistung und steckt in `quantityUsed` (Projekt 6193).
 *     Sie muss zaehlen — sonst faellt der Stand unter die Vorbelege.
 *   * Der blosse Abrechnungsvermerk: die Begehungen stehen einzeln im
 *     Protokoll, die ALT-Zeile sagt nur, welcher Altbeleg sie gefordert hat.
 *     Zaehlt sie MIT, steht die Leistung doppelt im Protokoll — und der
 *     Stichtag hat keine Wirkung mehr.
 *
 * Genau das war 6290-1 am 02.09.2026 (2.7.10 B75 Sunderweg): 13 Berichte, davon
 * 12 bis zum Stichtag 31.08., dazu die ALT-Zeile „Rechnung 6290/2 (7 Begehung)".
 * 12 + 7 = 19, gedeckelt auf quantityUsed 13 — der Beleg forderte den Bericht
 * vom 01.09. mit, der Leistungsnachweis zeigte ehrlich fuenf Tage, und beide
 * standen nebeneinander auf demselben Beleg.
 *
 * Die Regel: eine ALT-Zeile traegt nur so viel Leistung, wie das eigene
 * Protokoll NICHT erklaert (`quantityUsed` minus eigene Protokollmenge). Das ist
 * dieselbe Rechnung wie in openByMonth, damit Monatsuebersicht und Stichtag
 * dieselbe Antwort geben.
 */
const performedUpToCutoff = (
    services: ServiceItem[],
    log: ServiceTransaction[],
    to: string,
): Map<string, number> => {
    const eigene = log.filter((e) => !isBackfilledAltEntry(e));
    const alt = log.filter((e) => isBackfilledAltEntry(e));
    const eigeneBisStichtag = loggedQuantityInPeriod(eigene, { to });
    const eigeneGesamt = loggedQuantityInPeriod(eigene, {});
    const altBisStichtag = loggedQuantityInPeriod(alt, { to });

    const out = new Map<string, number>(eigeneBisStichtag);
    for (const service of services) {
        const altQty = altBisStichtag.get(service.id) ?? 0;
        if (altQty <= 0) continue;
        const traegt = Math.max(0, (service.quantityUsed || 0) - (eigeneGesamt.get(service.id) ?? 0));
        out.set(service.id, (out.get(service.id) ?? 0) + Math.min(altQty, traegt));
    }
    return out;
};

/**
 * Offene Positionen eines Projekts.
 *
 * Hier passiert die einzige Float-nach-Cent-Umrechnung des gesamten Moduls:
 * ServiceItem.unitPrice ist ein Euro-Float aus dem Bestands-LV. Ab hier wird
 * ausschliesslich in Cent gerechnet.
 *
 * Mit einem Leistungszeitraum wird die Menge zusaetzlich auf das gedeckelt, was
 * das Arbeitsprotokoll IN diesem Zeitraum ausweist. Das LV selbst kennt kein
 * Datum — quantityUsed ist eine Gesamtsumme —, deshalb ist das Protokoll die
 * einzige Grundlage einer Zeitraumabgrenzung.
 *
 * Folge, die man kennen muss: eine Leistung ohne protokollierten Eintrag im
 * Zeitraum faellt heraus. Bei Altbestaenden, in denen quantityUsed ohne
 * Logeintrag hochgesetzt wurde, ist das auffaellig wenig — aber es ist die
 * ehrliche Antwort. Der Projektcheck meldet genau diesen Fall als
 * "unlogged_quantity"; stillschweigend die Gesamtmenge in einen einzelnen
 * Monat zu schreiben waere die schlechtere Alternative.
 */
export const computeOpenPositions = (
    services: ServiceItem[],
    reservations: Map<string, number> = new Map(),
    period?: OpenPositionPeriod,
): OpenPosition[] => {
    const limitPeriod = period && (period.from || period.to) ? period : undefined;
    // Zaehlt NUR, was von den Eintraegen des Zeitraums noch offen ist. Die
    // bereits abgerechneten Anteile stehen in den Belegen; ohne sie bot der
    // Filter einen vollstaendig abgerechneten Mai erneut an, solange irgendwo
    // im Projekt noch etwas offen war.
    //
    // Ohne die VERZICHTETEN Eintraege (waivedEntryIds): sie sind erbracht,
    // werden aber nie gefordert. Zaehlten sie mit, deckte der Zeitraum mehr ab,
    // als er abrechnen darf, und der Rest kaeme ueber `overallOpen` aus einem
    // anderen Monat (Vorfall 6172, siehe performedByCutoff unten).
    const inPeriod = limitPeriod
        ? loggedQuantityInPeriod(
              ohneVerzichteteEintraege(limitPeriod.serviceLog ?? [], services),
              limitPeriod,
              limitPeriod.invoices,
          )
        : null;
    const undated =
        limitPeriod?.includeUndated
            ? undatedQuantities(services, limitPeriod.serviceLog ?? [])
            : null;

    // Dritter Deckel, allein aus dem STICHTAG: was bis dahin ueberhaupt
    // geleistet wurde, abzueglich des bereits Abgerechneten.
    //
    // Ohne ihn setzten sich die beiden anderen Grenzen gegenseitig ausser
    // Kraft, denn Math.min nimmt die LOCKERERE von beiden: `overallOpen` kennt
    // den Zeitraum nicht (quantityUsed ist eine Gesamtsumme ohne Datum), und
    // `allowed` kennt das Abgerechnete nur, soweit es als Zuordnung an einem
    // Beleg haengt.
    //
    // Sichtbar wird das erst bei WEITEM Fenster. Projekt 6016: 44 Stunden
    // protokolliert (40 bis zum 31.07., 4 im August), 18 von Hand als
    // abgerechnet gesetzt. Ein enges Fenster (nur Juli) haelt ueber `allowed`
    // dicht und sieht richtig aus; zieht man den Beginn bis in den November des
    // Vorjahres zurueck, uebersteigt `allowed` die 26 aus quantityUsed minus
    // quantityBilled — und der Entwurf forderte 26 statt 22 und nahm die
    // Auguststunden mit.
    //
    // Bewusst mit der BRUTTOSUMME des Protokolls (ohne Zuordnungsabzug) und dem
    // vollen quantityBilled: beide beziehen sich auf denselben Abschnitt "von
    // Anfang bis Stichtag", so wird kein Anteil zweimal abgezogen.
    //
    // Ausdruecklich NUR die Bis-Seite. Die Von-Seite grenzt weiterhin allein
    // ueber `allowed` ab; sie in diesen Deckel zu ziehen zoege abgerechnete
    // Mengen frueherer Monate von einem spaeteren Fenster ab und liesse dessen
    // Leistungen verschwinden.
    //
    // Ueber `performedUpToCutoff`, nicht ueber die rohe Protokollsumme: eine
    // ALT-Zeile, deren Begehungen einzeln im Protokoll stehen, zaehlte sonst
    // doppelt und hob den Deckel auf (6290-1, siehe dort).
    //
    // Der VERZICHT bis zum Stichtag geht ab, genau wie das Abgerechnete: er
    // ist bis dahin erbracht und wird nie gefordert. Ohne den Abzug lag der
    // Deckel um die verzichtete Menge zu hoch, `overallOpen` (das den Verzicht
    // kennt) gab den Ausschlag, und der Beleg zog eine Begehung NACH dem
    // Stichtag mit — 6172, 03.11.2026: Verzicht auf zwei Oktober-Doppeltage,
    // der Oktoberbeleg forderte die Begehung vom 02.11. Dieselbe Regel wie in
    // computeCumulativePositions: zugeordnete Eintraege nach dem Stichtag
    // bleiben aussen vor, ein Verzicht ohne Zuordnung zaehlt ganz.
    const untilCutoff = limitPeriod?.to
        ? performedUpToCutoff(services, limitPeriod.serviceLog ?? [], limitPeriod.to)
        : null;

    return services
        .map((service) => {
            const overallOpen = openQuantity(service, reservations.get(service.id) ?? 0);
            const allowed = inPeriod
                ? (inPeriod.get(service.id) ?? 0) + (undated?.get(service.id) ?? 0)
                : Infinity;
            const performedByCutoff = untilCutoff
                ? Math.max(
                      0,
                      Math.min(
                          service.quantityUsed || 0,
                          (untilCutoff.get(service.id) ?? 0) + (undated?.get(service.id) ?? 0),
                      ) - (service.quantityBilled || 0)
                        - Math.max(
                              0,
                              waivedQuantity(service)
                                  - verzichtNachStichtag(service, limitPeriod?.serviceLog ?? [], limitPeriod?.to ?? ''),
                          ),
                  )
                : Infinity;
            const openQty = Math.min(overallOpen, allowed, performedByCutoff);
            const unitPriceCents = roundCents((service.unitPrice || 0) * 100);
            return {
                service,
                openQty,
                unitPriceCents,
                netCents: roundCents(openQty * unitPriceCents),
                ...(inPeriod && openQty < overallOpen
                    ? { periodLimitedFromQty: overallOpen }
                    : {}),
            };
        })
        // Toleranz statt "> 0". quantityUsed und quantityBilled sind Floats aus
        // dem Bestands-LV: 0.1 + 0.2 - 0.3 ergibt 5.55e-17, und diese Position
        // galt dann als offen. Der Rechnungslauf schloss das Projekt nicht als
        // "nichts offen" aus, sondern legte einen Entwurf mit einer
        // 0,00-EUR-Zeile an und schrieb den Float-Rest ins LV zurueck.
        // Ein Tausendstel ist die Aufloesung, die ServiceItem.quantity ueberhaupt
        // hat (numeric(14,3)) — darunter gibt es keine echte Menge.
        .filter((p) => p.openQty > 0.0005);
};

/**
 * Verzichtete Menge einer Position, deren zugeordnete Protokolleintraege
 * (`waivedEntryIds`) NACH dem Stichtag liegen. Ein Verzicht ohne Zuordnung hat
 * kein Datum und zaehlt hier nie.
 */
const verzichtNachStichtag = (
    service: ServiceItem,
    log: ServiceTransaction[],
    to: string,
): number => {
    const ids = new Set((service.waivedEntryIds || []).map((id) => kennungFuerVergleich(id)));
    if (!ids.size) return 0;
    let menge = 0;
    for (const entry of log) {
        if (entry?.serviceId !== service.id || !ids.has(kennungFuerVergleich(entry.id))) continue;
        const tag = buchungstag(entry.date);
        if (tag && tag > to) menge += Number(entry.quantity) || 0;
    }
    return menge;
};

/**
 * Positionen einer KUMULATIVEN Rechnung: der gesamte Leistungsstand.
 *
 * Anders als computeOpenPositions wird hier NICHT abgezogen, was schon
 * abgerechnet ist. Genau das ist der Zweck: eine kumulative Rechnung zeigt
 * alles bisher Geleistete und zieht am Ende die vorherigen Rechnungen ab.
 * Wuerde man die abgerechnete Menge hier bereits abziehen, stuende der Abzug
 * doppelt im Beleg und der Kunde bekaeme eine zu niedrige Rechnung.
 *
 * Aus demselben Grund spielen Reservierungen keine Rolle: ein anderer Entwurf
 * beansprucht keine Menge, er zeigt nur denselben Stand.
 *
 * Mit einem Stichtag zaehlt, was BIS dahin protokolliert ist — nicht, was in
 * einem Zeitraum liegt. "Alles bisher Geleistete" ist eine Summe von Anfang an.
 *
 * Der Stand des Leistungsverzeichnisses (quantityUsed) ist dabei die Obergrenze.
 * Er ist die korrigierte Wahrheit ueber die erbrachte Menge; das Protokoll fuehrt
 * die Bewegungen, aus denen sie entstanden ist. Beide koennen zum Stichtag
 * auseinanderlaufen, weil eine nachtraegliche Korrektur (Schloss-Symbol in
 * "Nachtrag & Daten-Korrektur") als datierte Gegenbuchung im Protokoll steht:
 * zwei Buchungen im Juni/Juli, die Korrektur auf 1 im August — ein Beleg zum
 * Stichtag 31.07. sah nur die beiden Buchungen und forderte 2, obwohl das LV
 * laengst 1 sagt. Kumulativ kann der Gesamtstand nie ueber dem Gesamtstand des
 * LV liegen; computeOpenPositions deckelt aus demselben Grund auf den offenen
 * Bestand. Die Grenze kann nur greifen, wenn spaeter nach unten korrigiert
 * wurde — sie hebt keine Menge an und schneidet keine noch nicht protokollierte
 * Leistung weg.
 */
export const computeCumulativePositions = (
    services: ServiceItem[],
    period?: {
        /** yyyy-mm-dd. Stichtag; leer heisst "der Stand des Leistungsverzeichnisses". */
        to?: string;
        serviceLog?: ServiceTransaction[];
        /** Mengen ohne Protokolleintrag mitzaehlen. */
        includeUndated?: boolean;
    },
): OpenPosition[] => {
    const asOf = period?.to;
    const log = period?.serviceLog ?? [];
    // Ohne Stichtag ist der Stand des Leistungsverzeichnisses massgeblich —
    // er ist die Summe aller Bewegungen und braucht keine Rekonstruktion.
    // Ueber `performedUpToCutoff`: ALT-Zeilen zaehlen nur, soweit sie
    // Leistung TRAGEN, die das eigene Protokoll nicht erklaert (6290-1).
    const upTo = asOf ? performedUpToCutoff(services, log, asOf) : null;
    const undated = upTo && period?.includeUndated ? undatedQuantities(services, log) : null;

    return services
        .map((service) => {
            // MONATSPAUSCHALE: eine gedeckte Position (flatCoveredBy, siehe
            // projectLv.stampFlatCoverage) steht mit ihrem GELDSTAND still —
            // die Menge ist quantityBilled, der Zuwachs damit 0.
            //
            // Sie WEGZULASSEN waere der naheliegende, aber falsche Weg: der
            // kumulative Gesamtstand schrumpfte dann unter die bereits
            // ausgestellten Vorbelege, und der naechste Beleg wuerde negativ.
            // Der Stichtag spielt fuer sie keine Rolle — quantityBilled ist
            // kein Protokollstand, sondern die Summe der VOR der Umstellung
            // ausgestellten Belege, und die aendert sich rueckwirkend nicht.
            // `quantityUsed` laeuft daneben als Nachweis weiter und darf hier
            // nie wieder in die Menge einfliessen.
            //
            // PAKETPOSITION mit offenen Bestandteilen (oder unbekanntem Stand,
            // fail closed): dieselbe Regel wie die Deckung — die Position
            // steht mit ihrem bereits abgerechneten Stand still, der Zuwachs
            // ist 0. Auch hier NICHT weglassen: eine schon abgerechnete
            // Paketposition (etwa abgerechnet, danach ein Bestandteil von
            // einem Admin ergaenzt, oder der Stand gerade nicht ladbar) fiele
            // sonst aus dem Gesamtstand, und der Abzug der Vorrechnungen
            // machte den Beleg negativ.
            if (service.flatCoveredBy || istAbrechnungGesperrt(service)) {
                const frozen = service.quantityBilled || 0;
                const frozenUnitCents = roundCents((service.unitPrice || 0) * 100);
                return {
                    service,
                    openQty: frozen,
                    unitPriceCents: frozenUnitCents,
                    netCents: roundCents(frozen * frozenUnitCents),
                };
            }
            const total = service.quantityUsed || 0;
            const bisStichtagRoh = upTo
                ? Math.min(total, (upTo.get(service.id) ?? 0) + (undated?.get(service.id) ?? 0))
                : total;
            // VERZICHT: erbracht, aber nicht mehr gefordert — er gehoert nicht in
            // den Gesamtstand. Bis zum 21.10.2026 fehlte er hier ganz (nur
            // openQuantity kannte ihn, die kumulative Rechnung geht daran
            // vorbei): Projekt 5972 setzte einen Verzicht auf eine Begehung,
            // und der Beleg forderte sie trotzdem.
            //
            // Zum Stichtag zaehlt der Verzicht, soweit er nicht nachweislich
            // DANACH liegt: zugeordnete Eintraege (waivedEntryIds) mit Tag nach
            // dem Stichtag bleiben aussen vor, ein Verzicht „nur Menge" ohne
            // Datum zaehlt ganz — lieber einen Beleg spaeter fordern als eine
            // verzichtete Leistung einmal zu viel. Nie unter das bereits
            // Abgerechnete: sonst zoege der Abzug der Vorrechnungen den Beleg
            // ins Minus (ein Verzicht darf abgerechnete Menge ohnehin nicht
            // treffen, siehe serviceWaiverLogic).
            const verzichtet = waivedQuantity(service);
            const bisStichtag =
                verzichtet > 0.0005
                    ? Math.max(
                          Math.min(bisStichtagRoh, service.quantityBilled || 0),
                          Math.min(
                              total - verzichtet,
                              bisStichtagRoh -
                                  Math.max(0, verzichtet - (asOf ? verzichtNachStichtag(service, log, asOf) : 0)),
                          ),
                      )
                    : bisStichtagRoh;
            // PAKETPOSITION, Gate frei: der Deckel (Kernregel 3). Kumulativ
            // ist der Zuwachs `Menge − quantityBilled`; er darf hoechstens
            // `paketDeckel` sein, also die Menge hoechstens
            // quantityBilled + Deckel (= die Vertragsmenge, solange nicht mehr
            // abgerechnet ist). Eine doppelte Positionsbuchung (used 2 bei
            // Menge 1) fordert so nie die zweite Einheit. Ohne Paket ist der
            // Deckel Infinity und die Menge unveraendert.
            const deckel = paketDeckel(service);
            const qty = Number.isFinite(deckel)
                ? Math.min(bisStichtag, (service.quantityBilled || 0) + deckel)
                : bisStichtag;
            const unitPriceCents = roundCents((service.unitPrice || 0) * 100);
            return {
                service,
                openQty: qty,
                unitPriceCents,
                netCents: roundCents(qty * unitPriceCents),
            };
        })
        .filter((p) => p.openQty > 0.0005);
};

/**
 * Leistungsbeginn eines KUMULATIVEN Belegs — Gegenstueck zum Stichtag.
 *
 * Die Positionen eines kumulativen Belegs nennen den gesamten Leistungsstand
 * bis zum Stichtag, also auch die Begehung vom Januar. Der ausgewiesene
 * Leistungszeitraum muss deshalb bis zur ersten abgerechneten Leistung
 * zurueckreichen: er ist Pflichtangabe (§14 Abs. 4 Nr. 6 UStG) und muss ALLES
 * abdecken, was der Beleg abrechnet. Von Hand gesetzt behauptete er einen
 * Monat und der Beleg rechnete das halbe Jahr ab — ein Fehler, der richtig
 * aussieht.
 *
 * Massgeblich ist der fruehste abrechenbare Protokolleintrag bis zum Stichtag.
 * Buchungseintraege bleiben draussen (sie sind Abrechnungsvorgaenge, keine
 * Leistung), undatierte ebenso — was zu keinem Tag gehoert, kann keinen Beginn
 * begruenden.
 *
 * Ohne datierten Eintrag: undefined. Dann bleibt deliveryDateFrom leer, und der
 * Beleg weist allein den Stichtag als Leistungsdatum aus. Einen Beginn zu
 * erfinden waere die schlechtere Antwort.
 */
export const cumulativeDeliveryStart = (
    serviceLog: ServiceTransaction[] = [],
    /** yyyy-mm-dd. Stichtag; leer heisst "das ganze Protokoll". */
    asOf?: string,
): string | undefined => {
    let first: string | undefined;
    for (const entry of allocatableEntries(serviceLog)) {
        // Auf den Kalendertag kuerzen: das Protokoll fuehrt Zeitstempel, der
        // Stichtag ist ein reiner Tag. Ohne das faellt eine Leistung am
        // Stichtag selbst heraus — dieselbe Falle wie im Zeitraumfilter. Der
        // BERLINER Tag, wie dort (loggedQuantityInPeriod).
        const day = buchungstag(entry.date);
        if (!day) continue;
        if (asOf && day > asOf) continue;
        if (!first || day < first) first = day;
    }
    return first;
};

/**
 * Zwei Positionsnummern in ihrer natuerlichen Ordnung vergleichen.
 *
 * Zifferngruppen werden als Zahl verglichen, alles andere als Text: sonst
 * stuende "1.10" vor "1.9", und eine gegliederte Nummerierung waere nicht
 * sortierbar. Die kuerzere Nummer kommt zuerst ("2" vor "2.1").
 */
const POS_NUMBER_CHUNKS = /\d+|\D+/g;

export const comparePosNumbers = (a: string, b: string): number => {
    const ca = a.match(POS_NUMBER_CHUNKS) ?? [];
    const cb = b.match(POS_NUMBER_CHUNKS) ?? [];
    for (let i = 0; i < Math.max(ca.length, cb.length); i += 1) {
        const xa = ca[i];
        const xb = cb[i];
        if (xa === undefined) return -1;
        if (xb === undefined) return 1;
        const bothNumeric = /^\d/.test(xa) && /^\d/.test(xb);
        if (bothNumeric) {
            const diff = Number(xa) - Number(xb);
            if (diff !== 0) return diff;
            continue;
        }
        if (xa !== xb) return xa < xb ? -1 : 1;
    }
    return 0;
};

/**
 * Leistungszeilen nach ihrer Positionsnummer ordnen.
 *
 * Sortiert wird NUR innerhalb zusammenhaengender Bloecke von Leistungszeilen.
 * Abschnitte, Texte, Rabatte und Abschlagsabzuege sind Grenzen und bleiben, wo
 * sie stehen: eine Zwischenueberschrift traegt die Zeilen unter ihr, und ein
 * Erlaeuterungstext gehoert zu der Position, hinter die ihn jemand gesetzt hat.
 * Ueber so eine Grenze hinweg zu schieben aenderte die Aussage des Belegs.
 *
 * Bei gleicher Nummer steht die von Hand gesetzte Zeile vorn — sie ist die
 * ausdrueckliche Ansage, die fortlaufende Nummer nur der Ersatzwert.
 */
const sortLinesByPosNumber = (lines: InvoiceLine[]): InvoiceLine[] => {
    const out = [...lines];
    let start = 0;
    while (start < out.length) {
        if (out[start].kind !== 'SERVICE') {
            start += 1;
            continue;
        }
        let end = start;
        while (end < out.length && out[end].kind === 'SERVICE') end += 1;
        const block = out.slice(start, end).map((line, index) => ({ line, index }));
        block.sort((a, b) => {
            const cmp = comparePosNumbers(a.line.posNumber ?? '', b.line.posNumber ?? '');
            if (cmp !== 0) return cmp;
            const manualA = a.line.posNumberManual ? 0 : 1;
            const manualB = b.line.posNumberManual ? 0 : 1;
            if (manualA !== manualB) return manualA - manualB;
            // Gleichstand behaelt die bisherige Reihenfolge.
            return a.index - b.index;
        });
        for (let i = 0; i < block.length; i += 1) out[start + i] = block[i].line;
        start = end;
    }
    return out;
};

/**
 * Positionsnummern der Rechnung: fortlaufend, Abschnitte zaehlen nicht mit.
 *
 * Bewusst simpel und nicht aus der LV-Nummer abgeleitet: die Rechnung ist ein
 * eigenes Dokument, und bei einer Sammelrechnung ueber mehrere Projekte kaeme
 * dieselbe LV-Nummer sonst mehrfach vor.
 */
const numberInvoicePositions = (lines: InvoiceLine[]): InvoiceLine[] => {
    let n = 0;
    return lines.map((line) => {
        if (line.kind !== 'SERVICE') return { ...line, posNumber: undefined };
        // Der Zaehler laeuft auch ueber eine handgesetzte Nummer hinweg: sonst
        // verschoeben sich alle folgenden Nummern, sobald eine Zeile ihre
        // eigene traegt.
        n += 1;
        if (line.posNumberManual) return line;
        return { ...line, posNumber: String(n) };
    });
};

const samePosOrder = (a: InvoiceLine[], b: InvoiceLine[]): boolean =>
    a.length === b.length &&
    a.every((line, i) => line.id === b[i].id && line.posNumber === b[i].posNumber);

/**
 * Nummerieren UND sortieren: der Beleg steht danach immer in aufsteigender
 * Positionsnummer — gleich, ob die Nummer fortlaufend vergeben oder von Hand
 * gesetzt wurde.
 *
 * Beides haengt voneinander ab: die fortlaufende Nummer folgt der Reihenfolge,
 * die Reihenfolge folgt der Nummer. Deshalb wird abwechselnd nummeriert und
 * sortiert, bis sich nichts mehr bewegt. Ein Beleg ohne handgesetzte Nummer
 * ist schon nach dem ersten Durchlauf fertig — dort aendert das Sortieren
 * nichts. Die Obergrenze ist nur eine Bremse gegen ein Hin und Her bei
 * widerspruechlichen Nummern; sie liefert dann den zuletzt erreichten Stand.
 */
const POS_SORT_MAX_PASSES = 8;

export const nextInvoicePosNumbers = (lines: InvoiceLine[]): InvoiceLine[] => {
    let out = numberInvoicePositions(lines);
    for (let pass = 0; pass < POS_SORT_MAX_PASSES; pass += 1) {
        const next = numberInvoicePositions(sortLinesByPosNumber(out));
        if (samePosOrder(next, out)) break;
        out = next;
    }
    return out;
};

/**
 * Ueberschriftszeile eines Abschnitts.
 *
 * Die id ist aus der Abschnitts-id abgeleitet und damit stabil: eine bei jedem
 * Aufruf neu gewuerfelte id liesse React die Zeile jedes Mal neu aufbauen und
 * machte jeden Vergleich zweier Belegstaende wertlos.
 */
const sectionHeadingLine = (section: InvoiceSection): InvoiceLine => ({
    id: `sec_${section.id}`,
    kind: 'SECTION',
    sectionId: section.id,
    title: section.title,
    // Ausdruecklich ohne Betraege: die Ueberschrift traegt nichts zum
    // Nettobetrag bei (computeLineNet), und eine Nummer bekaeme sie ohnehin
    // nicht (numberInvoicePositions zaehlt nur SERVICE).
    netCents: 0,
    taxRateBp: 0,
});

/**
 * Die Gliederung des Belegs herstellen: Zeilen nach Abschnitten ordnen und die
 * Ueberschriftszeilen daraus ableiten.
 *
 * EINE Wahrheit ueber die Gliederung — die Zuordnung `line.sectionId` und die
 * Reihenfolge in `sections`. Die Ueberschriftszeilen sind Ableitung und werden
 * bei jedem Aufruf verworfen und neu gebildet; sonst haette der Beleg zwei
 * Quellen (Zuordnung UND Platz im Array), die spaetestens beim naechsten
 * Positionsaufbau auseinanderlaufen.
 *
 * Die Reihenfolge:
 *  1. Zeilen ohne Zuordnung, in ihrer bisherigen Reihenfolge. Dazu gehoeren
 *     ausdruecklich FREMDE Ueberschriften — die Sammelrechnung setzt je Projekt
 *     eine (billingRunLogic) — sie bleiben unangetastet stehen.
 *  2. je Abschnitt seine Ueberschrift und seine Positionen.
 *  3. belegweit erzeugte Zeilen (Nachlass, Nebenkostenzuschlag) zuletzt: sie
 *     haengen an der GESAMTEN Honorarsumme und gehoeren zu keinem Abschnitt.
 *     Innerhalb eines Abschnitts gedruckt, zoege der Andruck sie in dessen
 *     Zwischensumme (generatorInvoice).
 *
 * Ein Abschnitt ohne Positionen erzeugt KEINE Ueberschrift: eine Ueberschrift
 * ueber nichts liest sich auf einer Rechnung wie ein Fehler. In der Maske bleibt
 * er trotzdem stehen — sonst liesse sich nichts hineinlegen.
 *
 * Eine Zuordnung auf einen geloeschten Abschnitt wird geraeumt statt
 * mitgeschleppt: sonst taucht die Position wieder auf, sobald jemand einen
 * Abschnitt mit derselben id anlegt.
 *
 * Rein. Ohne Abschnitte bleibt nur das Raeumen uebrig — der Beleg sieht dann
 * aus wie vorher.
 */
export const applyInvoiceSections = (
    lines: InvoiceLine[],
    sections: InvoiceSection[],
): InvoiceLine[] => {
    const known = new Set(sections.map((s) => s.id));
    const rest = lines
        // Abgeleitete Ueberschriften weg — sie werden gleich neu gebildet.
        // Erkannt an der Zuordnung: fremde SECTION-Zeilen tragen keine.
        .filter((l) => !(l.kind === 'SECTION' && l.sectionId))
        .map((l) => (l.sectionId && !known.has(l.sectionId) ? { ...l, sectionId: undefined } : l));

    if (sections.length === 0) return rest;

    const trailing = rest.filter((l) => l.generated);
    const body = rest.filter((l) => !l.generated);

    const out: InvoiceLine[] = body.filter((l) => !l.sectionId);
    for (const section of sections) {
        const own = body.filter((l) => l.sectionId === section.id);
        if (own.length === 0) continue;
        out.push(sectionHeadingLine(section), ...own);
    }
    return [...out, ...trailing];
};

/**
 * Von Hand gesetzte Positionsnummern und Texte ueber einen Neuaufbau retten.
 *
 * Der Aufbau erzeugt die Zeilen frisch aus dem Projekt-LV und traegt damit
 * wieder dessen Nummer und Bezeichnung. Eine im Beleg vorgenommene Aenderung
 * waere ohne diesen Schritt weg — und zwar lautlos, denn der Aufbau laeuft
 * auch von selbst, etwa nach einer Zeitraumaenderung.
 *
 * Zugeordnet wird ueber serviceId: die Zeilen-id ist beim Neuaufbau eine
 * andere. Zeilen ohne Entsprechung im neuen Aufbau fallen weg, wie bisher.
 */
export const keepManualLineEdits = (
    built: InvoiceLine[],
    previous: InvoiceLine[],
): InvoiceLine[] => {
    const manual = new Map<string, InvoiceLine>();
    for (const line of previous) {
        if (line.kind !== 'SERVICE' || !line.serviceId) continue;
        // Die Abschnittszuordnung gehoert in dieselbe Rettung: sie ist eine
        // Angabe DIESES Belegs, im Projekt-LV steht nichts davon, und der
        // Aufbau laeuft auch von selbst. Ohne sie faenden sich nach einer
        // Zeitraumaenderung alle Positionen wieder "ohne Zuordnung".
        if (line.titleManual || line.posNumberManual || line.sectionId) {
            manual.set(line.serviceId, line);
        }
    }
    if (manual.size === 0) return built;

    return built.map((line) => {
        const alt = line.serviceId ? manual.get(line.serviceId) : undefined;
        if (!alt) return line;
        return {
            ...line,
            ...(alt.titleManual ? { title: alt.title, titleManual: true } : {}),
            ...(alt.posNumberManual
                ? { posNumber: alt.posNumber, posNumberManual: true }
                : {}),
            ...(alt.sectionId ? { sectionId: alt.sectionId } : {}),
        };
    });
};

/**
 * Darf diese Position aus dem Beleg herausgenommen werden?
 *
 * Herausnehmen heisst: die Leistung bleibt offen und wird mit einem spaeteren
 * Beleg gefordert — etwa, wenn im Mai nur die Begehungen abgerechnet werden und
 * der SiGe-Plan desselben Zeitraums erst danach.
 *
 * Fortschreibend ist das immer erlaubt: jede Zeile fordert genau ihre eigene
 * neue Menge, und was fehlt, fehlt folgenlos.
 *
 * KUMULATIV nur, solange die Leistung noch nie gefordert wurde. Der Abzug der
 * Vorbelege wird je VORBELEG als dessen Gesamtforderung gerechnet und nicht je
 * Leistung. Fehlte eine bereits geforderte Position im neuen Gesamtstand, zoege
 * der Beleg deshalb mehr ab, als er ausweist — er forderte zu wenig, und dem
 * Summenblock saehe man das nicht an.
 *
 * Massgeblich ist previouslyBilledQty an der Zeile: den Wert setzt der
 * kumulative Positionsaufbau aus quantityBilled der Leistung, und er steht
 * damit an jeder kumulativ gebildeten Zeile. Fehlt er, gab es keine Forderung.
 *
 * ZWEITE Quelle einer Forderung: nachgetragene Alt-Leistungen. Sie tragen
 * quantityBilled 0 — abgerechnet wurden sie ueber die Alt-Rechnung, deren
 * Abschlag der Beleg abzieht. Nach previouslyBilledQty allein saehen sie aus
 * wie "noch nie gefordert", und ihr Ausschluss naehme genau die
 * Bemessungsgrundlage weg, gegen die der Abzug geprueft wird
 * (advanceInvoiceLogic: advance_exceeds_rate_basis).
 */
export const canExcludeLine = (
    line: Pick<InvoiceLine, 'kind' | 'serviceId' | 'previouslyBilledQty'>,
    cumulative: boolean,
    /**
     * Leistungen mit ALT-Buchung (externalAdvances.altBookedServiceIds).
     * Fehlt die Angabe, bleibt es beim bisherigen Verhalten.
     */
    altBookedServiceIds?: ReadonlySet<string>,
): boolean => {
    // Nur LV-Positionen: von Hand ergaenzte Text-, Rabatt- und Zuschlagszeilen
    // haben keine Entsprechung im Projekt und werden schlicht geloescht.
    if (line.kind !== 'SERVICE' || !line.serviceId) return false;
    if (!cumulative) return true;
    if (altBookedServiceIds?.has(line.serviceId)) return false;
    return !((line.previouslyBilledQty ?? 0) > 0);
};

/**
 * Der Ausschluss, wie er beim ZEILENBAU tatsaechlich gilt.
 *
 * canExcludeLine sperrt die Schaltflaeche, aber die Sperre ist nur eine
 * Oberflaeche: details.excludedServiceIds steht am Beleg und ueberdauert. Ein
 * Entwurf, der vor dem Nachtrag angelegt wurde — oder einer, dessen Liste
 * gesetzt wurde, bevor die Alt-Buchungen geladen waren — traegt den Ausschluss
 * weiter, und jeder Neuaufbau liesse die Position wieder heraus. Deshalb faellt
 * die Entscheidung ein zweites Mal dort, wo sie bindend wird: beim Bau der
 * kumulativen Zeilen, gegen den frisch geladenen Projektstand.
 *
 * Fortschreibend bleibt jeder Ausschluss erlaubt — dort fordert jede Zeile nur
 * ihre eigene neue Menge.
 */
export const effectiveExcludedServiceIds = (
    excludedServiceIds: string[] | undefined,
    cumulative: boolean,
    altBookedServiceIds: ReadonlySet<string>,
): string[] => {
    const ids = excludedServiceIds ?? [];
    if (!cumulative || altBookedServiceIds.size === 0) return ids;
    return ids.filter((id) => !altBookedServiceIds.has(id));
};

// ═══════════════════════════════════════════════════════════════════════════
// Projektbezug
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Nachschlagen eines Projekts, das der Beleg selbst nicht kennt.
 *
 * Nur fuer Belege aus der Zeit vor details.projects gedacht: die Belegliste hat
 * die Projekte ohnehin geladen und kann die Luecke fuellen, ohne dass dafuer
 * etwas nachgeladen werden muesste.
 */
export type ProjectRefLookup = (projectId: string) => InvoiceProjectRef | undefined;

/** 'Nummer · Name'. Fehlt eines von beiden, bleibt das andere fuer sich stehen. */
export const formatProjectRef = (ref: InvoiceProjectRef): string =>
    [ref.number?.trim(), ref.name?.trim()].filter(Boolean).join(' · ');

/**
 * Derselbe Projektbezug OHNE unsere interne Projektnummer — nur der Name.
 *
 * Ausschliesslich fuer den Andruck: die Nummer ist ein Schluessel unseres
 * Betriebs, dem Auftraggeber sagt sie nichts, und auf seinem Beleg sucht er
 * die Baustelle am Namen. Innen bleibt sie ueberall stehen (Belegliste,
 * Maske, DATEV-Buchungstext) — dort ist sie der Schluessel, mit dem
 * gearbeitet wird.
 *
 * Ohne Namen faellt die Angabe auf die Nummer zurueck statt eine leere
 * Projektzeile zu drucken: ein Beleg, der ein Projekt nennt, muss es
 * benennen koennen, und eine Nummer ist immer noch eine Auskunft.
 */
export const formatProjectRefNameOnly = (ref: InvoiceProjectRef): string =>
    ref.name?.trim() || ref.number?.trim() || '';

/**
 * Die Typziffer aus einer Projektnummer (JJMM + Typziffer + laufende Nummer,
 * z.B. 26071002 -> '1'). 1 = SiGeKo, 2 = SiFa, 3 = Sonstige.
 *
 * Die Belegnummer soll denselben Typkreis tragen wie das abgerechnete Projekt.
 * Passt die Nummer nicht ins Muster — Altprojekte, handvergebene Nummern,
 * Suffixe, gar keine Nummer —, wird NICHT geraten: Rueckfall auf '1'. Eine
 * falsch geratene Ziffer stuende sonst dauerhaft auf einem ausgestellten
 * Beleg, waehrend '1' lediglich der Normalfall ist.
 */
export const typeDigitFromProjectNumber = (projectNumber?: string): '1' | '2' | '3' => {
    const raw = (projectNumber ?? '').trim();
    if (!/^\d{4}\d\d{3,}$/.test(raw)) return '1';
    const digit = raw.slice(4, 5);
    return digit === '2' || digit === '3' ? digit : '1';
};

/**
 * Ein Projektbezug aus mehreren Quellen zusammengesetzt.
 *
 * Feldweise auffuellen statt die erste Quelle zu nehmen: eine Abschlagszeile
 * kennt die Nummer nicht, das Projekt dahinter schon. Die Reihenfolge der
 * Kandidaten ist die Rangfolge — die erste Quelle, die ein Feld belegt, gewinnt
 * es.
 */
const mergeProjectRef = (
    id: string,
    candidates: Array<InvoiceProjectRef | undefined>,
): InvoiceProjectRef => {
    const merged: InvoiceProjectRef = { id };
    for (const c of candidates) {
        if (!c) continue;
        if (!merged.number?.trim() && c.number?.trim()) merged.number = c.number;
        if (!merged.name?.trim() && c.name?.trim()) merged.name = c.name;
    }
    return merged;
};

/**
 * Die Projekte eines Belegs mit Nummer und Name.
 *
 * Rangfolge der Quellen ist tragend, damit Liste, Maske und PDF dasselbe
 * zeigen:
 *
 * 1. details.projects — beim Anlegen festgehalten und beim Ausstellen
 *    eingefroren. Was hier steht, gilt; nur so stimmt eine Bildschirmanzeige
 *    mit dem archivierten Dokument ueberein.
 * 2. Die Zeilen selbst (projectNumber/projectName). Sie tragen den Bezug seit
 *    jeher mit und retten Altbelege ueber LV-Positionen.
 * 3. Das Nachschlagen im Aufrufer — die letzte Zuflucht, etwa bei einer alten
 *    Abschlagsrechnung, deren Pauschalzeile keinen Projektbezug traegt.
 *
 * Welche Projekte ueberhaupt in Frage kommen, sagt der Beleg: eine
 * Einzelrechnung ueber primaryProjectId, eine Sammelrechnung ueber die
 * Projekte ihrer Zeilen. Die Reihenfolge der Zeilen bleibt erhalten. Sagt
 * beides nichts, bleiben allein die Vermerkseintraege OHNE id uebrig — die
 * Eintraege MIT id nennen ein Projekt, das der Beleg selbst nicht als
 * abgerechnet ausweist, und werden nur auf ausdrueckliche Anforderung
 * herangezogen (invoiceListProjectRefs).
 */
export const invoiceProjectRefs = (
    invoice: InvoiceProjectSource,
    lookup?: ProjectRefLookup,
): InvoiceProjectRef[] => projectRefsOf(invoice, lookup, false);

/**
 * Der Projektbezug fuer die PROJEKTSPALTE DER BELEGLISTE — und nur dort.
 *
 * Die Liste laedt ihre Belege ohne Positionen (fetchInvoices withoutLines), und
 * eine Sammelrechnung hat kein primaryProjectId. Fuer sie bliebe die Spalte
 * deshalb leer, obwohl der Beleg seine Projekte selbst kennt: details.projects
 * wird beim Anlegen aus den Zeilen gebildet und beim Ausstellen eingefroren.
 * Diese Fassung laesst in genau diesem Fall den Vermerk VOLLSTAENDIG gelten,
 * also auch seine Eintraege mit id.
 *
 * Bewusst eine EIGENE Funktion und keine Erweiterung von invoiceProjectRefs:
 * dort haengen der Belegkopf (invoiceHeading), der Leistungsnachweis
 * (performanceProofProjectName), "Unser Zeichen" im PDF (invoiceOurReference)
 * und die Typziffer der Belegnummer (issueInvoice) mit dran. Eine Verbesserung
 * der ANZEIGE in einer Liste darf weder den Andruck eines Belegs noch eine
 * einmal gezogene Nummer veraendern.
 */
export const invoiceListProjectRefs = (
    invoice: InvoiceProjectSource,
    lookup?: ProjectRefLookup,
): InvoiceProjectRef[] => projectRefsOf(invoice, lookup, true);

/** Woraus sich der Projektbezug eines Belegs speist. */
type InvoiceProjectSource = {
    primaryProjectId?: string;
    lines?: InvoiceLine[];
    details?: { projects?: InvoiceProjectRef[] };
};

/**
 * @param frozenAsFallback Ohne Kopfprojekt und ohne Zeile mit Projektbezug den
 * eingefrorenen Vermerk vollstaendig gelten lassen. Setzt ausschliesslich
 * invoiceListProjectRefs; Begruendung dort.
 */
const projectRefsOf = (
    invoice: InvoiceProjectSource,
    lookup: ProjectRefLookup | undefined,
    frozenAsFallback: boolean,
): InvoiceProjectRef[] => {
    const frozen = new Map<string, InvoiceProjectRef>();
    for (const ref of invoice.details?.projects ?? []) {
        if (ref.id) frozen.set(ref.id, ref);
    }

    const fromLines = new Map<string, InvoiceProjectRef>();
    const order: string[] = [];
    for (const line of invoice.lines ?? []) {
        if (!line.projectId) continue;
        if (!fromLines.has(line.projectId)) {
            order.push(line.projectId);
            fromLines.set(line.projectId, {
                id: line.projectId,
                number: line.projectNumber,
                name: line.projectName,
            });
        }
    }

    const ids = invoice.primaryProjectId ? [invoice.primaryProjectId] : order;
    if (ids.length === 0) {
        // Reihenfolge und Umfang sind die des Vermerks; fehlende Felder fuellt
        // das Nachschlagen wie ueberall sonst auf. Doppelte ids kaemen aus
        // einem fehlerhaften Vermerk und werden wie bei den Zeilen auf das
        // erste Vorkommen zusammengezogen.
        const seen = new Set<string>();
        const refs: InvoiceProjectRef[] = [];
        for (const ref of invoice.details?.projects ?? []) {
            if (!ref.id) {
                refs.push(ref);
                continue;
            }
            // Ohne den Rueckfall bleibt ein Eintrag mit id aussen vor: der
            // Beleg selbst weist dieses Projekt gerade nicht als abgerechnet
            // aus, und der Andruck soll dem Beleg folgen, nicht dem Vermerk.
            if (!frozenAsFallback) continue;
            if (seen.has(ref.id)) continue;
            seen.add(ref.id);
            refs.push(mergeProjectRef(ref.id, [ref, lookup?.(ref.id)]));
        }
        return refs;
    }

    return ids.map((id) => mergeProjectRef(id, [frozen.get(id), fromLines.get(id), lookup?.(id)]));
};

/**
 * Der Projektbezug eines Belegs in einer Zeile.
 *
 * Bei mehreren Projekten wird ausdruecklich KEINES herausgegriffen: eine
 * Sammelrechnung, die eines von fuenf Projekten nennt, liest sich wie eine
 * Rechnung ueber genau dieses Projekt. Die Anzahl ist die ehrliche Aussage;
 * welche es sind, steht in den Abschnitten der Positionsliste.
 *
 * Leer, wenn kein Projekt bekannt ist — die Anzeige entscheidet dann selbst,
 * ob sie einen Gedankenstrich setzt oder die Angabe ganz weglaesst.
 */
export const invoiceProjectLabel = (refs: InvoiceProjectRef[]): string => {
    if (refs.length === 0) return '';
    if (refs.length === 1) return formatProjectRef(refs[0]);
    return `${refs.length} Projekte`;
};

/** Beschriftung der Referenzzeile im rechten Kopfblock. */
export const OUR_REFERENCE_LABEL = 'Unser Zeichen';

/**
 * "Unser Zeichen" fuer den Belegkopf: die Projektnummern des Belegs.
 *
 * Die klassische Referenzangabe eines Geschaeftsbriefs — unter welchem
 * Aktenzeichen der Vorgang bei UNS laeuft. Genau dafuer taugt die
 * Projektnummer: der Kunde nennt sie am Telefon, und der Beleg ist damit ohne
 * Umweg ueber die Belegnummer einem Vorgang zuzuordnen. Die Projektnummer aus
 * der UEBERSCHRIFT herauszunehmen (dort steht seither nur noch der Name) und
 * sie hier als benannte Referenz zu fuehren, ist dieselbe Entscheidung von
 * zwei Seiten: sie ist ein Schluessel, keine Bezeichnung.
 *
 * Bei mehreren Projekten stehen ALLE Nummern, dedupliziert und in der
 * Reihenfolge des Belegs. Anders als beim Projektvermerk der Ueberschrift
 * (invoiceHeading, wo bei mehreren Projekten bewusst KEINES genannt wird)
 * taeuscht das nichts vor: eine Liste von drei Nummern liest sich als drei
 * Vorgaenge, ein herausgegriffener Name laese den Beleg wie eine Rechnung
 * ueber genau dieses eine Projekt aussehen.
 *
 * Leer, wenn keine Nummer bekannt ist — der Generator laesst die Zeile dann
 * ganz weg. Eine Beschriftung ohne Wert ist ein Formularrest, kein Zeichen
 * (gleiche Regel wie bei costCenterLine).
 *
 * Pur und hier statt im Generator, damit die Regel ohne PDF pruefbar bleibt
 * (kein jsdom im Projekt).
 */
export const invoiceOurReference = (refs: InvoiceProjectRef[]): string => {
    const seen = new Set<string>();
    const numbers: string[] = [];
    for (const ref of refs) {
        const number = ref.number?.trim();
        if (!number || seen.has(number)) continue;
        seen.add(number);
        numbers.push(number);
    }
    return numbers.join(', ');
};

// ═══════════════════════════════════════════════════════════════════════════
// Belegkopf
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Titelzeile des Belegs und die Zeilen darunter.
 *
 * Pur und hier statt im Generator, weil sie eine Regel trifft und nicht ein
 * Layout: WELCHE Angaben oben stehen, muss testbar sein, ohne ein PDF zu
 * bauen. Der Generator entscheidet nur noch, wie gross und wie grau.
 *
 * GENAU EINE Titelzeile. Frueher stand unter einer eigenen Ueberschrift noch
 * ein zweiter, dezenter Belegartvermerk ("Schlussrechnung" / darunter
 * "Rechnung (Entwurf)"). Auf dem Papier las sich das wie zwei konkurrierende
 * Ueberschriften — der Bearbeiter waehlt den Titel bewusst, und was er waehlt,
 * ist die Ueberschrift. Die Belegart bleibt trotzdem ablesbar: sie steht im
 * rechten Kopfblock an der Nummernzeile ('Rechnungs-Nr.'), und der
 * Entwurfsvermerk wandert an den gewaehlten Titel (siehe unten).
 */
export interface InvoiceHeading {
    /** Fette Ueberschrift ueber dem Beleg — die einzige Titelzeile. */
    title: string;
    /**
     * Der Projektvermerk fuer den Andruck, OHNE Beschriftung und OHNE unsere
     * Projektnummer ('Neubau Lagerhalle'). Das Label setzt der Generator —
     * genau wie im Angebot, wo quote.title ebenfalls roh vorliegt. Leer, wenn
     * kein oder mehr als ein Projekt.
     *
     * Die Nummer bleibt aussen vor, weil sie ein interner Schluessel ist;
     * hat das Projekt keinen Namen, steht sie ersatzweise da (siehe
     * formatProjectRefNameOnly). Wer den vollen Bezug braucht — Belegliste,
     * Maske, DATEV — nimmt formatProjectRef.
     *
     * Traegt der Beleg einen eigenen Anzeigenamen (details.projectNameOverride),
     * gilt DER. Nur hier und damit nur auf dem Beleg: Projektlisten, CRM und
     * Auswertungen nennen unveraendert den echten Projektnamen.
     */
    projectLine: string;
}

/**
 * Vermerk, der einen Beleg ohne Nummer als Entwurf kennzeichnet.
 *
 * Eine Konstante, weil zwei Stellen sie brauchen: der Standardtitel haengt sie
 * an die Belegart, und invoiceHeading haengt sie an eine eigene Ueberschrift,
 * die sie noch nicht traegt.
 */
export const DRAFT_TITLE_NOTE = '(Entwurf)';

/**
 * Standardueberschrift: die Belegart, beim Entwurf mit dem Vermerk '(Entwurf)'.
 *
 * OHNE Belegnummer. Sie steht im rechten Kopfblock des Belegs ('Rechnungs-Nr.',
 * beim Entwurf mit '(Entwurf)' als Wert) — zwischen Rechnungsdatum und
 * Kunden-Nr., wo eine Buchhaltung sie sucht. Zweimal genannt las sie sich wie
 * zwei verschiedene Angaben, und die Ueberschrift wurde zur Nummernzeile,
 * statt zu sagen, was fuer ein Beleg das ist.
 *
 * Der Entwurfsvermerk bleibt dagegen oben: er ist keine Kopfangabe, sondern
 * eine Warnung ueber dem ganzen Blatt — dieses Papier ist noch kein Beleg.
 */
export const defaultInvoiceTitle = (invoice: {
    documentType: DocumentType;
    invoiceNumber?: string;
}): string =>
    `${DOCUMENT_TYPE_LABEL[invoice.documentType]}${
        invoice.invoiceNumber?.trim() ? '' : ` ${DRAFT_TITLE_NOTE}`
    }`;

/**
 * Belegarten, fuer die die globale Standard-Ueberschrift ueberhaupt gilt.
 *
 * NUR die schlichte Rechnung. Jede andere Belegart traegt ihre Art im Titel,
 * und die ist eine Aussage ueber den Beleg, keine Gestaltung: eine
 * SCHLUSSRECHNUNG schliesst den Auftrag ab und zieht die Abschlaege ab, eine
 * STORNORECHNUNG hebt einen Beleg auf, eine GUTSCHRIFT erstattet. Die globale
 * Einstellung ("Sicherheits- und Gesundheitsschutzkoordination") ist als
 * Betreffzeile der laufenden Rechnung gedacht — sie stand bisher aber ueber
 * ALLEN Belegarten und verdeckte damit genau die Angabe, die den Beleg
 * ausmacht. Auf dem abgeschlossenen Projekt stand "Rechnung" statt
 * "Schlussrechnung", obwohl die Belegart stimmte.
 *
 * Die Ueberschrift AM BELEG (details.headerText) bleibt davon unberuehrt: wer
 * fuer eine Schlussrechnung ausdruecklich einen anderen Titel setzt, bekommt
 * ihn. Nur die stille globale Vorgabe greift nicht mehr ueber die Belegart
 * hinweg.
 */
const DEFAULT_HEADER_TEXT_TYPES: ReadonlySet<DocumentType> = new Set<DocumentType>(['INVOICE']);

/**
 * Was oben steht, wenn der Beleg keine eigene Ueberschrift traegt: die globale
 * Einstellung (InvoiceConfig.defaultHeaderText), sonst der Standardtitel der
 * Belegart.
 *
 * Die Einstellung gilt nur fuer die schlichte Rechnung — siehe
 * DEFAULT_HEADER_TEXT_TYPES.
 *
 * Eigene Funktion, weil der Editor als Platzhalter genau das zeigen muss, was
 * beim Leerlassen tatsaechlich gedruckt wird — invoiceHeading trifft dieselbe
 * Wahl und nimmt nur den Beleg noch davor. Nur Leerraum zaehlt als nicht
 * gesetzt: ein versehentliches Leerzeichen in den Einstellungen darf keinem
 * Beleg eine leere Kopfzeile geben.
 */
export const fallbackInvoiceTitle = (
    invoice: { documentType: DocumentType; invoiceNumber?: string },
    defaultHeaderText?: string,
): string =>
    (DEFAULT_HEADER_TEXT_TYPES.has(invoice.documentType) ? defaultHeaderText?.trim() : '') ||
    defaultInvoiceTitle(invoice);

/**
 * Ueberschrift und Projektzeile eines Belegs.
 *
 * Zwei Invarianten tragen die Funktion:
 *
 * 1. GENAU EINE Ueberschrift, und es ist die gewaehlte. Setzt der Bearbeiter
 *    eine eigene ("Schlussrechnung"), steht sie allein oben. Frueher rutschte
 *    der Standardtitel dann in eine dezente Begleitzeile darunter, damit die
 *    Belegart ablesbar blieb — auf dem Papier ergab das zwei Ueberschriften
 *    ("Schlussrechnung" / "Rechnung (Entwurf)"), die sich widersprachen,
 *    obwohl der Bearbeiter die obere ausdruecklich gewaehlt hatte.
 *
 *    Die Belegart geht damit nicht verloren: sie steht im rechten Kopfblock an
 *    der Nummernzeile ('Rechnungs-Nr.', 'Gutschrift-Nr.'), und der Bezug eines
 *    Stornos oder einer Korrektur auf den Ursprungsbeleg steht in der eigenen
 *    Ueberschrift und in der Grundzeile (details.reasonText).
 *
 *    Der ENTWURFSVERMERK bleibt dagegen zwingend oben — er ist keine
 *    Kopfangabe, sondern eine Warnung ueber dem ganzen Blatt: dieses Papier ist
 *    noch kein Beleg. Traegt die eigene Ueberschrift ihn nicht selbst, haengt
 *    ihn diese Funktion an. Ohne das haetten Entwuerfe mit eigenem Titel den
 *    Vermerk zusammen mit der Begleitzeile verloren.
 * 2. Bei mehreren Projekten wird KEINES genannt. Eine Sammelrechnung, die eines
 *    von fuenf Projekten in die Kopfzeile schriebe, laese sich wie eine
 *    Rechnung ueber genau dieses eine. Welche es sind, steht in den
 *    Abschnitten der Positionsliste.
 *
 * Drei Quellen, eine Rangfolge: Beleg (details.headerText) vor globaler
 * Einstellung (options.defaultHeaderText) vor Standardtitel.
 *
 * Aufgeloest wird zur Renderzeit, nicht beim Anlegen. Wer die Einstellung
 * spaeter aendert, aendert damit alle Entwuerfe ohne eigene Ueberschrift —
 * ausgestellte Belege nicht, deren PDF ist archiviert.
 */
export const invoiceHeading = (
    invoice: {
        documentType: DocumentType;
        invoiceNumber?: string;
        primaryProjectId?: string;
        lines?: InvoiceLine[];
        details?: {
            headerText?: string;
            projects?: InvoiceProjectRef[];
            projectNameOverride?: string;
        };
    },
    lookup?: ProjectRefLookup,
    options?: { defaultHeaderText?: string },
): InvoiceHeading => {
    const standard = defaultInvoiceTitle(invoice);
    const effective =
        invoice.details?.headerText?.trim() ||
        fallbackInvoiceTitle(invoice, options?.defaultHeaderText);
    // Gesetzt heisst hier: der Standardtitel wurde verdraengt — egal von
    // welcher der beiden Quellen. Nur dann fehlt der Entwurfsvermerk, denn
    // der Standardtitel fuehrt ihn selbst mit (defaultInvoiceTitle).
    const custom = effective === standard ? '' : effective;
    const isDraft = !invoice.invoiceNumber?.trim();
    // Nicht doppeln: schreibt der Bearbeiter '(Entwurf)' selbst in den Titel
    // (oder stammt er von einer Vorlage, die ihn schon traegt), bleibt es bei
    // einem Vermerk.
    const title =
        custom && isDraft && !custom.includes(DRAFT_TITLE_NOTE)
            ? `${custom} ${DRAFT_TITLE_NOTE}`
            : custom || standard;

    const refs = invoiceProjectRefs(invoice, lookup);
    // Der eigene Anzeigename schlaegt den echten Projektnamen — aber nur, wo
    // ueberhaupt EIN Projekt genannt wird. Bei einer Sammelrechnung bliebe die
    // Zeile sonst leer und der Ueberschreiber wirkungslos sichtbar; bei einem
    // Beleg ganz ohne Projekt erfaende er einen Bezug, den es nicht gibt.
    const overridden = invoice.details?.projectNameOverride?.trim();

    return {
        title,
        // Namens-only: die Projektnummer gehoert nicht auf den Andruck.
        projectLine: refs.length === 1 ? overridden || formatProjectRefNameOnly(refs[0]) : '',
    };
};

/**
 * Der Projektname fuer den Leistungsnachweis ZU EINEM BELEG.
 *
 * Dieselbe Regel wie die Projektzeile des Belegs (invoiceHeading): eigener
 * Anzeigename schlaegt den eingefrorenen Ref-Namen, und nur wo genau EIN
 * Projekt genannt wird. Rechnung und angehaengter Nachweis muessen dieselbe
 * Baustelle benennen — zwei Namen fuer denselben Vorgang liest der Empfaenger
 * als zwei Vorgaenge.
 *
 * `undefined` heisst: keine Vorgabe — der Generator nimmt dann den Namen aus
 * den Projektdaten selbst.
 */
export const performanceProofProjectName = (invoice: {
    primaryProjectId?: string;
    lines?: InvoiceLine[];
    details?: { projects?: InvoiceProjectRef[]; projectNameOverride?: string };
}): string | undefined => {
    const refs = invoiceProjectRefs(invoice);
    if (refs.length !== 1) return undefined;
    return invoice.details?.projectNameOverride?.trim() || formatProjectRefNameOnly(refs[0]) || undefined;
};

// ═══════════════════════════════════════════════════════════════════════════
// Leistungszeitraum des Andrucks
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Kalendertag, der als Datum eines Belegs taugen kann.
 *
 * Verwirft insbesondere zweistellig angefangene Jahre. Ein `<input type="date">`
 * meldet waehrend des Tippens Zwischenstaende wie "0002-05-01": wer die
 * ungeprueft uebernimmt, speichert sie im Entwurf, und spaetestens die
 * Datumsrechnung macht daraus ein Jahr 1902. Ein Beleg mit einem solchen
 * Leistungsdatum ist nach §14 UStG unbrauchbar und faellt erst beim Empfaenger
 * auf.
 *
 * Untergrenze 1000 statt einer scharfen Plausibilitaet: die Funktion soll
 * Tippzwischenstaende abfangen, nicht ueber die Geschaeftsjahre eines Betriebs
 * urteilen.
 */
const plausibleDay = (value?: string): string | undefined => {
    const day = asDay(value?.trim());
    if (!day || day.length !== 10) return undefined;
    return Number(day.slice(0, 4)) >= 1000 ? day : undefined;
};

/**
 * Der auszuweisende Leistungszeitraum aus dem GEWAEHLTEN Abrechnungszeitraum.
 *
 * VERBATIM uebernommen, nicht auf Monatsgrenzen gezogen. Der Monatswaehler im
 * Anlagedialog liefert ohnehin bereits den Monatsersten und den Monatsletzten —
 * die gemeldete Rechnung fuer Juni/Juli weist damit "01.06.2026 – 31.07.2026"
 * aus, ohne dass hier gerundet werden muesste. Wer dagegen bewusst vom 10.06.
 * bis zum 20.06. abrechnet, bekaeme durch eine Rundung einen Beleg, der einen
 * Zeitraum behauptet, in dem er gar nicht abgerechnet hat: eine falsche Angabe
 * nach §14 Abs. 4 Nr. 6 UStG, in BT-73/74 der E-Rechnung und im Widerspruch
 * zur Zeile "Abrechnungszeitraum" auf demselben Blatt.
 *
 * Was der Beleg ABRECHNET, bleibt unberuehrt: der Positionsaufbau liest den
 * gewaehlten Zeitraum selbst, nicht diese Ableitung.
 *
 * Eine offene Seite bleibt offen — die fehlende faellt beim Andruck auf das
 * Leistungsdatum zurueck (printedServicePeriod).
 */
export const servicePeriodFromSelection = (selected: {
    from?: string;
    to?: string;
}): { servicePeriodFrom?: string; servicePeriodTo?: string } => {
    const from = plausibleDay(selected.from);
    const to = plausibleDay(selected.to);
    return {
        ...(from ? { servicePeriodFrom: from } : {}),
        ...(to ? { servicePeriodTo: to } : {}),
    };
};

export interface PrintedServicePeriodSource {
    deliveryDateFrom?: string;
    deliveryDateTo?: string;
    details?: {
        servicePeriodFrom?: string;
        servicePeriodTo?: string;
        /** Eingefrorener Leistungszeitraum — siehe performancePeriod. */
        performedFrom?: string;
        performedTo?: string;
        billingMode?: 'CUMULATIVE';
    };
}

export interface ProofFilterSource {
    deliveryDateFrom?: string;
    deliveryDateTo?: string;
    details?: {
        proofRangeFrom?: string;
        proofRangeTo?: string;
        proofExcludedServiceIds?: string[];
    };
}

/**
 * Der Zeitraum, auf den der LEISTUNGSNACHWEIS filtert.
 *
 * Vorgabe ist das Auswahlfenster des Belegs (deliveryDateFrom/-To) — das war
 * bis hierher die einzige Quelle und bleibt der Normalfall. Die Angabe am
 * Beleg (details.proofRangeFrom/-To) schlaegt sie, je Seite einzeln: wer nur
 * das Ende zieht, soll den Beginn nicht nachtragen muessen. Dieselbe Regel
 * wie printedServicePeriod, und aus demselben Grund dieselbe
 * Plausibilitaetspruefung — ein halb getipptes Datum ist keine Aussage,
 * sondern ein Zwischenstand, und es wuerde hier eine leere Anlage erzeugen.
 *
 * `null` und nicht `undefined` fuer die offene Seite: genau diese Form
 * verlangt buildPerformanceProofData, und eine Umformung unterwegs ist die
 * Stelle, an der eine offene Seite zur geschlossenen wird.
 */
export const proofFilterRange = (
    invoice: ProofFilterSource | undefined,
): { start: string | null; end: string | null } => ({
    start:
        plausibleDay(invoice?.details?.proofRangeFrom) ??
        asDay(invoice?.deliveryDateFrom) ??
        null,
    end:
        plausibleDay(invoice?.details?.proofRangeTo) ?? asDay(invoice?.deliveryDateTo) ?? null,
});

/**
 * Weicht der Nachweiszeitraum vom Auswahlfenster ab? Fuer die Maske, die das
 * sagen soll, statt den Unterschied an zwei Datumsfeldern verstecken zu
 * lassen.
 */
export const hasCustomProofRange = (invoice: ProofFilterSource | undefined): boolean => {
    const range = proofFilterRange(invoice);
    return (
        range.start !== (asDay(invoice?.deliveryDateFrom) ?? null) ||
        range.end !== (asDay(invoice?.deliveryDateTo) ?? null)
    );
};

/**
 * Die Positions-Ids, die der Nachweis auslaesst.
 *
 * Getrimmt und ohne Leerzeichen-Reste: die Liste entsteht aus Haekchen in der
 * Maske und wandert durch jsonb — ein Eintrag `''` wuerde nichts ausblenden,
 * aber jeden Vergleich „ist gefiltert?" auf wahr ziehen und die Maske eine
 * Einschraenkung melden lassen, die es nicht gibt.
 */
export const proofExcludedServiceIds = (invoice: ProofFilterSource | undefined): string[] =>
    (invoice?.details?.proofExcludedServiceIds ?? [])
        .map((id) => String(id ?? '').trim())
        .filter((id) => id.length > 0);

/** yyyy-mm-dd als TT.MM.JJJJ. Rein auf der Zeichenkette — keine Zeitzone. */
const germanDay = (day: string): string => {
    const [y, m, d] = day.split('-');
    return `${d}.${m}.${y}`;
};

/**
 * Der ABRECHNUNGSZEITRAUM: der Zeitraum, ueber den abgerechnet wird, so wie
 * der Bearbeiter ihn angegeben hat.
 *
 * Zwei Zeitraeume stehen auf dem Beleg, und sie beantworten verschiedene
 * Fragen:
 *
 *   • Abrechnungszeitraum (diese Funktion) — WORUEBER wird abgerechnet? Beim
 *     Anlegen aus der Auswahl uebernommen (servicePeriodFromSelection), in der
 *     Maske frei korrigierbar. Eine Angabe des Betriebs, keine Ableitung.
 *   • Leistungszeitraum (performancePeriod) — WANN wurde geleistet? Von der
 *     ersten bis zur letzten Leistung, die dieser Beleg abrechnet. Das ist der
 *     Leistungszeitpunkt nach §14 Abs. 4 Nr. 6 UStG.
 *
 * Beide koennen auseinanderfallen: eine Rechnung ueber Juni/Juli kann
 * Leistungen vom 05.03. bis 31.07. abrechnen, wenn sie kumulativ den
 * Gesamtstand fortschreibt. Genau dieser Unterschied war der gemeldete Fehler —
 * nicht die Spanne selbst, sondern dass sie unter dem falschen Namen stand.
 *
 * Der Speicherort heisst weiterhin details.servicePeriodFrom/-To. Die
 * jsonb-Schluessel bleiben, damit bestehende Belege unveraendert lesen; was
 * darin steht, ist der Abrechnungszeitraum.
 *
 * Vorrang hat die Angabe des Bearbeiters, und zwar je Seite einzeln: wer nur
 * das Ende korrigiert, soll nicht auch den Beginn nachtragen muessen. Fehlt
 * eine Seite, gilt dort das Leistungsdatum des Belegs — der Stand jedes Belegs
 * aus der Zeit vor diesen Feldern.
 *
 * Bewusst OHNE Plausibilitaetskorrektur der REIHENFOLGE: ein verdrehter
 * Zeitraum wird angezeigt, nicht stillschweigend geradegerueckt. Sichtbar zu
 * bleiben ist der Sinn der Eingabe; die Ausstellung sperrt validateInvoice
 * ohnehin. Ein noch unfertig getipptes Datum wird dagegen verworfen
 * (plausibleDay) — es ist keine Aussage, sondern ein Zwischenstand.
 */
export const printedServicePeriod = (
    invoice: PrintedServicePeriodSource | undefined,
): { from?: string; to?: string } => ({
    from: plausibleDay(invoice?.details?.servicePeriodFrom) ?? asDay(invoice?.deliveryDateFrom),
    to: plausibleDay(invoice?.details?.servicePeriodTo) ?? asDay(invoice?.deliveryDateTo),
});

export interface PerformancePeriodSource extends PrintedServicePeriodSource {
    lines?: InvoiceLine[];
}

/**
 * Erster und letzter TATSAECHLICH ERBRACHTER Leistungstag eines Protokolls.
 *
 * Die Grundlage des Leistungszeitraums, und bewusst NICHT die Zuordnung des
 * Belegs. Kumulativ verbraucht die Zuordnung die offenen Protokolleintraege
 * vom aeltesten her: ist der Zuwachs einer Rechnung schon durch die Eintraege
 * von Januar bis April gedeckt, endet ihre Zuordnung am 22.04. — obwohl im
 * Juli und August weiter geleistet wurde und der Beleg diesen Stand mit
 * abrechnet. Genau dieser Beleg wies "27.01.–22.04.2026" aus, waehrend die
 * letzten Begehungen am 27.07. und 10.08. stattfanden.
 *
 * Der Leistungszeitraum beantwortet, WANN geleistet wurde, nicht welche
 * Protokollzeilen die Mengenrechnung zufaellig aufgebraucht hat.
 *
 * `allocatableEntries` ist derselbe Filter, mit dem die Abrechnung arbeitet:
 * Buchungseintraege (BU-) und Zeilen ohne Menge zaehlen nicht — sie sind
 * Abrechnungsvorgaenge, keine Leistung. Dazu kommen hier die aus einer
 * Alt-Rechnung nachgetragenen Zeilen (ALT-): sie datieren auf den Altbeleg und
 * sind mit ihm bereits abgerechnet.
 *
 * `asOf` ist der Stichtag: spaeter Geleistetes gehoert nicht auf einen Beleg,
 * der bis zu diesem Tag abrechnet. Auf den Kalendertag gekuerzt, sonst faellt
 * eine Leistung am Stichtag selbst heraus.
 *
 * `since` ist der Beginn eines FORTSCHREIBENDEN Belegs ("Leistung von"). Er
 * rechnet nur die Leistungen seines Zeitraums ab, also beginnt auch sein
 * Leistungszeitraum nicht vorher. Ohne diese Grenze wies die Augustrechnung
 * von Projekt 6051 "02.02.2026 – 31.08.2026" aus — den ersten Protokolltag
 * des Projekts, den sie gar nicht abrechnet —, und im Editor liess sich das
 * nicht aendern. Kumulativ bleibt `since` leer: dort zeigt jede Zeile den
 * Gesamtstand ab der ersten Leistung.
 */
export const performedSpanFromLog = (
    serviceLog: ServiceTransaction[] | undefined,
    asOf?: string,
    since?: string,
): { from?: string; to?: string } => {
    const limit = asDay(asOf);
    const start = plausibleDay(since);
    let from: string | undefined;
    let to: string | undefined;
    // ALT-Zeilen bleiben draussen. Sie datieren auf die Alt-RECHNUNG, nicht auf
    // den Tag der Arbeit, und was sie tragen, hat ein frueherer Beleg bereits
    // abgerechnet. Blieben sie drin, zoege eine im Dezember 2025 eingelesene
    // Rechnung den Leistungsbeginn des naechsten Belegs bis dorthin zurueck —
    // ein Leistungszeitpunkt nach §14 Abs. 4 Nr. 6 UStG, den dieser Beleg gar
    // nicht abrechnet (Projekt 6193).
    //
    // Bewusst NUR hier und nicht in allocatableEntries: die Mengenverteilung
    // haelt ihre eigene, feinere Behandlung der ALT-Zeilen vor (openByMonth mit
    // altEntryIds/removedAltQty), und sie dort ein zweites Mal auszuschliessen
    // zoege den Altbestand doppelt ab.
    for (const entry of allocatableEntries(
        (serviceLog ?? []).filter((e) => !isBackfilledAltEntry(e)),
    )) {
        // Der BERLINER Tag der Leistung (Betreiber-Entscheid 1 vom
        // 02.10.2026) — der Tag, an dem auf der Baustelle gearbeitet wurde,
        // und derselbe, nach dem der Beleg seine Mengen zaehlt.
        const day = buchungstag(entry.date);
        if (!day) continue;
        if (limit && day > limit) continue;
        if (start && day < start) continue;
        if (!from || day < from) from = day;
        if (!to || day > to) to = day;
    }
    return { from, to };
};

/**
 * Die Spanne als details-Fragment zum Dazuspreizen — leer, wenn es keine gibt.
 *
 * Fragment und kein veraendertes details-Objekt: der Entwurf baut seine details
 * aus mehreren Quellen zusammen (dieselbe Form wie applyStickyCostCenter). Ohne
 * datierten Protokolleintrag bleibt es leer, statt eine Leistung zu behaupten,
 * fuer die es keinen Beleg gibt.
 */
export const performedDetails = (
    performed: { from?: string; to?: string } | undefined,
): { performedFrom?: string; performedTo?: string } => ({
    ...(performed?.from ? { performedFrom: performed.from } : {}),
    ...(performed?.to ? { performedTo: performed.to } : {}),
});

/**
 * Der von Hand gesetzte Leistungsbeginn eines kumulativen Belegs — oder
 * undefined, wenn der Aufbau ihn selbst ermitteln soll.
 *
 * Nur an einem KUMULATIVEN Beleg: fortschreibend gibt der Bearbeiter „Leistung
 * von" ohnehin direkt ein, dort waere das Feld eine zweite Quelle fuer
 * denselben Wert. Ein halb getipptes Datum gilt nicht (plausibleDay).
 */
export const manualCumulativeStart = (
    invoice: { details?: { billingMode?: string; cumulativeFromManual?: string } } | undefined,
): string | undefined =>
    invoice?.details?.billingMode === 'CUMULATIVE'
        ? plausibleDay(invoice.details.cumulativeFromManual)
        : undefined;

/**
 * Die ermittelte Spanne mit dem von Hand gesetzten Beginn — falls es einen
 * gibt. Das Ende bleibt, wie das Protokoll es sagt; liegt der gesetzte Beginn
 * dahinter, meldet das die Belegpruefung als verdrehten Zeitraum, statt dass
 * hier still ein Ende erfunden wuerde.
 */
export const withManualStart = (
    performed: { from?: string; to?: string } | undefined,
    manual: string | undefined,
): { from?: string; to?: string } => (manual ? { ...performed, from: manual } : { ...performed });

/**
 * Der LEISTUNGSZEITRAUM: von der ersten bis zur letzten Leistung, die DIESER
 * Beleg abrechnet. Der Leistungszeitpunkt nach §14 Abs. 4 Nr. 6 UStG.
 *
 * EINE Regel fuer alle Ausgaben — Andruck, E-Rechnung (BT-72), DATEV und der
 * angehaengte Nachweis. Zwei Fassungen liefen frueher oder spaeter
 * auseinander, und ein Beleg, dessen Buchungsstapel einen anderen
 * Leistungszeitpunkt nennt als sein Sichtbild, bucht in die falsche
 * Voranmeldungsperiode.
 *
 * ZUERST die am Beleg festgehaltene Spanne (details.performedFrom/-To). Sie
 * kommt aus dem Leistungsprotokoll des Projekts (performedSpanFromLog) und
 * wird beim Ausstellen eingefroren wie die Zuordnung. Nur sie kann die Frage
 * richtig beantworten: dem fertigen Beleg liegt das Protokoll nicht mehr bei,
 * und PDF, E-Rechnung, DATEV und Archiv arbeiten allein mit ihm.
 *
 * Erst wenn sie fehlt — Altbelege und Entwuerfe, die noch nie aufgebaut
 * wurden — greift die alte, aus dem Beleg selbst abgeleitete Kette. Sie ist
 * eine Naeherung: die Zuordnung nennt die abgerechneten, nicht die erbrachten
 * Tage. Fuer einen Beleg ohne festgehaltene Spanne ist sie das Beste, was da
 * ist.
 *
 * Das ENDE der Naeherung:
 *
 * 1. Die Zuordnung (billingPeriodOf), beim Ausstellen eingefroren.
 * 2. `fallback` — die Vorhersage fuer den Entwurf, der noch keine Zuordnung
 *    traegt (invoiceService: cumulativeBillingPeriod).
 * 3. deliveryDateTo, der Stichtag bzw. das Ende des Auswahlfensters.
 * 4. Zuletzt der Abrechnungszeitraum, nur wenn ein Beleg ueberhaupt kein
 *    Leistungsdatum traegt. Er steht bewusst GANZ HINTEN: ein Feld, das nur den
 *    Andruck steuern soll, darf den steuerlichen Leistungszeitpunkt nicht
 *    verdraengen, solange es echte Leistungsdaten gibt.
 *
 * Der BEGINN unterscheidet die beiden Abrechnungsmodelle:
 *
 * • KUMULATIV zuerst deliveryDateFrom. Dort zeigt jede Zeile den GESAMTEN
 *   Leistungsstand — abgerechnet ist alles von der ersten Leistung des
 *   Projekts (cumulativeFrom) an. Die Zuordnung traegt dort nur den ZUWACHS
 *   und waere als Leistungsbeginn viel zu spaet: der zweite Beleg eines
 *   Projekts naennte den Juni, obwohl er den Maerz mit abrechnet.
 *
 *   ABER nur, wenn es ueberhaupt eine Leistung gibt. Ohne datierten
 *   Protokolleintrag setzt der Abrechnungslauf den GEWAEHLTEN Zeitraumbeginn
 *   als deliveryDateFrom ein (billingRunService: `built.cumulativeFrom ??
 *   period.from`). Ihn als ersten Leistungstag auszuweisen hiesse, eine
 *   Leistung zu behaupten, fuer die es keinen Beleg gibt — auf einem Dokument,
 *   das den Leistungszeitpunkt nach §14 UStG ausweist. Fehlt die Evidenz,
 *   faellt der Beginn auf die ausdrueckliche Angabe des Bearbeiters zurueck
 *   und sonst weg.
 * • FORTSCHREIBEND zuerst die Zuordnung, dann dieselbe Kette wie oben. Dort
 *   rechnet der Beleg nur die neuen Mengen ab, und ihr erster Tag ist der
 *   Leistungsbeginn.
 */
export const performancePeriod = (
    invoice: PerformancePeriodSource | undefined,
    fallback?: { from: string; to: string },
): { from?: string; to?: string } => {
    // Die am Beleg festgehaltene Spanne schlaegt jede Ableitung: sie kommt aus
    // dem Leistungsprotokoll selbst und ist die einzige Quelle, die WANN
    // GELEISTET WURDE beantworten kann. Je Seite einzeln, damit ein Altbeleg
    // mit halb nachgetragener Angabe nicht auf einmal gar keine mehr hat.
    const frozen = {
        from: plausibleDay(invoice?.details?.performedFrom),
        to: plausibleDay(invoice?.details?.performedTo),
    };
    if (frozen.from && frozen.to) return frozen;

    const stated = printedServicePeriod(invoice);
    const delivery = {
        from: asDay(invoice?.deliveryDateFrom),
        to: asDay(invoice?.deliveryDateTo),
    };
    const allocated = billingPeriodOf(invoice?.lines) ?? fallback;
    const cumulative = invoice?.details?.billingMode === 'CUMULATIVE';
    // Kumulativ zaehlt deliveryDateFrom nur als Leistungsbeginn, wenn eine
    // tatsaechliche Leistung dahintersteht. Ohne sie bleibt allein die
    // ausdrueckliche Angabe — NICHT `stated`, denn das faellt selbst wieder auf
    // deliveryDateFrom zurueck und holte die erfundene Angabe zurueck.
    const cumulativeFrom = allocated
        ? (delivery.from ?? allocated.from)
        : plausibleDay(invoice?.details?.servicePeriodFrom);
    return {
        from:
            frozen.from ??
            (cumulative ? cumulativeFrom : (allocated?.from ?? delivery.from ?? stated.from)),
        to: frozen.to ?? allocated?.to ?? delivery.to ?? stated.to,
    };
};

export const SERVICE_PERIOD_INVERTED_MESSAGE =
    'Der ausgewiesene Abrechnungszeitraum endet vor seinem Beginn. Bitte „bis" auf ein Datum ab dem „von"-Datum setzen.';

/**
 * Beanstandung des Zeitraums, den der Beleg TATSAECHLICH ausweisen wuerde.
 *
 * Geprueft wird das Ergebnis von printedServicePeriod und nicht die beiden
 * Eingabefelder gegeneinander: die Angabe wirkt je Seite einzeln, und wer nur
 * "bis" auf den 31.05. setzt, waehrend der Beleg am 01.06. beginnt, hat genau
 * denselben verdrehten Zeitraum erzeugt — nur eben gegen die Gegenseite. Die
 * Pruefung allein auf den beiden Feldern liesse ihn durch bis auf den Andruck
 * und nach BT-73/74.
 *
 * Nur, wenn ueberhaupt eine eigene Angabe im Spiel ist: sonst stammt der
 * Zeitraum vollstaendig aus deliveryDateFrom/-To, und deren Verdrehung
 * beanstandet validateInvoice bereits als `delivery_period_inverted`. Zweimal
 * dieselbe Sache zu melden liesse den Bearbeiter zwei Fehler suchen.
 *
 * Ein einzelner Tag (von = bis) ist erlaubt — der Beleg nennt ihn dann als
 * Leistungsdatum.
 */
export const servicePeriodIssue = (
    invoice: PrintedServicePeriodSource | undefined,
): string | undefined => {
    const overridden =
        plausibleDay(invoice?.details?.servicePeriodFrom) ??
        plausibleDay(invoice?.details?.servicePeriodTo);
    if (!overridden) return undefined;
    const { from, to } = printedServicePeriod(invoice);
    return from && to && from > to ? SERVICE_PERIOD_INVERTED_MESSAGE : undefined;
};

/**
 * Der Beleg zieht Leistungen heran, die ausserhalb seines Abrechnungszeitraums
 * liegen.
 *
 * Der Abrechnungszeitraum entsteht beim Anlegen aus der gewaehlten Auswahl und
 * wird danach NICHT mehr nachgezogen — er ist eine Angabe des Bearbeiters, und
 * ein spaeterer Positionsaufbau darf sie nicht stillschweigend ueberschreiben.
 * Wer den Stichtag hinterher verschiebt, bekaeme sonst einen Beleg, der bis
 * August heranzieht und Juni/Juli als Abrechnungszeitraum ausweist. Deshalb:
 * nicht korrigieren, sondern sagen.
 *
 * Das ENDE wird immer verglichen, der BEGINN nur fortschreibend. Kumulativ
 * liegt er planmaessig auseinander: der Beleg schreibt den Gesamtstand fort und
 * reicht bis zur ersten Leistung des Projekts zurueck, waehrend abgerechnet
 * ueber den gewaehlten Monat wird — das ist der Sinn der Trennung und keine
 * Abweichung; der Leistungszeitraum auf dem Beleg weist die volle Spanne
 * ohnehin aus. Fortschreibend dagegen IST das Auswahlfenster der
 * Abrechnungszeitraum, und ein abweichender Beginn ist dort dieselbe stille
 * Falschaussage wie ein abweichendes Ende.
 *
 * Ein frisch angelegter Beleg meldet nie etwas: seine Auswahl wird VERBATIM
 * uebernommen (servicePeriodFromSelection), beide Seiten sind also gleich.
 */
export const servicePeriodDriftNote = (
    invoice: PrintedServicePeriodSource | undefined,
): string | undefined => {
    const stated = printedServicePeriod(invoice);
    const cumulative = invoice?.details?.billingMode === 'CUMULATIVE';
    const billedFrom = cumulative ? undefined : asDay(invoice?.deliveryDateFrom);
    const billedTo = asDay(invoice?.deliveryDateTo);
    const parts: string[] = [];
    if (billedFrom && stated.from && billedFrom !== stated.from) {
        parts.push(`ab ${germanDay(billedFrom)} abgerechnet, ausgewiesen ist ab ${germanDay(stated.from)}`);
    }
    if (billedTo && stated.to && billedTo !== stated.to) {
        parts.push(`bis ${germanDay(billedTo)} abgerechnet, ausgewiesen ist bis ${germanDay(stated.to)}`);
    }
    // Die FOLGE benennen, nicht nur die Abweichung: "bis 31.08. abgerechnet"
    // sagt dem Bearbeiter, dass die Augustbegehungen mit auf der Rechnung
    // stehen — die blosse Angabe zweier Daten tat das nicht. Und das Feld beim
    // Namen nennen: er sieht zwei Datumspaare untereinander und korrigiert
    // sonst das obere, das die Positionen steuert.
    return parts.length
        ? `Es werden Leistungen ${parts.join(' und ')} – Feld „Abrechnungszeitraum auf dem Beleg" prüfen.`
        : undefined;
};

/**
 * Der Stichtag, auf den sich die Abrechnung mit einem Klick begrenzen laesst.
 *
 * Der Fall aus der Praxis: der Bearbeiter traegt im Kasten "bis 31.07." ein und
 * haelt die Sache fuer erledigt — die Augustbegehungen werden trotzdem
 * abgerechnet, weil der Kasten nur den Andruck steuert und der Stichtag oben
 * unveraendert auf dem 31.08. steht. Der Hinweis darauf half nicht: niemand
 * verband ihn mit dem oberen Feld.
 *
 * Angeboten wird die Begrenzung nur in DIESER Richtung — der ausgewiesene
 * Zeitraum endet FRUEHER als der Stichtag. Dann ist die Absicht eindeutig, und
 * das Angebot macht den Beleg zu dem, was er behauptet.
 *
 * Umgekehrt (ausgewiesen bis nach dem Stichtag) gibt es keinen Knopf: den
 * Stichtag nach hinten zu schieben zoege zusaetzliche Leistungen in die
 * Rechnung. Das ist eine Entscheidung ueber Geld und gehoert bewusst getroffen,
 * nicht per Knopfdruck aus einem Hinweis heraus.
 *
 * Massgeblich ist die AUSDRUECKLICHE Angabe (details.servicePeriodTo), nicht
 * printedServicePeriod: ohne eigene Angabe faellt die auf deliveryDateTo
 * zurueck, und es gaebe nichts zu begrenzen.
 */
export const servicePeriodLimitOffer = (
    invoice: PrintedServicePeriodSource | undefined,
): string | undefined => {
    const statedTo = plausibleDay(invoice?.details?.servicePeriodTo);
    const billedTo = asDay(invoice?.deliveryDateTo);
    if (!statedTo || !billedTo) return undefined;
    return statedTo < billedTo ? statedTo : undefined;
};

// ═══════════════════════════════════════════════════════════════════════════
// Kostenstelle
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bezeichnung der Kostenstellenzeile, wenn der Beleg keine eigene traegt.
 *
 * "Kostenstelle" ist der haeufigste Fall und zugleich der Stand jedes Belegs
 * aus der Zeit vor details.costCenterLabel — ein Altbeleg druckt damit
 * unveraendert weiter.
 */
export const DEFAULT_COST_CENTER_LABEL = 'Kostenstelle';

/**
 * Die Kostenstellenzeile des Belegkopfs: "<Bezeichnung>: <Wert>".
 *
 * Leer, wenn kein WERT dasteht. Die Bezeichnung allein ergibt keine Zeile —
 * "PSP-Element:" ohne PSP-Element ist keine Angabe, sondern ein Formularrest;
 * sie entsteht in der Maske schon dann, wenn jemand eine Vorlage antippt und
 * den Wert dann doch nicht fuellt.
 *
 * Hier und nicht im Generator, damit die Regel einmal existiert und ohne PDF
 * geprueft werden kann (kein jsdom im Projekt).
 */
export const costCenterLine = (details?: {
    costCenter?: string;
    costCenterLabel?: string;
}): string => {
    const value = details?.costCenter?.trim();
    if (!value) return '';
    const label = details?.costCenterLabel?.trim() || DEFAULT_COST_CENTER_LABEL;
    return `${label}: ${value}`;
};

/** Zuletzt am Beleg verwendete Kostenstelle — Bezeichnung samt Wert. */
export interface LastUsedCostCenter {
    /** Fehlt, wenn der Beleg nur einen Wert trug (Stand vor costCenterLabel). */
    label?: string;
    value: string;
}

/**
 * Belegarten, die eine Kostenstelle vorbelegt bekommen.
 *
 * Dieselbe Menge wie bei den Kundenkonditionen und aus demselben Grund:
 * originaere Forderungsbelege entstehen aus dem Nichts und duerfen sich am
 * zuletzt Gewohnten orientieren. Storno, Korrektur und Gutschrift beziehen sich
 * dagegen auf einen Ursprungsbeleg — sie uebernehmen DESSEN Kostenstelle
 * (invoiceService), und hatte der keine, ist "keine" die richtige Antwort.
 * Eine zuletzt anderswo verwendete Stelle wuerde den Empfaenger auffordern,
 * die Aufhebung woanders gegenzubuchen als die Rechnung.
 */
export const STICKY_COST_CENTER_TYPES = new Set<DocumentType>([
    'INVOICE',
    'ADVANCE_INVOICE',
    'FINAL_INVOICE',
    'REMAINING_INVOICE',
]);

/**
 * Soll dieser Entwurf die zuletzt verwendete Kostenstelle nachschlagen?
 *
 * Nein, sobald der Aufrufer selbst etwas mitgibt — auch eine blosse
 * Bezeichnung. Die Vorbelegung ist eine Bequemlichkeit und darf eine
 * ausdrueckliche Angabe nie ueberschreiben.
 *
 * Getrennt von applyStickyCostCenter, damit der Aufrufer die Abfrage sparen
 * kann, statt sie zu stellen und das Ergebnis wegzuwerfen.
 */
export const wantsStickyCostCenter = (
    documentType: DocumentType,
    details?: { costCenter?: string; costCenterLabel?: string },
): boolean =>
    STICKY_COST_CENTER_TYPES.has(documentType) &&
    !details?.costCenter?.trim() &&
    !details?.costCenterLabel?.trim();

/**
 * Die Vorbelegung als details-Fragment — leer, wenn nichts vorzubelegen ist.
 *
 * Bewusst ein Fragment zum Dazuspreizen und kein veraendertes details-Objekt:
 * der Entwurf baut seine details aus mehreren Quellen zusammen, und ein
 * weiterer Bearbeitungsschritt darauf verdeckte, was am Ende gilt.
 */
export const applyStickyCostCenter = (
    documentType: DocumentType,
    details: { costCenter?: string; costCenterLabel?: string } | undefined,
    last: LastUsedCostCenter | null | undefined,
): { costCenter?: string; costCenterLabel?: string } => {
    if (!wantsStickyCostCenter(documentType, details)) return {};
    const value = last?.value?.trim();
    if (!value) return {};
    const label = last?.label?.trim();
    return { costCenter: value, ...(label ? { costCenterLabel: label } : {}) };
};

/**
 * Eine Belegzeile, aus der die Vorbelegungen des naechsten Entwurfs gelesen
 * werden — Kostenstelle und abweichender Projektname.
 *
 * Nur die Angaben, die dafuer gebraucht werden. Die Abfrage in
 * invoiceCostCenters holt ohnehin `details` als Ganzes (ein jsonb laesst sich
 * nicht spaltenweise ziehen), aber der Typ nennt ausdruecklich, was gelesen
 * wird — sonst wandert stillschweigend die halbe Rechnung in die Vorbelegung.
 */
export interface CostCenterSourceRow {
    primaryProjectId?: string | null;
    /**
     * Der Debitor des Belegs (invoices.customer_id).
     *
     * Nur die Kundenabweichung liest ihn, und zwar weil sie als einzige der
     * Vorbelegungen den EMPFAENGER bestimmt: ein Projekt kann den Auftraggeber
     * wechseln (Zusammenfuehrung zweier Kunden, Umhaengen ueber
     * crmService.linkProjectsToCustomer), und die Anschrift des vorigen
     * Auftraggebers gehoert dann nicht mehr auf den naechsten Beleg. Kostenstelle
     * und Projektname bleiben bewusst rein projektbezogen — sie sagen etwas ueber
     * die Baustelle und nicht darueber, wer sie bezahlt.
     */
    customerId?: string | null;
    details?: {
        costCenter?: string;
        costCenterLabel?: string;
        projectNameOverride?: string;
        addressExtra1?: string;
        addressExtra2?: string;
        addressExtra3?: string;
        customerOverride?: CustomerOverride;
    } | null;
}

/**
 * Die Kostenstelle EINES Belegs, soweit er eine traegt.
 *
 * Ohne Wert gibt es keine — eine Bezeichnung allein ist ein Formularrest und
 * taugt nicht als Vorbelegung (dieselbe Regel wie in costCenterLine).
 */
export const lastUsedCostCenterOf = (
    details?: { costCenter?: string; costCenterLabel?: string } | null,
): LastUsedCostCenter | null => {
    const value = details?.costCenter?.trim();
    if (!value) return null;
    const label = details?.costCenterLabel?.trim();
    return { value, ...(label ? { label } : {}) };
};

/**
 * Die zuletzt verwendete Kostenstelle JE PROJEKT.
 *
 * Projektbezogen und nicht global: der Betrieb rechnet parallel an mehreren
 * Baustellen ab, und jeder Auftraggeber fuehrt seine eigene Kostenstelle. Eine
 * globale Vorbelegung trug die Stelle des zuletzt bearbeiteten FREMDEN Projekts
 * in den neuen Beleg — der Empfaenger buchte damit auf ein Konto, das ihn nichts
 * angeht. Kennt ein Projekt keine, bleibt das Feld leer; ein Rueckfall auf
 * "irgendeine" waere genau der Fehler.
 *
 * Erwartet die Zeilen NEUESTE ZUERST (die Abfrage sortiert nach updated_at) und
 * nimmt je Projekt die erste, die eine Kostenstelle traegt. "Zuletzt verwendet"
 * heisst zuletzt angefasst und nicht zuletzt angelegt: die Kostenstelle wird
 * oft erst in den bestehenden Entwurf getippt.
 */
export const pickLastUsedCostCenters = (
    rows: CostCenterSourceRow[],
): Map<string, LastUsedCostCenter> => {
    const out = new Map<string, LastUsedCostCenter>();
    for (const row of rows) {
        const projectId = row.primaryProjectId?.trim();
        if (!projectId || out.has(projectId)) continue;
        const found = lastUsedCostCenterOf(row.details);
        if (found) out.set(projectId, found);
    }
    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// Abweichender Projektname auf dem Beleg
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Soll dieser Entwurf den zuletzt verwendeten Projektnamen nachschlagen?
 *
 * Dieselbe Belegartmenge wie bei der Kostenstelle (STICKY_COST_CENTER_TYPES)
 * und aus demselben Grund: originaere Forderungsbelege duerfen sich am zuletzt
 * Gewohnten orientieren, Storno, Korrektur und Gutschrift erben stattdessen
 * DEN Namen des Ursprungsbelegs (invoiceService: copiedProjectName). Ein
 * Storno, das eine Baustelle anders benennt als die Rechnung, die es aufhebt,
 * waere fuer den Empfaenger ein anderer Vorgang.
 *
 * Nein, sobald der Aufrufer selbst einen Namen mitgibt — die Vorbelegung ist
 * ein Vorschlag und darf eine ausdrueckliche Angabe nie ueberschreiben.
 */
export const wantsStickyProjectName = (
    documentType: DocumentType,
    details?: { projectNameOverride?: string },
): boolean =>
    STICKY_COST_CENTER_TYPES.has(documentType) && !details?.projectNameOverride?.trim();

/**
 * Die Vorbelegung als details-Fragment — leer, wenn nichts vorzubelegen ist.
 *
 * Fragment zum Dazuspreizen, genau wie applyStickyCostCenter; Begruendung
 * dort.
 */
export const applyStickyProjectName = (
    documentType: DocumentType,
    details: { projectNameOverride?: string } | undefined,
    last: string | null | undefined,
): { projectNameOverride?: string } => {
    if (!wantsStickyProjectName(documentType, details)) return {};
    const name = last?.trim();
    return name ? { projectNameOverride: name } : {};
};

/**
 * Der abweichende Projektname JE PROJEKT, aus den letzten Belegen.
 *
 * Gleiche Erwartung wie pickLastUsedCostCenters: Zeilen NEUESTE ZUERST, je
 * Projekt die erste, die einen Namen traegt. Bewusst UNABHAENGIG von der
 * Kostenstelle ausgewaehlt — beide Angaben werden getrennt gepflegt, und der
 * Beleg, in dem zuletzt eine Kostenstelle stand, ist nicht zwingend der, in
 * dem zuletzt ein eigener Projektname stand.
 */
export const pickLastUsedProjectNames = (rows: CostCenterSourceRow[]): Map<string, string> => {
    const out = new Map<string, string>();
    for (const row of rows) {
        const projectId = row.primaryProjectId?.trim();
        if (!projectId || out.has(projectId)) continue;
        const name = row.details?.projectNameOverride?.trim();
        if (name) out.set(projectId, name);
    }
    return out;
};

/**
 * Die Adress-Zusatzzeilen (details.addressExtra1..3) als Vorbelegung.
 *
 * Ein gemeinsames Objekt fuer alle drei: die Zeilen gehoeren inhaltlich
 * zusammen (Vergabestelle + Referenz + Abteilung EINES Auftraggebers) und
 * werden deshalb immer aus DEMSELBEN Beleg uebernommen — Zeile 1 aus dem
 * Maerz-Beleg und Zeile 2 aus dem Mai-Beleg ergaebe eine Anschrift, die so
 * nie jemand geschrieben hat.
 */
export interface LastUsedAddressExtras {
    addressExtra1?: string;
    addressExtra2?: string;
    addressExtra3?: string;
}

/**
 * Vorbelegen nur, wenn der Aufrufer KEINE der drei Zeilen selbst mitgibt —
 * dieselbe Regel wie bei der Kostenstelle: eine ausdrueckliche Angabe wird
 * nie ueberschrieben, auch nicht teilweise.
 */
export const wantsStickyAddressExtras = (
    documentType: DocumentType,
    details?: LastUsedAddressExtras,
): boolean =>
    STICKY_COST_CENTER_TYPES.has(documentType) &&
    !details?.addressExtra1?.trim() &&
    !details?.addressExtra2?.trim() &&
    !details?.addressExtra3?.trim();

/**
 * Die Vorbelegung als details-Fragment — leer, wenn nichts vorzubelegen ist.
 * Fragment zum Dazuspreizen, genau wie applyStickyCostCenter.
 */
export const applyStickyAddressExtras = (
    documentType: DocumentType,
    details: LastUsedAddressExtras | undefined,
    last: LastUsedAddressExtras | null | undefined,
): LastUsedAddressExtras => {
    if (!wantsStickyAddressExtras(documentType, details) || !last) return {};
    const out: LastUsedAddressExtras = {};
    if (last.addressExtra1?.trim()) out.addressExtra1 = last.addressExtra1.trim();
    if (last.addressExtra2?.trim()) out.addressExtra2 = last.addressExtra2.trim();
    if (last.addressExtra3?.trim()) out.addressExtra3 = last.addressExtra3.trim();
    return out;
};

/**
 * Die Adress-Zusatzzeilen JE PROJEKT, aus den letzten Belegen.
 *
 * Gleiche Erwartung wie pickLastUsedCostCenters: Zeilen NEUESTE ZUERST, je
 * Projekt der erste Beleg, der IRGENDEINE Zusatzzeile traegt — und von dem
 * dann alle drei zusammen (siehe LastUsedAddressExtras).
 */
export const pickLastUsedAddressExtras = (
    rows: CostCenterSourceRow[],
): Map<string, LastUsedAddressExtras> => {
    const out = new Map<string, LastUsedAddressExtras>();
    for (const row of rows) {
        const projectId = row.primaryProjectId?.trim();
        if (!projectId || out.has(projectId)) continue;
        const extras: LastUsedAddressExtras = {};
        if (row.details?.addressExtra1?.trim()) extras.addressExtra1 = row.details.addressExtra1.trim();
        if (row.details?.addressExtra2?.trim()) extras.addressExtra2 = row.details.addressExtra2.trim();
        if (row.details?.addressExtra3?.trim()) extras.addressExtra3 = row.details.addressExtra3.trim();
        if (Object.keys(extras).length) out.set(projectId, extras);
    }
    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// Abweichende Kundenangaben auf dem Beleg
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Soll dieser Entwurf die belegbezogene Kundenabweichung nachschlagen?
 *
 * Dieselbe Belegartmenge wie Kostenstelle und Projektname und aus demselben
 * Grund: originaere Forderungsbelege duerfen sich am zuletzt Gewohnten
 * orientieren, Storno, Korrektur und Gutschrift erben stattdessen die Angaben
 * DES Ursprungsbelegs. Ein Storno, das den Empfaenger anders anschreibt als
 * die Rechnung, die es aufhebt, waere fuer den Kunden ein anderer Vorgang.
 *
 * Nein, sobald der Aufrufer selbst eine Abweichung mitgibt — die Vorbelegung
 * ist ein Vorschlag und darf eine ausdrueckliche Angabe nie ueberschreiben.
 */
export const wantsStickyCustomerOverride = (
    documentType: DocumentType,
    details?: { customerOverride?: CustomerOverride },
): boolean =>
    STICKY_COST_CENTER_TYPES.has(documentType) && !hasCustomerOverride(details?.customerOverride);

/**
 * Die Vorbelegung als details-Fragment — leer, wenn nichts vorzubelegen ist.
 * Fragment zum Dazuspreizen, genau wie applyStickyCostCenter.
 */
export const applyStickyCustomerOverride = (
    documentType: DocumentType,
    details: { customerOverride?: CustomerOverride } | undefined,
    last: CustomerOverride | null | undefined,
): { customerOverride?: CustomerOverride } => {
    if (!wantsStickyCustomerOverride(documentType, details)) return {};
    return hasCustomerOverride(last) ? { customerOverride: last as CustomerOverride } : {};
};

/**
 * Die abweichenden Kundenangaben JE PROJEKT, aus den letzten Belegen.
 *
 * Zeilen NEUESTE ZUERST — wie bei pickLastUsedCostCenters. Alle Felder kommen
 * aus DEMSELBEN Beleg: eine Abweichung ist ein zusammenhaengendes Anschriftfeld;
 * halb aus dem Maerz- und halb aus dem Mai-Beleg ergaebe einen Empfaenger, den
 * so nie jemand geschrieben hat.
 *
 * Genau hier liegt der praktische Nutzen: eine Baustelle wird ueber Abschlag,
 * Nachtrag und Schlussrechnung hinweg abgerechnet, und die abweichende
 * Rechnungsanschrift des Auftraggebers gilt fuer das ganze Projekt — sie soll
 * nicht an jedem einzelnen Beleg neu eingetragen werden.
 *
 * ZWEI Regeln, die diese Auswahl von Kostenstelle und Projektname trennen, und
 * beide gibt es, weil hier der EMPFAENGER auf dem Spiel steht:
 *
 * 1. Nur Belege DESSELBEN Debitors zaehlen. Ein Projekt kann den Auftraggeber
 *    wechseln (Kundenzusammenfuehrung, crmService.linkProjectsToCustomer). Rein
 *    projektbezogen nachgeschlagen adressierte der naechste Entwurf — im
 *    Abrechnungslauf gleich reihenweise — die Anschrift des VORIGEN
 *    Auftraggebers, waehrend die Forderung auf den neuen gebucht wird. Sichtbar
 *    waere davon nur ein kleines Kennzeichen "Abweichend".
 *
 * 2. Der juengste Beleg des Kunden entscheidet, auch wenn er KEINE Abweichung
 *    traegt. Das Ueberspringen leerer Belege liess eine ausdruecklich entfernte
 *    Abweichung wieder aufleben: Rechnung 2 nahm sie weg, Rechnung 3 holte sie
 *    aus Rechnung 1 zurueck. "Zuletzt entfernt" ist dieselbe Aussage des
 *    Bearbeiters wie "zuletzt gesetzt" und muss genauso zaehlen. Bei
 *    Kostenstelle und Projektname bleibt es beim Ueberspringen — dort ist ein
 *    leeres Feld ein nicht ausgefuelltes Formular, keine Ansage.
 *
 * `expectedCustomerByProject` nennt je Projekt den Debitor, fuer den der neue
 * Beleg entsteht. Fehlt er, wird NICHT vorbelegt: ohne bekannten Empfaenger
 * laesst sich Regel 1 nicht pruefen, und die Richtung, in die dieses Feature
 * scheitern darf, ist das leere Feld.
 */
export const pickLastUsedCustomerOverrides = (
    rows: CostCenterSourceRow[],
    expectedCustomerByProject?: ReadonlyMap<string, string | undefined>,
): Map<string, CustomerOverride> => {
    const out = new Map<string, CustomerOverride>();
    /**
     * Projekte, ueber die der juengste Beleg des richtigen Kunden schon
     * entschieden hat — getrennt von `out`, weil "entschieden" hier auch
     * "entschieden: keine" heissen kann (Regel 2).
     */
    const decided = new Set<string>();
    for (const row of rows) {
        const projectId = row.primaryProjectId?.trim();
        if (!projectId || decided.has(projectId)) continue;
        const expected = expectedCustomerByProject?.get(projectId)?.trim();
        // Belege eines fremden Debitors werden komplett uebergangen: sie duerfen
        // die Abweichung weder liefern noch die des richtigen Kunden verdecken.
        if (!expected || row.customerId?.trim() !== expected) continue;
        decided.add(projectId);
        const found = row.details?.customerOverride;
        if (hasCustomerOverride(found)) out.set(projectId, found as CustomerOverride);
    }
    return out;
};

/**
 * Was ein neuer Entwurf vom zuletzt bearbeiteten Beleg DESSELBEN PROJEKTS
 * uebernimmt.
 *
 * Ein gemeinsames Ergebnis, weil es EINE Abfrage ist: alle Angaben stehen im
 * selben details-jsonb derselben Belege. Eine zweite Abfrage fuer den
 * Projektnamen zoege dieselben Zeilen ein zweites Mal.
 */
export interface LastUsedInvoiceDefaults {
    costCenter?: LastUsedCostCenter;
    projectName?: string;
    addressExtras?: LastUsedAddressExtras;
    customerOverride?: CustomerOverride;
}

/**
 * Beide Vorbelegungen JE PROJEKT aus einem Satz Belegzeilen.
 *
 * Setzt sich aus den beiden Einzelauswahlen zusammen, statt sie in einer
 * Schleife zu vermengen: jede Angabe hat ihre eigene Regel, wann eine Zeile
 * als Fundstelle zaehlt, und die getrennten Funktionen bleiben einzeln
 * pruefbar. Die Zeilenzahl ist gedeckelt (LAST_USED_MAX_ROWS), zwei Durchlaeufe
 * darueber kosten nichts.
 *
 * `expectedCustomerByProject` geht AUSSCHLIESSLICH an die Kundenabweichung
 * weiter (Begruendung dort). Kostenstelle und Projektname bleiben projektbezogen
 * und greifen auch dann, wenn das Projekt den Auftraggeber gewechselt hat — sie
 * beschreiben die Baustelle, nicht den Empfaenger.
 */
export const pickLastUsedInvoiceDefaults = (
    rows: CostCenterSourceRow[],
    expectedCustomerByProject?: ReadonlyMap<string, string | undefined>,
): Map<string, LastUsedInvoiceDefaults> => {
    const costCenters = pickLastUsedCostCenters(rows);
    const projectNames = pickLastUsedProjectNames(rows);
    const addressExtras = pickLastUsedAddressExtras(rows);
    const customerOverrides = pickLastUsedCustomerOverrides(rows, expectedCustomerByProject);
    const out = new Map<string, LastUsedInvoiceDefaults>();
    for (const projectId of new Set([
        ...costCenters.keys(),
        ...projectNames.keys(),
        ...addressExtras.keys(),
        ...customerOverrides.keys(),
    ])) {
        const costCenter = costCenters.get(projectId);
        const projectName = projectNames.get(projectId);
        const extras = addressExtras.get(projectId);
        const customerOverride = customerOverrides.get(projectId);
        out.set(projectId, {
            ...(costCenter ? { costCenter } : {}),
            ...(projectName ? { projectName } : {}),
            ...(extras ? { addressExtras: extras } : {}),
            ...(customerOverride ? { customerOverride } : {}),
        });
    }
    return out;
};

/**
 * Der Betreff eines Angebots als Vorlage fuer die Belegueberschrift.
 *
 * Quelle ist die Angebotsart (details.offerType) — die fette Kopfzeile, die im
 * Angebots-PDF unter der Angebotsnummer steht und sagt, worum es geht.
 * Hilfsweise das Bauvorhaben (quote.title); die Angebotsnummer selbst bleibt
 * aussen vor, sie gehoert nicht auf eine Rechnung.
 *
 * Mehrzeilige Angebotsarten werden zu einer Zeile verbunden: die
 * Belegueberschrift ist einzeilig, ein eingebettetes \n wuerde im PDF als
 * Kaestchen erscheinen.
 */
export const quoteHeadlineText = (
    quote: { title?: string; details?: { offerType?: string } } | null | undefined,
): string =>
    (quote?.details?.offerType?.trim() || quote?.title?.trim() || '')
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .join(' · ');

// ═══════════════════════════════════════════════════════════════════════════
// Validierung
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Belegarten, die eine Ursprungsrechnung mindern oder aufheben. Nur hier sind
 * negative Mengen und ein Zahlbetrag von 0 zulaessig.
 */
export const CORRECTION_TYPES = new Set<DocumentType>([
    'CANCELLATION_INVOICE',
    'CORRECTION_INVOICE',
    'COMMERCIAL_CREDIT_INVOICE',
    'SELF_BILLING_CREDIT_NOTE',
]);

export type ValidationLevel = 'error' | 'warning';

export interface ValidationMessage {
    code: string;
    level: ValidationLevel;
    field?: string;
    message: string;
}

// ─── Vorabrechnung ohne abzugsfaehigen Beleg ────────────────────────────────

/**
 * Mengen unterhalb dieser Schwelle sind keine Menge.
 *
 * Ein Tausendstel ist die Aufloesung, die ServiceItem.quantity ueberhaupt hat
 * (numeric(14,3)); darunter liegt nur noch Gleitkommarauschen — 0.1 + 0.2 - 0.3
 * ergibt 5.55e-17. Derselbe Wert wie BOOKING_EPSILON (invoiceWriteBackLogic)
 * und wie die Filter in computeOpenPositions; bewusst hier wiederholt, weil
 * invoiceWriteBackLogic seinerseits aus diesem Modul liest.
 *
 * Das ist die eigentliche Toleranz dieser Pruefung: sie sitzt dort, wo die
 * Ungenauigkeit entsteht — an der MENGE je Position, nicht an einer Summe.
 */
const UNCOVERED_QTY_EPSILON = 0.0005;

/**
 * Betragssockel, ab dem der Befund gemeldet wird (1,00 EUR).
 *
 * Nach dem Umbau auf den Mengenvergleich ist dies der einzige verbliebene
 * Puffer, und er ist ABSOLUT — er waechst nicht mehr mit dem Volumen. Die
 * frueheren 1 % der erwarteten Summe (bei 6172: 2.102,10 EUR blind) und ihr
 * Deckel von 5.000,00 EUR sind ersatzlos entfallen: sie pufferten STRUKTURELLE
 * Abweichungen zwischen einer Betrags- und einer Mengenaussage — Nachlass auf
 * dem Vorbeleg, spaeter geaenderter LV-Preis, reine Betragskorrektur. Keine
 * davon veraendert eine Menge, keine davon kann hier noch auftreten.
 *
 * Was noch abweichen kann, ist eine Stelle hinter dem Komma: die feinste Menge
 * ist ein Tausendstel, und der hoechste Einzelpreis im Bestand liegt bei rund
 * 1.000,00 EUR (6172: 960,00 EUR je Tagespauschale). Ein Tausendstel davon
 * sind 1,00 EUR — darunter beschreibt ein Befund keinen fehlenden Beleg mehr,
 * sondern die letzte Nachkommastelle einer Menge. Genau da liegt der Sockel.
 */
const UNCOVERED_PRIOR_BILLING_FLOOR_CENTS = 100;

/** Befund von uncoveredPriorBilling. Alle Betraege NETTO in ganzen Cent. */
export interface UncoveredPriorBilling {
    /** Wert der Menge, die die Zeilen als bereits abgerechnet ausweisen. */
    billedValueCents: Cents;
    /** Davon durch Belege gedeckt: Vorbelege des Projekts und Alt-Abschlaege. */
    coveredValueCents: Cents;
    /** billedValue - coveredValue, immer groesser als die Toleranz. */
    gapCents: Cents;
    /** Die angelegte Toleranz — fuer Tests und fuer die Meldung nachvollziehbar. */
    toleranceCents: Cents;
    /** Ungedeckte MENGE je betroffener Position — der eigentliche Befund. */
    positions: { lvPosition?: string; title: string; quantity: number; unit?: string }[];
}

/**
 * Eine Position, wie sie die Deckungsrechnung braucht — die gemeinsame Form
 * von Belegzeile und Projektstand.
 *
 * WARUM diese Zwischenform: dieselbe Frage wird an ZWEI Stellen gestellt, und
 * sie muessen dieselbe Antwort geben. Beim Ausstellen sperrt
 * uncoveredPriorBilling den fertigen Beleg; beim ANLEGEN erzwingt
 * projectUncoveredPriorBilling (billingMode.ts) die fortschreibende Anlage,
 * damit der Beleg gar nicht erst falsch entsteht. Rechneten beide fuer sich,
 * gaebe es die beiden Faelle, die niemand erklaeren kann: der Zwang greift,
 * obwohl die Sperre zufrieden waere — oder der Beleg entsteht kumulativ und
 * laesst sich danach nicht ausstellen.
 */
export interface PriorBillingPosition {
    lvPosition?: string;
    title: string;
    unit?: string;
    /** Menge, die als bereits abgerechnet ausgewiesen ist (LV: quantityBilled). */
    billedQty: number;
    /**
     * Durch Belege gedeckte Menge. `undefined` heisst "nicht ermittelt" und ist
     * ausdruecklich etwas anderes als 0 — siehe priorBillingCoverageUnknown.
     */
    coveredQty: number | undefined;
    unitPriceCents: Cents;
    /**
     * Zeilennachlass in Prozent; am reinen Projektstand gibt es keinen. Ein
     * fester Nachlassbetrag der Zeile kommt hier als sein Prozentsatz an
     * (effectiveLineDiscountPercent) — bewertet wird eine TEILmenge.
     */
    discountPercent?: number;
}

/**
 * Die gemeinsame Rechengroesse: fuer welche bereits abgerechnete MENGE fehlt
 * der Beleg, und was ist sie wert?
 *
 * Der Rechenweg selbst — je Position, ohne Verrechnung untereinander,
 * einseitig, mit derselben Mengen-Toleranz und demselben Betragssockel — ist
 * unter uncoveredPriorBilling ausfuehrlich begruendet; hier steht nur noch die
 * Arithmetik.
 *
 * @param externalCoverCents Netto der externen Alt-Abschlaege
 *                           (public.external_advances). Sie gehen als BETRAG
 *                           ein, weil eine Rechnung von vor der App keine
 *                           Positionen hat. Sie koennen den Befund nur
 *                           MINDERN, nie einen erzeugen — und genau sie sind
 *                           der Ausweg, den die Meldung nennt.
 */
export const uncoveredPriorBillingOf = (
    positions: readonly PriorBillingPosition[],
    externalCoverCents: Cents = 0,
): UncoveredPriorBilling | null => {
    let billedValueCents = 0;
    let rawGapCents = 0;
    const found: UncoveredPriorBilling['positions'] = [];
    for (const pos of positions) {
        const billedQty = pos.billedQty ?? 0;
        if (!(billedQty > UNCOVERED_QTY_EPSILON)) continue;
        const coveredQty = pos.coveredQty;
        // Nicht ermittelt heisst nicht "null gedeckt": siehe
        // priorBillingCoverageUnknown.
        if (coveredQty === undefined) continue;
        // Derselbe Rechenweg wie die Zeilensumme (recalculateLines), damit der
        // Befund zu den Konditionen DIESER Zeile bewertet ist.
        const discount = pos.discountPercent ?? 0;
        const valueOf = (qty: number): Cents =>
            roundCents(qty * (pos.unitPriceCents ?? 0) * (1 - discount / 100));
        billedValueCents += valueOf(billedQty);
        const uncoveredQty = billedQty - coveredQty;
        if (!(uncoveredQty > UNCOVERED_QTY_EPSILON)) continue;
        rawGapCents += valueOf(uncoveredQty);
        found.push({
            lvPosition: pos.lvPosition,
            title: pos.title,
            quantity: uncoveredQty,
            unit: pos.unit,
        });
    }
    if (rawGapCents <= 0) return null;

    const gapCents = rawGapCents - Math.max(0, externalCoverCents);
    const toleranceCents = UNCOVERED_PRIOR_BILLING_FLOOR_CENTS;
    if (gapCents <= toleranceCents) return null;

    return {
        billedValueCents,
        coveredValueCents: billedValueCents - gapCents,
        gapCents,
        toleranceCents,
        positions: found,
    };
};

/**
 * Kumulativ abgerechnete Vorleistung, fuer die es keinen abzugsfaehigen Beleg
 * gibt — der Kern des Vorfalls 6172.
 *
 * WARUM: computeCumulativePositions nimmt bewusst den vollen quantityUsed und
 * sieht quantityBilled nicht an (siehe dort). Der Abzug des bereits
 * Abgerechneten laeuft vollstaendig ueber die Vorbelege im Summenblock. Wurde
 * ein Teil der Leistung im ALTSYSTEM fakturiert, existiert dazu kein Beleg im
 * System: der Beleg fordert dieselbe Leistung ein zweites Mal, und dem
 * Summenblock sieht man das nicht an. Bei 6172 wies das LV 213 Tagespauschalen
 * und 47,75 Stunden als abgerechnet aus, auf einem Beleg standen davon 22 und
 * 9,5 — 187.950,00 EUR netto zu viel gefordert.
 *
 * GEZAEHLT WIRD IN MENGE, nicht in Betraegen. Die Frage lautet: fuer welche
 * bereits abgerechnete MENGE existiert kein Beleg?
 *
 *   ungedeckt je Position = previouslyBilledQty - previouslyInvoicedQty
 *   Befund in Euro        = Summe (ungedeckte Menge x Einzelpreis)
 *
 * Der Einzelpreis drueckt den Befund nur noch in Euro aus — er entscheidet
 * nicht mehr, OB einer vorliegt. Das ist der Unterschied zur ersten Fassung,
 * die eine Betragsaussage (Summe der Abzuege) gegen eine Mengenaussage
 * (previouslyBilledQty x heutiger Preis) hielt. Drei Faelle liessen die beiden
 * Seiten auseinanderlaufen, obwohl der Beleg einwandfrei war, und ALLE drei
 * fallen mit dem Mengenvergleich weg:
 *   - Nachlasszeile auf dem Vorbeleg — mindert seine Forderung, nicht seine
 *     Menge. Frueher: 100.000,00 erwartet gegen 91.800,00 gefordert.
 *   - LV-Preis nach der Abrechnung geaendert — die erwartete Seite rechnete mit
 *     dem HEUTIGEN Preis, der Vorbeleg trug den alten.
 *   - fruehere reine Betragskorrektur (quantityInformational) — mindert den
 *     Abzug, laesst die Menge stehen.
 * Die Naeherung, die dafuer noetig war (der kleinere Wert aus anteiligem und
 * absolutem Nachlass), ist mit ihnen entfallen. Sie hat kleine echte Luecken
 * maskiert; das tut jetzt nichts mehr.
 *
 * previouslyInvoicedQty setzt der Zeilenaufbau (invoiceService,
 * buildProjectServiceLines) aus zwei Mengenquellen: was die abzugsfaehigen
 * Vorbelege des Projekts beansprucht haben (advanceInvoiceLogic,
 * invoicedQuantityByService — dort auch die Vorzeichenbehandlung von Storno und
 * Gutschrift) und was als Alt-Rechnung nachgetragen wurde (externalAdvances,
 * altCoverageByService).
 *
 * JE POSITION geprueft, ohne Verrechnung untereinander: eine Ueberdeckung auf
 * Position A schafft keinen Beleg fuer Position B. Ueberdeckung wird deshalb
 * bei null gekappt, statt gegen andere Positionen aufgerechnet zu werden.
 *
 * EINSEITIG: nur eine zu KLEINE Deckung ist ein Befund. Eine zu grosse ist der
 * Normalfall — ein Vorbeleg kann Leistungen enthalten, die dieser Beleg gar
 * nicht mehr auffuehrt (herausgenommene Position), oder eine spaetere Korrektur
 * hat quantityBilled gesenkt, ohne den Beleg anzufassen.
 *
 * EXTERNE ALT-ABSCHLAEGE gehen als BETRAG ein, und nur sie. Ein Eintrag aus
 * public.external_advances ist eine Rechnung von vor der App: er hat eine
 * Summe, aber keine Positionen — in Menge laesst er sich nicht ausdruecken.
 * Ohne diesen Posten haette die Meldung keinen Ausweg: sie verweist auf genau
 * dieses Erfassen als Abhilfe. Er kann den Befund nur MINDERN, nie einen
 * erzeugen. Der zweite Weg, einen Altbeleg nachzutragen (Alt-Positionen-
 * Uebernahme im Buchhaltungsbereich), schreibt dagegen echte Mengen ins
 * Protokoll und steckt bereits in previouslyInvoicedQty.
 *
 * NICHT geprueft wird bei Storno, Gutschrift und Korrektur: mirrorLines
 * (creditNoteLogic.ts) spiegelt Menge und Betrag, kopiert previouslyBilledQty
 * aber unveraendert POSITIV weiter. Ohne diese Grenze meldete jeder Storno
 * eines kumulativen Belegs die volle Vorabrechnung als Luecke und liesse sich
 * nicht mehr ausstellen — dieselbe Vorzeichenfalle wie bei
 * advance_exceeds_total.
 *
 * Fortschreibende Belege sind strukturell nicht betroffen: dort rechnet
 * computeOpenPositions used - billed, previouslyBilledQty wird gar nicht erst
 * gesetzt, und ein Abzug existiert in dem Modell nicht.
 *
 * Zeilen OHNE previouslyInvoicedQty bleiben hier aussen vor — sie sind nicht
 * ungedeckt, sie sind ungeprueft. Dafuer gibt es priorBillingCoverageUnknown.
 *
 * @returns Der Befund, oder null wenn kein Befund vorliegt.
 */
export const uncoveredPriorBilling = (
    invoice: Pick<Invoice, 'documentType' | 'lines' | 'advanceDeductions'> & {
        details?: Invoice['details'];
    },
): UncoveredPriorBilling | null => {
    if (invoice.details?.billingMode !== 'CUMULATIVE') return null;
    if (CORRECTION_TYPES.has(invoice.documentType)) return null;

    return uncoveredPriorBillingOf(
        (invoice.lines ?? [])
            .filter(
                (line) =>
                    line.kind === 'SERVICE' &&
                    // Menge und Preis benennen nur die Ursprungsposition — sie
                    // stehen fuer keine abgerechnete Menge (siehe
                    // InvoiceLine.quantityInformational).
                    !line.quantityInformational,
            )
            .map((line) => ({
                lvPosition: line.lvPosition,
                title: line.title,
                unit: line.unit,
                billedQty: line.previouslyBilledQty ?? 0,
                coveredQty: line.previouslyInvoicedQty,
                unitPriceCents: line.unitPriceCents ?? 0,
                // Auch ein FESTER Positionsnachlass gehoert in die Bewertung —
                // als sein Prozentsatz, weil hier eine Teilmenge bewertet wird
                // und ein Betrag fuer die ganze Zeile gilt.
                discountPercent: effectiveLineDiscountPercent(line),
            })),
        sumCents(
            (invoice.advanceDeductions ?? [])
                .filter((d) => isExternalAdvanceId(d.advanceInvoiceId))
                .map((d) => d.netCents),
        ),
    );
};

/**
 * Laesst sich an diesem Beleg gar nicht pruefen, ob die Vorabrechnung gedeckt
 * ist?
 *
 * Wahr fuer Entwuerfe aus der Zeit VOR dieser Pruefung: sie tragen
 * previouslyBilledQty, aber kein previouslyInvoicedQty. Ebenso, wenn die
 * Belegabfrage beim Aufbau weggebrochen ist (buildProjectServiceLines laesst
 * das Feld dann bewusst weg, statt 0 vorzutaeuschen).
 *
 * Das fehlende Feld als 0 zu lesen waere der naheliegende, aber falsche Weg:
 * jeder Altentwurf mit Vorrechnung meldete dann die volle Vorabrechnung als
 * Luecke und liesse sich nicht mehr ausstellen. Es stillschweigend zu
 * ueberspringen waere der andere Fehler — der Beleg ginge ungeprueft durch,
 * und genau davor soll die Sperre schuetzen.
 *
 * Deshalb die dritte Antwort: WARNUNG statt Sperre. Sie sagt, dass die Pruefung
 * nicht laufen konnte, und nennt die Abhilfe (Positionen neu aufbauen). Der
 * Entwurf bleibt ausstellbar, aber niemand kann behaupten, es habe niemand
 * hingesehen.
 *
 * Teilweise geprueft ist moeglich und richtig: eine von Hand ergaenzte Zeile
 * traegt das Feld nicht, die aufgebauten schon. Dann greift die Sperre fuer die
 * geprueften Zeilen UND diese Warnung fuer den Rest.
 */
export const priorBillingCoverageUnknown = (
    invoice: Pick<Invoice, 'documentType' | 'lines'> & { details?: Invoice['details'] },
): boolean => {
    if (invoice.details?.billingMode !== 'CUMULATIVE') return false;
    if (CORRECTION_TYPES.has(invoice.documentType)) return false;
    return (invoice.lines ?? []).some(
        (l) =>
            l.kind === 'SERVICE' &&
            !l.quantityInformational &&
            (l.previouslyBilledQty ?? 0) > UNCOVERED_QTY_EPSILON &&
            l.previouslyInvoicedQty === undefined,
    );
};

// ─── Monatspauschale: die Riegel am Beleg ───────────────────────────────────

/** Das Wenige eines Protokolleintrags, das die Pauschalpruefung braucht. */
export interface FlatCheckLogEntry {
    id?: string;
    serviceId?: string;
    bookingReference?: string;
    /** Nur fuer finalInvoiceLeavesOpenIssues: Leistungstag und Menge. */
    date?: string;
    quantity?: number;
}

/**
 * Die Felder einer Position, die die Paketregel (componentLineIssues) liest —
 * optional, damit Aufrufer mit schmalen Positionslisten weiter passen. Beide
 * Aufrufer (InvoiceEditor, planProjectIssue) reichen volle ServiceItem-Listen
 * aus loadProjectById durch; dort stehen `bestandteile`/`bestandteileOffen`
 * (projectLv.bestandteilFelderAusZeile).
 */
export type ComponentCheckServiceLike = FlatCoverageServiceLike &
    Partial<Pick<ServiceItem, 'bestandteile' | 'bestandteileOffen' | 'quantityContracted' | 'quantityWaived' | 'unit'>>;

/** Der Projektstand, gegen den die Pauschalregeln pruefen. */
export interface FlatCheckProject {
    services: ComponentCheckServiceLike[];
    serviceLog?: FlatCheckLogEntry[] | null;
    /** Laufzeit des Projekts (globalData.startDate / endDate). */
    startDate?: string | null;
    endDate?: string | null;
}

/**
 * Projekt-Id -> Stand. Der Kontext kommt vom AUFRUFER (der Editor laedt das
 * Projekt ohnehin, der Ausstellpfad ebenso) — validateInvoice bleibt pur und
 * synchron. Fehlt der Kontext oder ein Projekt darin, laufen die Pauschalregeln
 * fuer dieses Projekt nicht; Projekte ohne Pauschalposition brauchen gar
 * keinen Eintrag.
 */
export type FlatCheckContext = Record<string, FlatCheckProject>;

/**
 * ERBRACHT, ABER NICHT IM LEISTUNGSSTAND — der Riegel gegen die zu kleine
 * Forderung.
 *
 * Aufnahmekriterium der Rechnungsleiste ist eine FALSCHE GELDFORDERUNG. Bisher
 * hiess das in der Praxis "zu viel gefordert"; dieser Befund ist die andere
 * Richtung und genauso falsch: die Leistung ist erbracht, protokolliert,
 * archiviert und versendet — nur `quantityUsed` wurde nie fortgeschrieben, und
 * die Rechnung rechnet aus dem Leistungsstand.
 *
 * Der Vorfall, aus dem die Regel stammt (Projekt 6106, 01.09.2026): Bericht
 * #45 war vollstaendig, die Protokollzeile stand mit Menge 1 im Projekt, der
 * Integritaetswaechter meldete den Fall 41 Minuten nach dem Versand — und die
 * Schlussrechnung bot trotzdem 44 statt 45 Begehungen an. 175 EUR, die auf
 * keinem Beleg gestanden haetten. Der Waechter ist eine MELDUNG in einer
 * Liste; gefehlt hat der Halt an der Stelle, an der das Geld entsteht.
 *
 * Warum Blocker und nicht Warnung: die Gegenrichtung `unlogged_quantity` ist
 * bewusst nur ein Hinweis, weil die Menge dort erbracht IST und nur der
 * Nachweis fehlt — eine Rechnung darf daran nicht scheitern. Hier ist es
 * umgekehrt: forderte der Beleg zu wenig, ist er nach dem Ausstellen
 * eingefroren, und die fehlende Menge braucht einen zweiten Beleg. Die
 * Aufloesung ist billig (nachbuchen oder den Protokolleintrag korrigieren),
 * der Schaden teuer.
 *
 * Geprueft werden ALLE Positionen des Projekts, nicht nur die auf dem Beleg:
 * eine Position, deren offene Menge durch den fehlenden Stand auf null faellt,
 * erscheint auf dem Beleg gar nicht erst — genau der Fall, den niemand sieht.
 *
 * Korrektur-, Storno- und Gutschriftbelege bleiben aussen vor, wie bei den
 * Pauschalriegeln: sie fordern nichts Neues, und ihr Stand ist eingefroren.
 */
export const missingUsedIssues = (
    invoice: Invoice,
    context: FlatCheckContext | undefined,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    if (!context || CORRECTION_TYPES.has(invoice.documentType)) return out;

    const fmt = (q: number) => q.toLocaleString('de-DE', { maximumFractionDigits: 3 });

    for (const project of Object.values(context)) {
        const services = Array.isArray(project?.services) ? project.services : [];
        for (const f of findMissingUsedQuantity(services as any, project?.serviceLog ?? [])) {
            out.push({
                code: 'logged_exceeds_used',
                level: 'error',
                field: 'lines',
                message:
                    `Erbrachte Leistung fehlt im Leistungsstand (${f.lvPosition || f.title || f.serviceId}): ` +
                    `protokolliert ${fmt(f.logged)}, im Leistungsstand ${fmt(f.used)} — es fehlen ${fmt(f.missing)}. ` +
                    'Die Rechnung würde zu wenig fordern. Bitte die Leistung nachbuchen oder den Protokolleintrag korrigieren.',
            });
        }
    }
    return out;
};

/**
 * PAKETPOSITION — der Riegel am Beleg (`component_line_incomplete`).
 *
 * openQuantity und computeCumulativePositions bauen eine gesperrte
 * Paketposition gar nicht erst als Forderung auf. Auf den Beleg kommt sie
 * trotzdem: von Hand hinzugefuegt, aus einem Entwurf, der vor der Ruecknahme
 * eines Bestandteils entstand, oder mit von Hand erhoehter Menge. Diese Regel
 * ist der Halt dort, wo das Geld entsteht — und sie laeuft bindend ein zweites
 * Mal in planProjectIssue gegen den FRISCH geladenen Stand (dieselbe Begruendung
 * wie bei flatRateIssues).
 *
 * Ein Fehler, wenn eine SERVICE-Zeile (nicht nur informativ) eine
 * Paketposition FORDERT und
 *  1. deren Gate nicht frei ist (Bestandteile offen ODER Stand unbekannt —
 *     fail closed), oder
 *  2. mehr fordert als der Deckel erlaubt (Vertragsmenge minus abgerechnet,
 *     Kernregel 3 — eine doppelte Positionsbuchung wird nie doppelt
 *     gefordert).
 *
 * „Fordern" heisst wie bei flatRateIssues: fortschreibend die Zeilenmenge,
 * kumulativ nur der ZUWACHS ueber dem bereits Abgerechneten. Eine gesperrte
 * Position, die kumulativ mit ihrem abgerechneten Stand stillsteht, fordert
 * nichts und ist richtig so. Den Deckel prueft kumulativ der GESAMTSTAND der
 * Zeile gegen die Vertragsmenge: der Zuwachs dort rechnet der Abzug der
 * Vorbelege, und ein paralleler Beleg haette den Zuwachs sonst als
 * Ueberforderung gemeldet, obwohl der Abzug ihn auffaengt.
 *
 * Text ohne Bestandteilnamen — der Stand („4/5") reicht, um zu wissen, was
 * zu tun ist. Korrektur-, Storno- und Gutschriftbelege bleiben aussen vor:
 * ein Storno einer abgerechneten Paketposition muss immer gehen.
 */
export const componentLineIssues = (
    invoice: Invoice,
    context: FlatCheckContext | undefined,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    if (!context || CORRECTION_TYPES.has(invoice.documentType)) return out;

    const cumulative = invoice.details?.billingMode === 'CUMULATIVE';
    const fmt = (q: number) => q.toLocaleString('de-DE', { maximumFractionDigits: 3 });

    for (const [projectId, project] of Object.entries(context)) {
        const services = Array.isArray(project?.services) ? project.services : [];
        const pakete = new Map(
            services.filter((s) => s && hatBestandteile(s)).map((s) => [s.id, s] as const),
        );
        if (pakete.size === 0) continue;
        const lines = (invoice.lines ?? []).filter(
            (l) =>
                l.kind === 'SERVICE' &&
                !l.quantityInformational &&
                !!l.serviceId &&
                (l.projectId ?? invoice.primaryProjectId) === projectId,
        );
        for (const l of lines) {
            const s = pakete.get(l.serviceId as string);
            if (!s) continue;
            const menge = l.quantity ?? 0;
            const billed = s.quantityBilled ?? 0;
            const demanded = cumulative ? menge - (l.previouslyBilledQty ?? billed) : menge;
            if (demanded <= UNCOVERED_QTY_EPSILON) continue;
            const name = [l.lvPosition || s.lvPosition, l.title || s.title].filter(Boolean).join(' ');
            const gate = {
                bestandteile: s.bestandteile,
                bestandteileOffen: s.bestandteileOffen,
            };
            if (istAbrechnungGesperrt(gate)) {
                out.push({
                    code: 'component_line_incomplete',
                    level: 'error',
                    field: 'lines',
                    message:
                        `Position ${name}: ${bestandteilSperrText(gate)} — erst abrechenbar, wenn alle erledigt sind. ` +
                        'Die Position vom Beleg nehmen oder die Positionen neu aufbauen.',
                });
                continue;
            }
            const deckel = paketDeckel({
                bestandteile: s.bestandteile,
                quantityContracted: s.quantityContracted ?? 0,
                quantityBilled: billed,
            });
            const vertrag = s.quantityContracted ?? 0;
            const ueber = cumulative
                ? menge > Math.max(vertrag, billed) + UNCOVERED_QTY_EPSILON
                : demanded > deckel + UNCOVERED_QTY_EPSILON;
            if (ueber) {
                out.push({
                    code: 'component_line_incomplete',
                    level: 'error',
                    field: 'lines',
                    message:
                        `Position ${name}: gefordert ${fmt(cumulative ? menge : demanded)}${l.unit ? ` ${l.unit}` : ''}, ` +
                        `als Paketposition abrechenbar sind höchstens ${fmt(cumulative ? Math.max(vertrag, billed) : deckel)} ` +
                        `(Vertragsmenge ${fmt(vertrag)}) — eine doppelte Buchung darf nicht doppelt gefordert werden. ` +
                        'Die Menge korrigieren oder die Positionen neu aufbauen.',
                });
            }
        }
    }
    return out;
};

/**
 * SCHLUSSRECHNUNG MIT LEISTUNG NACH DEM STICHTAG — der Hinweis, dass der Beleg
 * nicht alles fordert (`final_invoice_leaves_open`).
 *
 * Vorfall 5888 (20.10.2026): Bericht #39 vom 20.10. war um 11:25 versendet und
 * gebucht. Die Schlussrechnung RE-26101108 entstand um 16:54 mit Stichtag
 * 19.10. und rechnete 38 von 39 Begehungen ab. Der Stichtag deckelt die Menge
 * auf das, was bis dahin protokolliert ist (performedUpToCutoff). Die
 * Begehung vom 20.10. fiel deshalb still heraus, und keine Meldung nannte sie.
 * Die Rueckfrage zu offenen Monaten gibt es nur VOR dem Zeitraum
 * (openMonthsBeforePeriod), nicht danach.
 *
 * Bei einer laufenden Rechnung ist Leistung nach dem Stichtag der Normalfall,
 * sie kommt auf die naechste. Eine Schluss- oder Restrechnung ist aber die
 * letzte. Was sie liegen laesst, braucht danach einen eigenen Beleg.
 *
 * Gemeldet wird, was NACH dem Stichtag protokolliert ist (Berliner Tag, wie
 * loggedQuantityInPeriod) UND nach diesem Beleg noch offen bleibt. Die zweite
 * Bedingung haelt den Hinweis still, wenn die Menge von Hand auf den Beleg
 * genommen oder verzichtet wurde. Buchungs- und ALT-Zeilen sind keine
 * Leistung. Mit Vorzeichen summiert: eine zurueckgenommene Begehung nach dem
 * Stichtag hebt sich auf.
 *
 * Eine Warnung und keine Sperre (Betreiberwunsch 21.10.2026: „Hinweis"). Es
 * gibt legitime Faelle, etwa eine Begehung nach Bauende, die nicht mehr
 * berechnet wird. Der saubere Weg dafuer ist der Verzicht, und dann schweigt
 * die Regel. Nur fuer Entwuerfe: nach dem Ausstellen steht die Menge schon im
 * Abrechnungsstand, und der Vergleich wuerde doppelt zaehlen.
 *
 * Geprueft werden die Projekte im Kontext. Der Editor laedt nur das
 * Hauptprojekt (wie fuer alle Regeln an diesem Kontext). Eine
 * Sammel-Schlussrechnung ueber mehrere Projekte ohne Hauptprojekt bekommt den
 * Hinweis deshalb nicht.
 */
export const finalInvoiceLeavesOpenIssues = (
    invoice: Invoice,
    context: FlatCheckContext | undefined,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    if (!context) return out;
    if (invoice.documentType !== 'FINAL_INVOICE' && invoice.documentType !== 'REMAINING_INVOICE') return out;
    if (!isEditable(invoice.status)) return out;
    const stichtag = buchungstag(invoice.deliveryDateTo);
    if (!stichtag) return out;

    const cumulative = invoice.details?.billingMode === 'CUMULATIVE';
    const fmt = (q: number) => q.toLocaleString('de-DE', { maximumFractionDigits: 3 });
    const feld = cumulative ? 'Leistungsstand zum' : 'Leistung bis';

    // Was dieser Beleg je Position fordert: kumulativ der Gesamtstand der
    // Zeile, fortschreibend der Zuwachs. Eine Teilrechnung (mehrere
    // Auftraggeber) traegt nur ihren Anteil in `quantity`, die ungeteilte
    // Menge steht in `splitFullQuantity`. Gegen den Abrechnungsstand des
    // ganzen Projekts zaehlt die ungeteilte, sonst fehlte der Anteil der
    // anderen Teilnehmer.
    const aufDemBeleg = new Map<string, number>();
    for (const l of invoice.lines ?? []) {
        if (l.kind !== 'SERVICE' || l.quantityInformational || !l.serviceId) continue;
        const menge = Number(l.splitFullQuantity ?? l.quantity) || 0;
        aufDemBeleg.set(l.serviceId, (aufDemBeleg.get(l.serviceId) ?? 0) + menge);
    }

    for (const project of Object.values(context)) {
        const services = Array.isArray(project?.services) ? project.services : [];
        // `letzterTag` nur aus Zugaengen: eine Ruecknahme nach der letzten
        // Begehung ist kein Tag, bis zu dem der Stichtag reichen muss.
        const nachStichtag = new Map<string, { menge: number; letzterTag: string | null }>();
        for (const e of project?.serviceLog ?? []) {
            if (!e?.serviceId || isAccountingEntry(e) || isBackfilledAltEntry(e)) continue;
            const tag = buchungstag(e.date);
            if (!tag || tag <= stichtag) continue;
            const menge = Number(e.quantity) || 0;
            const bisher = nachStichtag.get(e.serviceId);
            const letzter = bisher?.letzterTag ?? null;
            nachStichtag.set(e.serviceId, {
                menge: (bisher?.menge ?? 0) + menge,
                letzterTag: menge > 0 && (!letzter || tag > letzter) ? tag : letzter,
            });
        }
        if (nachStichtag.size === 0) continue;

        for (const s of services) {
            const nach = nachStichtag.get(s.id);
            if (!nach || !nach.letzterTag || nach.menge <= UNCOVERED_QTY_EPSILON) continue;
            const used = s.quantityUsed || 0;
            const billed = s.quantityBilled || 0;
            const verzichtet = Math.max(0, Math.min(s.quantityWaived || 0, used));
            const zeile = aufDemBeleg.get(s.id) ?? 0;
            const gedeckt = cumulative ? Math.max(billed, zeile) : billed + zeile;
            const bleibtOffen = Math.min(nach.menge, used - verzichtet - gedeckt);
            if (bleibtOffen <= UNCOVERED_QTY_EPSILON) continue;
            out.push({
                code: 'final_invoice_leaves_open',
                level: 'warning',
                field: 'deliveryDateTo',
                message:
                    `Nicht alles in dieser Schlussrechnung (${s.lvPosition || s.title || s.id}): ` +
                    `nach dem Stichtag ${germanDay(stichtag)} wurden noch ${fmt(bleibtOffen)}` +
                    `${s.unit ? ` ${s.unit}` : ''} erbracht (zuletzt am ${germanDay(nach.letzterTag)}), ` +
                    'die dieser Beleg nicht fordert. ' +
                    `Damit sie mitkommen, „${feld}" auf den ${germanDay(nach.letzterTag)} setzen.`,
            });
        }
    }
    return out;
};

/**
 * MONATSPAUSCHALE — die vier Riegel am Beleg. Aufnahmekriterium der
 * Rechnungsleiste: nur was eine FALSCHE GELDFORDERUNG verhindert, wird
 * Blocker. Alle vier Befunde sind welche.
 *
 *  1. `flat_covered_line_billed` — eine gedeckte Position fordert NEUES Geld.
 *     Bewusst nicht "Menge > 0": im kumulativen Stand bleibt eine gedeckte
 *     Position mit Menge = quantityBilled STEHEN (computeCumulativePositions),
 *     sonst schrumpfte der Gesamtstand unter die Vorbelege. Falsch ist erst
 *     der ZUWACHS ueber den bereits abgerechneten Stand — der ist mit der
 *     Pauschale bezahlt und wuerde doppelt gefordert.
 *  2. `flat_month_duplicate` — derselbe Pauschalmonat zweimal auf dem Beleg.
 *     Der Schluessel FLAT-YYYY-MM ist der Riegel gegen Doppelbuchung, nicht
 *     die Menge; steht er zweimal im Protokoll der Pauschalposition, naehrt
 *     er deren Forderung doppelt. Gezaehlt wird ueber die bookingReference
 *     und NIE ueber das Datumsfeld (ein Folgebericht erbt Datumsfelder).
 *     Traegt die Zeile bereits eine Zuordnung (allocations), zaehlen nur die
 *     zugeordneten Eintraege — dann sagt der Beleg selbst, welche Monate er
 *     abrechnet; vorher fordert die Zeile den ganzen Stand und damit jeden
 *     Eintrag.
 *  3. `flat_month_unbooked` — der Abrechnungszeitraum enthaelt einen Monat
 *     der Laufzeit ohne Monatsbuchung: dem Beleg FEHLT Geld. Der Monat ist
 *     auch ohne eine einzige Begehung faellig (Betreiber-Entscheidung 2), die
 *     Pauschale kauft Bereitschaft, nicht Stueckzahl. Ohne lesbaren
 *     Laufzeitbeginn keine Aussage — dieselbe Vorsicht wie openFlatMonths:
 *     lieber kein Riegel als einer aus geratenen Monaten.
 *  4. `flat_month_unbilled` — ein GEBUCHTER Monat im Abrechnungszeitraum,
 *     die Pauschale hat offenes Geld, aber der Beleg traegt keine
 *     geldwirksame Pauschalzeile mit Zuwachs: dem Beleg FEHLT Geld. Ohne
 *     diese Regel waere Regel 3 asymmetrisch — ein Beleg ohne Pauschalzeile
 *     blockiert bei UNGEBUCHTEM Monat, rutscht bei GEBUCHTEM aber durch
 *     (geloeschte oder informative Pauschalzeile).
 *
 * Korrektur-, Storno- und Gutschriftbelege bleiben ganz aussen vor: sie
 * fordern nichts Neues, und ihre gespiegelten negativen Mengen wuerden jede
 * der drei Regeln sinnlos treffen.
 *
 * Exportiert fuer den Ausstellpfad: planProjectIssue (invoiceService) prueft
 * mit GENAU dem Projektstand, aus dem auch zugeordnet und gebucht wird — ein
 * eigener zweiter Lesevorgang nur fuer diese Pruefung wuerde die
 * "ein Schnappschuss je Projekt"-Invariante des Ausstellens brechen.
 */
export const flatRateIssues = (
    invoice: Invoice,
    context: FlatCheckContext | undefined,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    if (!context || CORRECTION_TYPES.has(invoice.documentType)) return out;

    const cumulative = invoice.details?.billingMode === 'CUMULATIVE';
    const periodTo = (invoice.deliveryDateTo ?? '').trim();
    const periodFrom = (invoice.deliveryDateFrom ?? '').trim() || periodTo;
    const fmtQty = (q: number) =>
        q.toLocaleString('de-DE', { maximumFractionDigits: 3 });

    for (const [projectId, project] of Object.entries(context)) {
        const services = Array.isArray(project?.services) ? project.services : [];
        const flats = services.filter((s) => isFlatRateService(s));
        if (flats.length === 0) continue;
        const nameOf = (id: string | undefined) => {
            const s = services.find((x) => x.id === id);
            return s?.title || s?.lvPosition || id || '?';
        };
        const coverage = resolveFlatCoverage(services);
        // Die Zeilen dieses Projekts. Ohne eigene Projektangabe gehoert eine
        // Zeile zum Hauptprojekt des Belegs — von Hand ergaenzte Zeilen tragen
        // keine. Rechnet der Beleg dieses Projekt gar nicht ab, gibt es hier
        // auch keine falsche Forderung zu verhindern.
        const lines = (invoice.lines ?? []).filter(
            (l) =>
                l.kind === 'SERVICE' &&
                (l.projectId ?? invoice.primaryProjectId) === projectId,
        );
        if (lines.length === 0) continue;
        const log: FlatCheckLogEntry[] = Array.isArray(project.serviceLog)
            ? project.serviceLog
            : [];

        // ── Regel 1: gedeckte Position fordert neues Geld ─────────────────
        for (const l of lines) {
            if (l.quantityInformational || !l.serviceId) continue;
            if (!coverage.has(l.serviceId)) continue;
            const demanded = cumulative
                ? (l.quantity ?? 0) - (l.previouslyBilledQty ?? 0)
                : (l.quantity ?? 0);
            if (demanded <= UNCOVERED_QTY_EPSILON) continue;
            out.push({
                code: 'flat_covered_line_billed',
                level: 'error',
                field: 'lines',
                message:
                    `Die Position "${l.title}" ist durch die Monatspauschale ` +
                    `"${nameOf(coverage.get(l.serviceId))}" abgegolten, der Beleg fordert aber ` +
                    `${fmtQty(demanded)}${l.unit ? ` ${l.unit}` : ''} neu — das wäre doppelt bezahlt. ` +
                    `Die Position vom Beleg nehmen oder die Positionen neu aufbauen ` +
                    `(Leistungszeitraum erneut übernehmen).`,
            });
        }

        for (const flat of flats) {
            // ── Regel 2: derselbe Pauschalmonat zweimal ───────────────────
            const flatLine = lines.find(
                (l) => l.serviceId === flat.id && !l.quantityInformational,
            );
            if (flatLine) {
                const flatEntries = log.filter(
                    (e) =>
                        e?.serviceId === flat.id &&
                        isFlatBookingReference(e.bookingReference),
                );
                // Beide Seiten ueber `kennungFuerVergleich` (dieselbe Regel wie
                // allocatedByEntry): eine uuid, die die Zuordnung gross nennt
                // und das Protokoll klein (Journal-Weg), ist derselbe Monat.
                const allocated = new Set(
                    (flatLine.allocations ?? []).map((a) => kennungFuerVergleich(a.entryId)),
                );
                const relevant =
                    allocated.size > 0
                        ? flatEntries.filter((e) => e.id && allocated.has(kennungFuerVergleich(e.id)))
                        : flatEntries;
                const counts = new Map<string, number>();
                for (const e of relevant) {
                    const ref = String(e.bookingReference).trim();
                    counts.set(ref, (counts.get(ref) ?? 0) + 1);
                }
                for (const [ref, count] of counts) {
                    if (count < 2) continue;
                    out.push({
                        code: 'flat_month_duplicate',
                        level: 'error',
                        field: 'lines',
                        message:
                            `Der Pauschalmonat ${flatKeyLabel(ref)} der Position ` +
                            `"${nameOf(flat.id)}" ist ${count}-mal gebucht — der Beleg würde ihn ` +
                            `mehrfach fordern. Die doppelte Monatsbuchung im Leistungsprotokoll ` +
                            `entfernen, dann die Positionen neu aufbauen.`,
                    });
                }
            }

            // ── Regel 3: Monat der Laufzeit ohne Monatsbuchung ────────────
            // Nicht an der Pauschalzeile aufgehaengt: auch ein Beleg, dem die
            // Pauschalzeile fehlt, fordert fuer den Zeitraum zu wenig.
            if (!periodTo) continue; // sperrt bereits delivery_date_missing
            const startRaw = String(project.startDate ?? '').trim();
            const endRaw = String(project.endDate ?? '').trim();
            if (!startRaw || flatMonthKey(startRaw) === null) continue;
            if (endRaw && flatMonthKey(endRaw) === null) continue;
            const from = periodFrom > startRaw ? periodFrom : startRaw;
            const to = endRaw && endRaw < periodTo ? endRaw : periodTo;
            const booked = bookedFlatMonths(log, flat.id);
            const rangeKeys = flatMonthKeysInRange(from, to);
            const missing = rangeKeys.filter((k) => !booked.has(k));
            if (missing.length > 0) {
                const labels = missing.map((k) => flatKeyLabel(k)).join(', ');
                out.push({
                    code: 'flat_month_unbooked',
                    level: 'error',
                    field: 'deliveryDateTo',
                    message:
                        `Der Abrechnungszeitraum enthält ${missing.length === 1 ? 'den Monat' : 'die Monate'} ` +
                        `${labels} ohne Buchung der Monatspauschale "${nameOf(flat.id)}" — dem Beleg fehlt Geld. ` +
                        `Auch ein Monat ohne Begehung ist fällig: die Monate erst über den Monatsvorschlag ` +
                        `buchen (angebrochene Monate mit frei überschreibbarer Menge) oder den ` +
                        `Abrechnungszeitraum anpassen.`,
                });
            }

            // ── Regel 4: gebuchter Monat, aber der Beleg fordert nichts ───
            // Das Spiegelbild von Regel 3: dort fehlt die BUCHUNG, hier fehlt
            // trotz Buchung das GELD. Ohne diese Regel rutscht ein Beleg
            // durch, dessen Pauschalzeile geloescht oder auf informativ
            // gestellt wurde: Regel 1 prueft nur GEDECKTE Positionen (die
            // Pauschale deckt nie sich selbst), Regel 2 braucht die Zeile,
            // Regel 3 haengt an der Buchungslage — alle drei schweigen, und
            // der Beleg geht um den Monatsbetrag zu leicht raus. Massstab ist
            // der STAND (offen vs. gefordert), NICHT die Menge je Monat: die
            // Monatsmenge ist frei ueberschreibbar (Betreiber-Entscheidung 1),
            // eine Mengen-je-Monat-Pruefung wuerde legitime 0,5-Monate
            // blockieren.
            const openFlatQty =
                (flat.quantityUsed ?? 0) - (flat.quantityBilled ?? 0);
            if (
                rangeKeys.some((k) => booked.has(k)) &&
                openFlatQty > UNCOVERED_QTY_EPSILON
            ) {
                const demanded = flatLine
                    ? cumulative
                        ? (flatLine.quantity ?? 0) - (flatLine.previouslyBilledQty ?? 0)
                        : (flatLine.quantity ?? 0)
                    : 0;
                if (demanded <= UNCOVERED_QTY_EPSILON) {
                    out.push({
                        code: 'flat_month_unbilled',
                        level: 'error',
                        field: 'lines',
                        message:
                            `Die Monatspauschale "${nameOf(flat.id)}" hat gebuchte, noch nicht ` +
                            `abgerechnete Monate im Abrechnungszeitraum, der Beleg fordert dafür aber ` +
                            `nichts — dem Beleg fehlt Geld. Die Pauschalzeile fehlt oder steht auf ` +
                            `"ohne Betrag": die Positionen neu aufbauen (Leistungszeitraum erneut ` +
                            `übernehmen).`,
                    });
                }
            }
        }
    }
    return out;
};

/**
 * Titel der Summenzeile, zu der der Andruck gedeckte Positionen zusammenfasst.
 * Exportiert, damit Tests und Maske denselben Wortlaut nennen.
 */
export const FLAT_SUMMARY_TITLE = 'Leistungen bis zur Umstellung auf die Monatspauschale';
export const FLAT_SUMMARY_DESCRIPTION =
    'bereits mit den Vorrechnungen abgerechnet — im Abzug unten enthalten';

/**
 * Die Zeilen des Belegs, wie der ANDRUCK sie zeigt (PDF und E-Rechnung).
 *
 * Bei details.flatRateDisplay === 'FLAT_ONLY' werden die durch eine
 * Monatspauschale gedeckten Leistungszeilen (InvoiceLine.flatCovered) je
 * Steuersatz zu EINER Zeile zusammengefasst. Alles andere — Pauschalzeile,
 * ungedeckte Leistungen, Text-, Nachlass-, Zuschlags- und Abzugszeilen —
 * bleibt, wie es ist. Ohne den Schalter kommen die Zeilen unveraendert zurueck.
 *
 * Warum zusammenfassen und nicht weglassen: kumulativ nennt der Beleg den
 * Gesamtstand und zieht die Vorrechnungen JE BELEG ab. Der eingefrorene Stand
 * der gedeckten Positionen steckt in diesen Vorrechnungen; fehlte er im
 * Gesamtstand, forderte der Beleg zu wenig. Die Summenzeile traegt deshalb
 * exakt die Summe der ersetzten Zeilen — je Steuersatz, damit die
 * Steuerrechnung des Belegs (und BR-CO-10 der E-Rechnung) unberuehrt bleibt.
 *
 * Reine Darstellung: `invoice.lines` wird nicht angefasst. Summen, Abzug,
 * Rueckschreibung, Zuordnung und Storno lesen weiterhin den Beleg. Ein
 * gespiegeltes Storno kommt hier mit negativen Betraegen an — die Summenzeile
 * traegt dann Menge -1 und den Betrag als Einzelpreis, weil die E-Rechnung
 * keinen negativen Einzelpreis kennt (BR-27), eine negative Menge aber schon.
 *
 * Platz der Summenzeile: hinter der letzten verbleibenden Leistungszeile
 * desselben Abschnitts — die Pauschale steht so vor dem Altbestand. Bleibt in
 * dem Abschnitt keine, tritt sie an die Stelle der ersten ersetzten Zeile.
 * Zeilen, deren Summe 0 ist, fallen ersatzlos weg: da ist nichts zu belegen.
 */
export const presentInvoiceLines = (
    invoice: Pick<Invoice, 'lines'> & { details?: Pick<InvoiceDetails, 'flatRateDisplay'> },
): InvoiceLine[] => {
    const lines = invoice.lines ?? [];
    if (invoice.details?.flatRateDisplay !== 'FLAT_ONLY') return lines;
    const collapsible = (l: InvoiceLine): boolean => l.kind === 'SERVICE' && l.flatCovered === true;
    if (!lines.some(collapsible)) return lines;

    // Gruppe = Abschnitt x Steuersatz x Steuerkategorie. Der Abschnitt gehoert
    // dazu, weil eine Sammelrechnung je Projekt einen Abschnitt fuehrt und die
    // Summe des einen Projekts nicht im anderen stehen darf.
    type Group = { key: string; members: InvoiceLine[]; firstIndex: number };
    const groups = new Map<string, Group>();
    lines.forEach((l, index) => {
        if (!collapsible(l)) return;
        const key = `${l.sectionId ?? ''}|${l.taxRateBp}|${l.taxCategoryCode ?? ''}`;
        const g = groups.get(key);
        if (g) g.members.push(l);
        else groups.set(key, { key, members: [l], firstIndex: index });
    });

    const kept: InvoiceLine[] = lines.filter((l) => !collapsible(l));
    const summaryOf = (g: Group): InvoiceLine | null => {
        const first = g.members[0];
        const netCents = g.members.reduce((sum, l) => sum + (l.netCents || 0), 0);
        if (netCents === 0) return null;
        return {
            id: `flat-summary:${g.key}`,
            kind: 'SERVICE',
            projectId: first.projectId,
            projectNumber: first.projectNumber,
            projectName: first.projectName,
            sectionId: first.sectionId,
            title: FLAT_SUMMARY_TITLE,
            description: FLAT_SUMMARY_DESCRIPTION,
            unit: 'pauschal',
            quantity: netCents < 0 ? -1 : 1,
            unitPriceCents: Math.abs(netCents),
            taxRateBp: first.taxRateBp,
            ...(first.taxCategoryCode ? { taxCategoryCode: first.taxCategoryCode } : {}),
            netCents,
            netCentsExplicit: true,
            quantityInformational: true,
            flatCovered: true,
        };
    };

    const out = [...kept];
    // Feste Reihenfolge der Gruppen: nach erster Fundstelle im Beleg.
    const ordered = [...groups.values()].sort((a, b) => a.firstIndex - b.firstIndex);
    for (const g of ordered) {
        const summary = summaryOf(g);
        if (!summary) continue;
        const sameSection = (l: InvoiceLine) =>
            l.kind === 'SERVICE' && (l.sectionId ?? '') === (g.members[0].sectionId ?? '');
        // Auch hinter einer bereits eingefuegten Summenzeile: so stehen
        // mehrere Steuersaetze in der Reihenfolge ihrer ersten Fundstelle.
        let at = -1;
        for (let i = out.length - 1; i >= 0; i -= 1) {
            if (sameSection(out[i])) {
                at = i + 1;
                break;
            }
        }
        if (at < 0) {
            // Keine Leistungszeile im Abschnitt uebrig: an die Stelle der
            // ersten ersetzten Zeile, gemessen an dem, was VOR ihr im Beleg
            // stand und uebrig geblieben ist.
            const before = lines.slice(0, g.firstIndex).filter((l) => !collapsible(l));
            at = out.findIndex((l) => !before.includes(l));
            if (at < 0) at = out.length;
        }
        out.splice(at, 0, summary);
    }
    return out;
};

/**
 * Prueft eine Rechnung vor der Freigabe.
 *
 * Fehler verhindern das Ausstellen, Warnungen nicht. Die Aufteilung folgt
 * Spezifikation §26: alles, was die Rechnung nach §14 UStG unwirksam machen
 * oder zu einer Doppelabrechnung fuehren wuerde, ist ein Fehler.
 *
 * `flatContext` traegt den Projektstand fuer die Monatspauschal-Riegel
 * (flatRateIssues). Er ist optional, weil nicht jeder Aufrufer ein Projekt
 * laden kann — ohne ihn laufen genau diese vier Regeln nicht, alle anderen
 * unveraendert. Derselbe Stand speist den Fehlbestand (missingUsedIssues) und
 * den Paketriegel (componentLineIssues); bindend laufen alle drei noch einmal
 * beim Ausstellen (invoiceService.planProjectIssue).
 */
export const validateInvoice = (
    invoice: Invoice,
    customer: Customer | undefined,
    cfg: InvoiceConfig,
    flatContext?: FlatCheckContext,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    const err = (code: string, message: string, field?: string) =>
        out.push({ code, level: 'error', message, field });
    const warn = (code: string, message: string, field?: string) =>
        out.push({ code, level: 'warning', message, field });

    // ── Rechnungsaussteller (§14 Abs. 4 Nr. 1 und 2) ──────────────────────
    if (!cfg.vatId?.trim() && !cfg.taxNumber?.trim()) {
        err(
            'seller_tax_id_missing',
            'Es fehlt die Umsatzsteuer-Identifikationsnummer oder die Steuernummer des Rechnungsausstellers.',
        );
    }

    // ── Rechnungsempfaenger ───────────────────────────────────────────────
    if (!customer) {
        err('customer_missing', 'Der Rechnung ist kein Kunde zugeordnet.', 'customerId');
    } else {
        if (!customer.name?.trim()) {
            err('customer_name_missing', 'Der Rechnungsempfänger hat keinen Namen.', 'customerId');
        }
        const hasAddress =
            !!customer.street?.trim() ||
            !!customer.zip?.trim() ||
            !!customer.city?.trim() ||
            !!(customer as { address?: string }).address?.trim();
        if (!hasAddress) {
            err(
                'customer_address_missing',
                'Der Rechnungsempfänger hat keine vollständige Anschrift.',
                'customerId',
            );
        }
    }

    // ── Belegdaten ────────────────────────────────────────────────────────
    if (!invoice.invoiceDate) {
        err('invoice_date_missing', 'Es fehlt das Rechnungsdatum.', 'invoiceDate');
    }
    // Der Leistungszeitpunkt ist Pflichtangabe (§14 Abs. 4 Nr. 6 UStG). Ein
    // angefangener Zeitraum ohne Ende reicht nicht: das PDF haette dann keine
    // ausweisbare Angabe, und nach der Festschreibung waere das nicht mehr zu
    // heilen.
    if (!invoice.deliveryDateTo) {
        err(
            'delivery_date_missing',
            invoice.deliveryDateFrom
                ? 'Der Leistungszeitraum hat kein Ende. Ohne Enddatum lässt sich der Leistungszeitpunkt nicht ausweisen.'
                : 'Es fehlt das Leistungsdatum bzw. der Leistungszeitraum.',
            'deliveryDateTo',
        );
    }
    if (
        invoice.deliveryDateFrom &&
        invoice.deliveryDateTo &&
        invoice.deliveryDateFrom > invoice.deliveryDateTo
    ) {
        err(
            'delivery_period_inverted',
            'Der Leistungszeitraum endet vor seinem Beginn.',
            'deliveryDateTo',
        );
    }
    // Der von Hand ausgewiesene Zeitraum ebenso — geprueft wird der Zeitraum,
    // den der Beleg tatsaechlich ausweist, einschliesslich der Seite, die aus
    // dem Leistungsdatum kommt. Er landet im Andruck UND in BT-73/74 der
    // E-Rechnung; verdreht kaeme er dort durch jede Schemapruefung und fiele
    // erst beim Empfaenger auf.
    if (servicePeriodIssue(invoice)) {
        err('service_period_inverted', SERVICE_PERIOD_INVERTED_MESSAGE, 'servicePeriodTo');
    }

    // ── Positionen ────────────────────────────────────────────────────────
    const serviceLines = invoice.lines.filter((l) => l.kind === 'SERVICE');
    if (serviceLines.length === 0) {
        err('lines_missing', 'Die Rechnung enthält keine Positionen.', 'lines');
    }
    for (const line of serviceLines) {
        if (!line.title?.trim()) {
            err('line_title_missing', 'Eine Position hat keine Leistungsbeschreibung.', 'lines');
        } else if (line.title.trim().length < 20 && !line.description?.trim()) {
            warn(
                'line_description_vague',
                `Die Leistungsbeschreibung "${line.title.trim()}" könnte zu ungenau sein.`,
                'lines',
            );
        }
        // Negative Mengen gehoeren ausschliesslich in Korrektur- und
        // Gutschriftbelege. Auf einer Abschlags- oder Schlussrechnung wuerde die
        // Rueckschreibung sie als Storno deuten und abgerechnete Mengen wieder
        // freigeben, obwohl es dazu keinen Beleg gibt.
        if ((line.quantity ?? 0) < 0 && !CORRECTION_TYPES.has(invoice.documentType)) {
            err(
                'line_negative_quantity',
                'Eine Position hat eine negative Menge ohne Korrekturbezug.',
                'lines',
            );
        }
        if ((line.unitPriceCents ?? 0) === 0) {
            warn('line_zero_price', `Die Position "${line.title}" hat den Einzelpreis 0,00 €.`, 'lines');
        }
        // ── Positionsnachlass ─────────────────────────────────────────────
        // Ueber 100 % ist keine Minderung mehr, sondern eine Umkehrung des
        // Vorzeichens: die Position forderte Geld zurueck, ohne dass der Beleg
        // es sagt. Negativ waere ein verkappter Zuschlag.
        const linePercent = line.discountPercent ?? 0;
        if (linePercent < 0 || linePercent > 100) {
            err(
                'line_discount_out_of_range',
                `Der Nachlass der Position "${line.title}" liegt mit ${formatPercent(linePercent)} % ausserhalb von 0 bis 100 %.`,
                'lines',
            );
        }
        // Ein negativer Nachlass ist ein verkappter Zuschlag: er erhoeht die
        // Forderung, waehrend der Beleg "Nachlass" sagt. Zuschlaege haben ihre
        // eigene Zeilenart.
        if ((line.discountAmountCents ?? 0) < 0) {
            err(
                'line_discount_negative',
                `Der Nachlass der Position "${line.title}" ist negativ und würde die Forderung erhöhen.`,
                'lines',
            );
        }
        // Ein fester Nachlass groesser als die Position dreht ihr Vorzeichen —
        // dasselbe Ergebnis wie ueber 100 %, nur in Euro erfasst. Geprueft wird
        // ueber das Vorzeichen und nicht ueber "kleiner als", damit Storno und
        // Gutschrift (negative Grundlage) nicht jedes Mal anschlagen; genau 0
        // ist erlaubt (voller Nachlass).
        //
        // Zeilen mit eigenem Betrag (Korrektur, Gutschrift) bleiben aussen vor:
        // dort ist netCents eine DIFFERENZ und darf dem Vorzeichen von Menge mal
        // Preis widersprechen — genau das ist eine mindernde Korrektur.
        const lineBase = lineGrossBeforeDiscount(line);
        if (
            !line.netCentsExplicit &&
            lineBase !== 0 &&
            Math.sign(line.netCents) === -Math.sign(lineBase)
        ) {
            err(
                'line_discount_exceeds_base',
                `Der Nachlass der Position "${line.title}" übersteigt ihren Betrag von ${formatCents(lineBase)}.`,
                'lines',
            );
        }
    }

    // ── Nachlass auf den ganzen Beleg ─────────────────────────────────────
    const documentPercent = invoice.details?.discountPercent ?? 0;
    if (documentPercent < 0 || documentPercent > 100) {
        err(
            'document_discount_out_of_range',
            `Der Nachlass auf den Beleg liegt mit ${formatPercent(documentPercent)} % ausserhalb von 0 bis 100 %.`,
            'discountPercent',
        );
    }
    if ((invoice.details?.discountAmountCents ?? 0) < 0) {
        err(
            'document_discount_negative',
            'Der Nachlass auf den Beleg ist negativ und würde die Forderung erhöhen.',
            'discountAmountCents',
        );
    }
    const documentDiscountRequested =
        documentPercent > 0 || !!invoice.details?.discountAmountCents;
    const serviceNetCents = sumCents(serviceLines.map((l) => l.netCents));
    const discountNetCents = sumCents(
        invoice.lines.filter((l) => l.generated === 'DISCOUNT').map((l) => l.netCents),
    );
    if (documentDiscountRequested && discountNetCents === 0) {
        // Der Nachlass steht am Beleg, hat aber keine Grundlage gefunden — auf
        // dem Papier fehlt er dann ersatzlos. Warnung und nicht Fehler: ohne
        // Positionen sperrt bereits lines_missing, und der Beleg ist rechnerisch
        // in sich richtig.
        warn(
            'document_discount_without_base',
            'Der Nachlass auf den Beleg wirkt nicht: es gibt keine Position, auf die er sich beziehen könnte.',
            'discountPercent',
        );
    }
    // Wie auf der Position: der Nachlass darf die Leistung nicht ins Gegenteil
    // drehen. Sonst forderte der Beleg eine Erstattung, ohne eine zu sein.
    if (
        serviceNetCents !== 0 &&
        Math.sign(serviceNetCents + discountNetCents) === -Math.sign(serviceNetCents)
    ) {
        err(
            'document_discount_exceeds_total',
            `Der Nachlass auf den Beleg übersteigt die Honorarsumme von ${formatCents(serviceNetCents)}.`,
            'discountPercent',
        );
    }

    // ── Rechnerische Konsistenz ───────────────────────────────────────────
    const recomputed = calculateInvoiceTotals(invoice.lines, invoice.advanceDeductions);
    if (
        recomputed.grossTotalCents !== invoice.totals.grossTotalCents ||
        recomputed.netTotalCents !== invoice.totals.netTotalCents ||
        recomputed.taxTotalCents !== invoice.totals.taxTotalCents
    ) {
        err(
            'totals_inconsistent',
            'Die gespeicherten Summen stimmen nicht mit den Positionen überein.',
        );
    }
    if (
        invoice.totals.netTotalCents + invoice.totals.taxTotalCents !==
        invoice.totals.grossTotalCents
    ) {
        err('gross_mismatch', 'Brutto entspricht nicht Netto zuzüglich Umsatzsteuer.');
    }

    // ── Abschlaege ────────────────────────────────────────────────────────
    // Der Vergleich muss das Vorzeichen des Belegs beruecksichtigen. Ein Storno
    // hat einen NEGATIVEN Bruttobetrag und ohne Abschlaege prepaidCents = 0 —
    // die naive Bedingung `prepaid > gross` war dort immer wahr (0 > -11900) und
    // hat JEDEN Storno, jede Gutschrift und jede mindernde Korrektur beim
    // Ausstellen blockiert. Gemeint ist: der Abzug darf die Leistung nicht
    // uebersteigen, und "uebersteigen" heisst bei einem negativen Beleg
    // betragsmaessig groesser.
    const { prepaidCents, grossTotalCents } = invoice.totals;
    const advanceExceeds =
        grossTotalCents >= 0 ? prepaidCents > grossTotalCents : prepaidCents < grossTotalCents;
    if (advanceExceeds) {
        err(
            'advance_exceeds_total',
            'Die abgezogenen Abschlagszahlungen übersteigen die Gesamtleistung. Dieser Fall muss als Guthaben behandelt werden.',
        );
    }
    // Der Gegenpol zu advance_exceeds_total: dort ist der Abzug zu GROSS, hier
    // fehlt zu bereits abgerechneter MENGE der Beleg. Das faellt im Summenblock
    // niemandem auf — der Beleg rechnet in sich auf und fordert trotzdem bereits
    // abgerechnete Leistung ein zweites Mal (Vorfall 6172, 187.950,00 EUR netto
    // zu viel).
    //
    // FEHLER und nicht Warnung: bei 6172 stand eine Warnung da und hat nichts
    // verhindert. Der Befund bedeutet immer eine Doppelabrechnung, und die ist
    // nach Freigabe nicht mehr zu heilen — sie faellt nach §14c UStG an. Der
    // legitime Fall (nachgetragener Altbestand) loest den Befund nicht aus,
    // solange der Alt-Abschlag erfasst ist; genau dieses Erfassen ist die
    // Abhilfe, die die Meldung nennt.
    const uncovered = uncoveredPriorBilling(invoice);
    if (uncovered) {
        const worst = [...uncovered.positions]
            .sort((a, b) => b.quantity - a.quantity)
            .slice(0, 3)
            .map(
                (p) =>
                    `${p.lvPosition ? `${p.lvPosition} ` : ''}${p.title}: ${p.quantity.toLocaleString('de-DE', { maximumFractionDigits: 3 })}${p.unit ? ` ${p.unit}` : ''}`,
            )
            .join('; ');
        err(
            'prior_billing_not_deducted',
            `Die Positionen weisen ${formatCents(uncovered.billedValueCents)} netto als bereits ` +
                `abgerechnet aus, durch Belege gedeckt sind davon nur ${formatCents(uncovered.coveredValueCents)} netto. ` +
                `Es fehlen ${formatCents(uncovered.gapCents)} (${worst}): dieser kumulative Beleg fordert bereits ` +
                `abgerechnete Leistung ein zweites Mal. Die fehlenden Vorrechnungen als Alt-Abschlag erfassen ` +
                `oder den Beleg fortschreibend anlegen.`,
        );
    }
    // Der Entwurf stammt von vor dieser Pruefung (oder die Belegabfrage ist beim
    // Aufbau weggebrochen): die Zeilen sagen, wieviel bereits abgerechnet ist,
    // aber nicht, wieviel davon auf einem Beleg steht. WARNUNG, nicht Sperre —
    // das fehlende Feld als "null gedeckt" zu lesen sperrte jeden Altentwurf mit
    // Vorrechnung. Stillschweigend durchlassen darf man ihn trotzdem nicht.
    if (priorBillingCoverageUnknown(invoice)) {
        warn(
            'prior_billing_coverage_unknown',
            'An diesem Entwurf lässt sich nicht prüfen, ob die bereits abgerechnete Menge durch Vorrechnungen gedeckt ist — er stammt aus der Zeit vor dieser Prüfung. Die Positionen neu aufbauen (Leistungszeitraum erneut übernehmen), dann wird geprüft.',
        );
    }
    const seen = new Set<string>();
    for (const d of invoice.advanceDeductions) {
        if (seen.has(d.advanceInvoiceId)) {
            err(
                'advance_used_twice',
                `Die Abschlagsrechnung ${d.invoiceNumber} wird mehrfach abgezogen.`,
            );
        }
        seen.add(d.advanceInvoiceId);
        if (d.netCents + d.taxCents !== d.grossCents) {
            err(
                'advance_split_mismatch',
                `Beim Abzug der Abschlagsrechnung ${d.invoiceNumber} ergibt Netto zuzüglich Steuer nicht den Bruttobetrag.`,
            );
        }
    }

    // ── Monatspauschale ───────────────────────────────────────────────────
    // Drei Riegel gegen doppelt gefordertes bzw. fehlendes Pauschalgeld —
    // Regeln und Begruendung an flatRateIssues.
    out.push(...flatRateIssues(invoice, flatContext));

    // ── Erbracht, aber nicht im Leistungsstand ────────────────────────────
    // Der Riegel gegen die zu KLEINE Forderung — Begruendung an
    // missingUsedIssues.
    out.push(...missingUsedIssues(invoice, flatContext));

    // ── Paketposition: Bestandteile offen oder ueber dem Deckel ───────────
    // Derselbe Projektstand wie oben (der Editor laedt die Positionen samt
    // Bestandteilen ohnehin). Begruendung an componentLineIssues.
    out.push(...componentLineIssues(invoice, flatContext));

    // ── Schlussrechnung laesst Leistung nach dem Stichtag liegen ──────────
    // Hinweis, keine Sperre — Begruendung an finalInvoiceLeavesOpenIssues.
    out.push(...finalInvoiceLeavesOpenIssues(invoice, flatContext));

    // ── Steuer ────────────────────────────────────────────────────────────
    for (const g of invoice.totals.taxGroups) {
        if (g.taxRateBp === 0 && !g.taxExemptionReason?.trim() && !cfg.smallBusiness) {
            err(
                'tax_exemption_reason_missing',
                'Bei einem Steuersatz von 0 % muss der Grund der Steuerbefreiung angegeben werden.',
            );
        }
        if (g.taxCategoryCode === 'AE') {
            warn(
                'reverse_charge',
                'Reverse Charge: Der Hinweis "Steuerschuldnerschaft des Leistungsempfängers" muss auf dem Beleg stehen und der Steuerschlüssel freigegeben sein.',
            );
        }
    }
    if (invoice.totals.taxGroups.length > 1) {
        warn(
            'multiple_tax_rates',
            'Die Rechnung enthält mehrere Steuersätze. Zahlungen müssen den Steuergruppen ausdrücklich zugeordnet werden.',
        );
    }

    // ── Zahlung und Bank ──────────────────────────────────────────────────
    if (!invoice.dueDate) {
        warn('due_date_missing', 'Es ist kein Fälligkeitsdatum gesetzt.', 'dueDate');
    }
    const bank = invoice.details?.bankAccount;
    if (!bank?.iban?.trim() && !cfg.iban?.trim()) {
        warn('iban_missing', 'Es ist keine Bankverbindung hinterlegt.');
    }

    // ── E-Rechnung ────────────────────────────────────────────────────────
    if (!invoice.details?.buyerReference?.trim() && !customer?.customerNumber?.trim()) {
        warn(
            'buyer_reference_missing',
            'Es fehlt eine Käuferreferenz (Leitweg-ID oder Kundennummer). Für eine XRechnung ist sie Pflicht.',
        );
    }

    // ── Nullbetrag ────────────────────────────────────────────────────────
    if (invoice.totals.payableCents === 0 && !CORRECTION_TYPES.has(invoice.documentType)) {
        err('nothing_to_invoice', 'Der Zahlbetrag ist 0,00 €. Es gibt nichts abzurechnen.');
    }

    return out;
};

export const hasBlockingErrors = (messages: ValidationMessage[]): boolean =>
    messages.some((m) => m.level === 'error');

// ═══════════════════════════════════════════════════════════════════════════
// Zahlungserfassung
// ═══════════════════════════════════════════════════════════════════════════

export interface PaymentDraft {
    direction: PaymentDirection;
    valueDate: string;
    amountCents: Cents;
    method: PaymentMethod;
    reference?: string;
    note?: string;
}

/**
 * Prueft eine Zahlungsbuchung.
 *
 * Ueberzahlung ist bewusst nur eine Warnung: sie kommt real vor (Rundung des
 * Kunden, Skontoverzicht, doppelte Ueberweisung) und wird nicht verhindert,
 * sondern zum Kundenguthaben. Ein Fehler waere nur, was fachlich unmoeglich
 * ist — eine Zahlung auf einen Beleg, der noch gar nicht ausgestellt wurde.
 */
export const validatePayment = (
    draft: PaymentDraft,
    invoice: Pick<
        Invoice,
        'status' | 'documentType' | 'invoiceDate' | 'totals' | 'paidCents' | 'creditedCents'
    >,
    todayIso: string,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    const err = (code: string, message: string, field?: string) =>
        out.push({ code, level: 'error', message, field });
    const warn = (code: string, message: string, field?: string) =>
        out.push({ code, level: 'warning', message, field });

    if (!Number.isSafeInteger(draft.amountCents) || draft.amountCents === 0) {
        err('amount_invalid', 'Der Zahlbetrag muss ungleich 0,00 € sein.', 'amountCents');
    } else if (draft.amountCents < 0) {
        err(
            'amount_negative',
            'Negative Beträge werden nicht als Zahlung erfasst. Für eine Erstattung die Richtung "Ausgehend" wählen.',
            'amountCents',
        );
    }

    if (!draft.valueDate) {
        err('value_date_missing', 'Das Wertstellungsdatum fehlt.', 'valueDate');
    } else {
        if (draft.valueDate > todayIso) {
            warn(
                'value_date_future',
                'Das Wertstellungsdatum liegt in der Zukunft.',
                'valueDate',
            );
        }
        if (invoice.invoiceDate && draft.valueDate < invoice.invoiceDate) {
            warn(
                'value_date_before_invoice',
                'Die Zahlung liegt vor dem Rechnungsdatum. Bitte prüfen, ob sie zu diesem Beleg gehört.',
                'valueDate',
            );
        }
    }

    // PARTIALLY_CREDITED gehoert dazu: eine teilweise gutgeschriebene Rechnung
    // ist weiterhin offen, und der Kunde zahlt den Rest. Ohne diesen Status
    // liesse sich seine Zahlung nie erfassen — der Datenbank-Trigger, der sie
    // auf PAID fortschreibt, wuerde nie erreicht.
    const bookable: InvoiceStatus[] = [
        'ISSUED',
        'SENT',
        'PARTIALLY_PAID',
        'PARTIALLY_CREDITED',
        'PAID',
        'OVERDUE',
        'DISPUTED',
        // Der Zustandsautomat sieht PAID -> REFUND_PENDING -> REFUNDED vor.
        // Ohne diesen Eintrag liesse sich auf einen Beleg in REFUND_PENDING die
        // Erstattung selbst nicht buchen — der Vorgang waere eine Sackgasse.
        'REFUND_PENDING',
    ];
    if (!bookable.includes(invoice.status)) {
        err(
            'not_bookable',
            `Auf einen Beleg im Status "${invoice.status}" kann keine Zahlung gebucht werden.`,
        );
    }

    const open = openAmountCents(invoice);
    if (draft.direction === 'INCOMING' && draft.amountCents > open) {
        warn(
            'overpayment',
            'Der Betrag übersteigt den offenen Posten. Der Überhang wird als Kundenguthaben geführt.',
            'amountCents',
        );
    }
    // Anders als die Ueberzahlung ist das kein legitimer Fall: waere mehr
    // erstattet als je eingegangen ist, wuerde der Zahlungseingang negativ —
    // und der offene Posten (Zahlbetrag minus Eingang) damit GROESSER als die
    // Rechnung selbst. Das System wuerde eine Forderung ausweisen, die es nicht
    // gibt. Die Datenbank lehnt es zusaetzlich per CHECK ab.
    if (draft.direction === 'OUTGOING' && draft.amountCents > invoice.paidCents) {
        err(
            'refund_exceeds_paid',
            'Es soll mehr erstattet werden, als auf diesem Beleg eingegangen ist.',
            'amountCents',
        );
    }

    return out;
};
